import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { WeatherStripPanel, CountryCardsPanel, NotificationsCard, BackupCard, SegmentAttachments, DayPhotos, AllAttachmentsPanel, PinnedPlacesPanel, WalkingToursPanel, ProfileTagsCard, NearMyHotelPanel } from "./panels";
import { nearestDayFallbackCandidates } from "./scroll-utils.js";

// ── Client error reporting ────────────────────────────────────────────────
// Posts errors to /api/client-errors so the ErrorBoundary, global window
// handlers, and silent catch blocks all surface in one place. Throttled
// per-message so a render-loop crash doesn't DoS the sink.
const _recentErrors = new Map(); // message → timestamp
export function reportError(source, error, extra) {
  try {
    const message = String(error?.message || error || "Unknown");
    const now = Date.now();
    const last = _recentErrors.get(message);
    if (last && now - last < 30_000) return;
    _recentErrors.set(message, now);
    if (_recentErrors.size > 100) {
      for (const [k, t] of _recentErrors) if (now - t > 60_000) _recentErrors.delete(k);
    }
    const tokens = (() => { try { return JSON.parse(localStorage.getItem("tripiq_tokens") || "null"); } catch { return null; } })();
    fetch("/api/client-errors", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
      },
      body: JSON.stringify({
        source,
        message,
        stack: error?.stack || null,
        componentStack: extra?.componentStack || null,
        url: window.location.href,
        extra: extra && !extra.componentStack ? extra : null,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

// Without this, any descendant render error unmounts the whole tree and the
// user sees a blank screen (only window.location.reload() recovers). The
// boundary converts that into a "Something went wrong" panel with a recover
// button. Errors thrown inside event handlers or async callbacks are NOT
// caught by React boundaries — those still need their own try/catch.
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
    reportError("ErrorBoundary", error, { componentStack: info?.componentStack });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#120f1e", color: "#e8e6f0", padding: 24, fontFamily: "Outfit, system-ui, sans-serif" }}>
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ margin: "0 0 8px", fontWeight: 600 }}>Something went wrong</h2>
          <p style={{ color: "#9992b8", fontSize: 14, marginBottom: 20 }}>
            {String(this.state.error?.message || this.state.error || "Unknown error")}
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button onClick={() => this.setState({ error: null })}
              style={{ padding: "8px 16px", background: "#252040", color: "#e8e6f0", border: "1px solid #352f58", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              Try to recover
            </button>
            <button onClick={() => window.location.reload()}
              style={{ padding: "8px 16px", background: "#448aff", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              Reload app
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// ══════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500;700&family=DM+Mono:wght@400;500&display=swap');
:root {
  --bg: #120f1e; --sidebar: #1a1630; --card: #252040; --card-hover: #2d2850;
  --border: #352f58; --text: #e8e6f0; --text-dim: #9992b8; --text-muted: #6b6490;
  --green: #00e676; --red: #ff5252; --amber: #ffab40; --blue: #448aff; --purple: #b388ff;
  --flight-color: #448aff; --hotel-color: #ffab40; --excursion-color: #00e676;
  --transport-color: #b388ff; --other-color: #9992b8;
  /* Typography scale */
  --text-xs: 11px; --text-sm: 12px; --text-base: 13px; --text-md: 14px; --text-lg: 16px; --text-xl: 18px; --text-2xl: 20px;
}
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root { height: 100%; overflow: hidden; overscroll-behavior: none; }
body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--text); }
h1,h2,h3,h4,h5 { font-family: 'Outfit', sans-serif; }
code, .mono { font-family: 'DM Mono', monospace; }

.app { display: flex; height: 100vh; height: 100dvh; overflow: hidden; }
.sidebar { width: 220px; background: var(--sidebar); border-right: 1px solid var(--border); padding: 20px 0; display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; }
.sidebar-logo { padding: 0 20px 24px; font-family: 'Outfit'; font-weight: 700; font-size: 22px; color: var(--green); letter-spacing: -0.5px; }
.sidebar-logo span { color: var(--text); }
.nav-item { padding: 10px 20px; cursor: pointer; display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--text-dim); transition: all 0.15s; border-left: 3px solid transparent; }
.nav-item:hover { color: var(--text); background: rgba(255,255,255,0.03); }
.nav-item.active { color: var(--green); border-left-color: var(--green); background: rgba(0,230,118,0.05); }
.nav-icon { font-size: 18px; width: 24px; text-align: center; }
/* No min-height here: flex:1 already fills .app's height. A stray
   min-height: 100vh forced .main taller than the visible viewport on
   iPad Safari (100vh > 100dvh when the URL bar is showing), so the
   bottom of Past Trips ended up cropped by .app's overflow:hidden. */
.main { flex: 1; margin-left: 220px; padding: 24px 32px; min-height: 0; overflow-y: auto; }

.card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
.card:hover { border-color: rgba(255,255,255,0.1); }
.btn { padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; font-family: 'DM Sans'; font-size: 13px; font-weight: 500; transition: all 0.15s; }
.btn-primary { background: var(--green); color: #111; }
.btn-primary:hover { filter: brightness(1.1); }
.btn-secondary { background: var(--card); color: var(--text); border: 1px solid var(--border); }
.btn-secondary:hover { background: var(--card-hover); }
.btn-xs { padding: 3px 8px; font-size: var(--text-xs); border-radius: 6px; }
.btn-sm { padding: 5px 10px; font-size: var(--text-sm); }
.btn-md { padding: 8px 14px; font-size: var(--text-base); border-radius: 8px; }
.btn-lg { padding: 12px 20px; font-size: var(--text-md); border-radius: 10px; }
.btn-danger { background: var(--red); color: #fff; }

.input { background: var(--sidebar); border: 1px solid var(--border); border-radius: 8px; padding: 10px 14px; color: var(--text); font-family: 'DM Sans'; font-size: 14px; outline: none; width: 100%; min-width: 0; box-sizing: border-box; }
.input:focus { border-color: var(--green); }
.input::placeholder { color: var(--text-muted); }
textarea.input { min-height: 120px; resize: vertical; }

.badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.badge-flight { background: rgba(68,138,255,0.15); color: var(--flight-color); }
.badge-hotel { background: rgba(255,171,64,0.15); color: var(--hotel-color); }
.badge-excursion { background: rgba(0,230,118,0.15); color: var(--excursion-color); }
.badge-transport { background: rgba(179,136,255,0.15); color: var(--transport-color); }
.badge-other { background: rgba(153,146,184,0.15); color: var(--other-color); }
.badge-event { background: rgba(255,171,64,0.15); color: var(--amber); }
.badge-confirmed { background: rgba(0,230,118,0.15); color: var(--green); }
.badge-tentative { background: rgba(255,171,64,0.15); color: var(--amber); }
.badge-unconfirmed { background: rgba(255,82,82,0.15); color: var(--red); }
.badge-suggested { background: rgba(179,136,255,0.15); color: var(--purple); }

/* Trip cards on My Trips. Hover lifts; :active gives a clear press cue
   so taps still register visually on slow networks before the timeline
   loads. .pressing is the same dim applied programmatically while a
   navigation is queueing, so the feedback persists past finger-release. */
.trip-card { cursor: pointer; transition: transform 0.12s, border-color 0.12s, opacity 0.12s, filter 0.12s; }
.trip-card:hover { transform: translateY(-2px); border-color: var(--green); }
.trip-card:active,
.trip-card.pressing { transform: scale(0.985); opacity: 0.7; filter: brightness(0.9); }

/* Route-city pills inside trip cards. The :active rule covers the finger-
   down moment; the .pressing class is applied via JS for ~600ms after
   tap, so the user keeps seeing confirmation even while the timeline is
   loading and the page scrolls to land on the matched day. Without that
   sustained cue, the screen "jumps" to the destination day before the
   user has any visual confirmation their tap registered. */
.route-leg { transition: transform 0.12s, background-color 0.12s, box-shadow 0.12s, filter 0.12s; }
.route-leg:active { transform: scale(0.94); filter: brightness(1.15); }
.route-leg.pressing {
  transform: scale(0.96);
  background: rgba(0, 230, 118, 0.28) !important;
  color: var(--green) !important;
  box-shadow: 0 0 0 2px rgba(0, 230, 118, 0.55), 0 4px 12px rgba(0, 230, 118, 0.25);
  filter: brightness(1.1);
}

/* Calendar-icon button next to "Open today" on the hero trip card.
   :active gives finger-down feedback so the user knows the icon
   registered the tap even before the calendar tab finishes loading. */
.cal-btn { transition: transform 0.12s, background-color 0.12s, box-shadow 0.12s, filter 0.12s; }
.cal-btn:active {
  transform: scale(0.92);
  background: rgba(0, 230, 118, 0.28) !important;
  box-shadow: 0 0 0 2px rgba(0, 230, 118, 0.55), 0 4px 12px rgba(0, 230, 118, 0.25);
  filter: brightness(1.15);
}

.timeline-day { margin-bottom: 24px; }
.timeline-date { font-family: 'Outfit'; font-size: 16px; font-weight: 600; margin-bottom: 10px; display: flex; align-items: center; gap: 8px; }
.timeline-date .day-name { color: var(--green); }
.timeline-date .day-num { color: var(--text-dim); font-weight: 400; font-size: 14px; }
.timeline-segment { display: flex; gap: 10px; padding: 12px; background: var(--sidebar); border-radius: 10px; margin-bottom: 8px; border-left: 3px solid var(--border); transition: all 0.15s; cursor: pointer; flex-wrap: wrap; }
.timeline-segment:hover { background: var(--card); }
.timeline-segment.flight { border-left-color: var(--flight-color); }
.timeline-segment.hotel { border-left-color: var(--hotel-color); }
.timeline-segment.excursion { border-left-color: var(--excursion-color); }
.timeline-segment.transport { border-left-color: var(--transport-color); }
.timeline-segment.car_rental { border-left-color: var(--purple); }
.timeline-segment.bike_hire { border-left-color: var(--green); }
.timeline-segment.ferry { border-left-color: var(--blue); }
.timeline-segment.event { border-left-color: var(--amber); }
.seg-icon { font-size: 20px; width: 28px; text-align: center; flex-shrink: 0; padding-top: 2px; }
.seg-body { flex: 1; min-width: 0; overflow: hidden; }
.seg-title { font-weight: 600; font-size: 14px; margin-bottom: 3px; }
.seg-detail { font-size: 12px; color: #ccc8dd; word-break: break-word; }
.seg-time { font-family: 'DM Mono'; font-size: 12px; color: #e8e6f0; white-space: nowrap; }

.drop-zone { border: 2px dashed var(--border); border-radius: 12px; padding: 48px 24px; text-align: center; cursor: pointer; transition: all 0.2s; }
.drop-zone:hover, .drop-zone.dragover { border-color: var(--green); background: rgba(0,230,118,0.03); }
.drop-zone h3 { margin-bottom: 8px; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; display: flex; align-items: center; justify-content: center; }
.modal { position: relative; background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 28px; max-width: 520px; width: 90%; max-height: 85vh; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.modal h2 { margin-bottom: 16px; font-size: var(--text-2xl); padding-right: 36px; }
.modal-close { position: absolute; top: 10px; right: 10px; background: none; border: none; color: var(--text-muted); font-size: 26px; line-height: 1; cursor: pointer; padding: 6px 12px; border-radius: 8px; z-index: 1; }
.modal-close:hover { background: var(--sidebar); color: var(--text); }
body.modal-open { overflow: hidden; }
.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-size: var(--text-sm); color: var(--text-dim); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.form-section-title { font-size: var(--text-xs); color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.6px; margin: 16px 0 8px; padding-top: 8px; border-top: 1px solid var(--border); }
.form-section-title:first-child { margin-top: 0; padding-top: 0; border-top: none; }
@media (max-width: 768px) {
  .modal-overlay { align-items: flex-end; }
  /* padding-top includes safe-area-inset-top so the X / heading aren't hidden
     under the standalone-PWA status bar / Dynamic Island when the sheet grows
     tall enough to reach the screen top. */
  .modal { width: 100%; max-width: none; border-radius: 16px 16px 0 0; max-height: 92vh; padding: calc(20px + env(safe-area-inset-top)) 18px calc(20px + env(safe-area-inset-bottom)); }
  .modal-close { top: calc(8px + env(safe-area-inset-top)); right: 8px; }
}

/* Visual type chip grid (Add/Edit Segment). Matches the iOS type picker. */
.type-chip-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.type-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--bg); color: var(--text); font-size: var(--text-sm); cursor: pointer; transition: background 0.15s, border-color 0.15s; }
.type-chip:hover { border-color: var(--text-muted); }
.type-chip.selected { background: var(--chip-bg, rgba(255,255,255,0.08)); border-color: var(--chip-color, var(--text)); color: var(--chip-color, var(--text)); }
.type-chip .type-chip-emoji { font-size: 14px; line-height: 1; }

.empty-state { text-align: center; padding: 60px 20px; color: var(--text-dim); }
.empty-state h2 { color: var(--text); margin-bottom: 8px; }

.grid-2 { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }

.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.section-header h2 { font-size: 20px; }

.countdown { font-family: 'DM Mono'; font-size: 28px; font-weight: 700; color: var(--green); }
.countdown-label { font-size: 13px; color: var(--text-dim); margin-top: 2px; }
.timeline-header { margin-bottom: 20px; }
.timeline-header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.timeline-header-title { min-width: 0; flex: 1; }
.timeline-header-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
.timeline-countdown { text-align: right; margin-right: 12px; }

.tab-bar { display: flex; gap: 2px; margin-bottom: 20px; background: var(--sidebar); border-radius: 10px; padding: 3px; }
.tab-btn { flex: 1; padding: 8px; border: none; background: none; color: var(--text-dim); cursor: pointer; border-radius: 8px; font-size: 13px; font-family: 'DM Sans'; transition: all 0.15s; }
.tab-btn.active { background: var(--card); color: var(--text); }

.email-list-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: var(--sidebar); border-radius: 10px; margin-bottom: 8px; cursor: pointer; transition: background 0.15s; }
.email-list-item:hover { background: var(--card); }
.parse-status { font-size: 11px; text-transform: uppercase; }
.parse-status.parsed { color: var(--green); }
.parse-status.parsing { color: var(--amber); }
.parse-status.failed { color: var(--red); }
.parse-status.pending { color: var(--text-muted); }

.briefing-section { margin-bottom: 16px; }
.briefing-section h4 { color: var(--green); margin-bottom: 6px; font-size: 14px; }
.briefing-section p, .briefing-section li { font-size: 13px; color: var(--text-dim); line-height: 1.6; }
.briefing-section ul { list-style: none; padding: 0; }
.briefing-section li::before { content: "→ "; color: var(--green); }

.packing-category { margin-bottom: 12px; }
.packing-category h4 { font-size: 13px; color: var(--amber); margin-bottom: 4px; text-transform: uppercase; }
.packing-item { font-size: 13px; color: var(--text-dim); padding: 2px 0; }

/* Login screen */
.login-screen { display: flex; align-items: center; justify-content: center; min-height: 100vh; background: var(--bg); }
.login-box { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 40px; width: 380px; text-align: center; }
.login-box h1 { font-family: 'Outfit'; font-size: 32px; margin-bottom: 6px; }
.login-box h1 .accent { color: var(--green); }
.login-box p { color: var(--text-dim); margin-bottom: 24px; font-size: 14px; }
.login-divider { display: flex; align-items: center; gap: 12px; margin: 16px 0; color: var(--text-muted); font-size: 12px; }
.login-divider::before, .login-divider::after { content: ""; flex: 1; height: 1px; background: var(--border); }

/* Responsive */
@media (max-width: 768px) {
  .sidebar { display: none; }
  .app { flex-direction: column; }
  .main { margin-left: 0; padding: 16px; padding-top: calc(env(safe-area-inset-top) + 12px); flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; padding-bottom: 16px; }
  .mobile-bottom { flex-shrink: 0; }
  .mobile-fx { display: flex; background: var(--card); border-top: 1px solid var(--border); padding: 6px 16px; align-items: center; justify-content: space-between; cursor: pointer; -webkit-tap-highlight-color: rgba(0,230,118,0.1); }
  .mobile-nav { display: flex; background: var(--sidebar); border-top: 1px solid var(--border); padding: 4px 0 env(safe-area-inset-bottom); }
  .mobile-nav .nav-item { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 6px 2px; border-left: none; gap: 2px; font-size: 9px; min-width: 0; }
  .mobile-nav .nav-item .nav-icon { font-size: 18px; line-height: 1; }
  .mobile-nav .nav-item.active { border-left: none; color: var(--green); }
  .grid-2 { grid-template-columns: 1fr; }
  .input { word-break: break-word; }
  /* Timeline header: stack the title and the action row on mobile so the title gets the full width */
  .timeline-header-row { flex-direction: column; align-items: stretch; gap: 12px; }
  .timeline-header-title h2 { font-size: 20px !important; }
  .timeline-header-actions { flex-wrap: wrap; gap: 6px; }
  .timeline-countdown { text-align: left; margin-right: 0; margin-left: auto; }
  .timeline-countdown .countdown { font-size: 22px; }
  .timeline-countdown .countdown-label { font-size: 11px; }
  /* Defensive: long unbreakable strings (order numbers, ticket numbers, URLs) should wrap rather than blow out the layout */
  .timeline-segment, .seg-body, .seg-detail, .seg-title { overflow-wrap: anywhere; }
  .main h2 { overflow-wrap: anywhere; }
  /* iOS Safari auto-zooms in on inputs with font-size < 16px, leaves the viewport
     offset to the right after blur. Force >=16px on all form controls to prevent it.
     Also add min-width:0 so inputs inside grids can shrink below their default size. */
  input, select, textarea, .input { font-size: 16px !important; min-width: 0 !important; max-width: 100%; }
  /* Hard cap horizontal scroll at the document level as a final safety net */
  html, body, #root { max-width: 100vw; overflow-x: hidden; }
  /* Expanded segment detail card: drop the 44px left indent on mobile so right-edge
     content (Delete button, status badges, lounge details) isn't clipped by overflow:hidden */
  .seg-detail-card { margin-left: 8px !important; }
  .seg-detail-card .form-group { min-width: 0; }
}
.mobile-bottom { display: none; }
@media (max-width: 768px) { .mobile-bottom { display: block !important; } }

.pull-indicator { position: fixed; top: 0; left: 0; right: 0; z-index: 200; display: flex; justify-content: center; align-items: center; transition: height 0.2s, opacity 0.2s; overflow: hidden; background: var(--sidebar); border-bottom: 1px solid var(--border); }
.pull-indicator .spinner { width: 20px; height: 20px; border: 2px solid var(--border); border-top-color: var(--green); border-radius: 50%; animation: spin 0.6s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.timeline-segment.dragging { opacity: 0.4; }
.timeline-segment.drag-over { border-top: 2px solid var(--green); margin-top: -2px; }
.drag-handle { cursor: grab; color: var(--text-muted); font-size: 14px; padding: 0 4px; user-select: none; flex-shrink: 0; display: flex; align-items: center; }
.drag-handle:active { cursor: grabbing; }
.desktop-only { display: flex; }
.mobile-only { display: none !important; }
/* Mobile reorder controls show on narrow viewports OR any touch-primary device.
   The (hover: none) + (pointer: coarse) combo catches iPads in landscape and
   other tablets where width > 768px but HTML5 drag-and-drop won't fire. */
@media (max-width: 768px), (hover: none) and (pointer: coarse) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
}
.day-note-text { font-size: 13px; color: #fff; padding: 4px 0; font-style: italic; }
.day-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.ai-suggestion { font-size: 13px; color: #e8e6f0; padding: 10px 14px; background: rgba(0,230,118,0.05); border-radius: 8px; margin-bottom: 8px; border-left: 2px solid var(--green); line-height: 1.5; }
`;

// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: "trips", label: "My Trips", icon: "✈" },
  { id: "timeline", label: "Timeline", icon: "◈" },
  { id: "calendar", label: "Calendar", icon: "◎" },
  { id: "inbox", label: "Inbox", icon: "✉" },
  { id: "ai", label: "AI Assistant", icon: "✦" },
  { id: "profile", label: "Profile", icon: "◑" },
];

// IATA airport code → primary city (lower-case for direct substring match).
// Mirrors the server's AIRPORT_CITIES table used during route_cities
// resolution, so a city pill like "Barcelona" can find a segment whose
// location is just "BCN". Lower-case kept for cheap lookup.
const AIRPORT_TO_CITY = {
  // Australia
  mel: "melbourne", syd: "sydney", bne: "brisbane", per: "perth", adl: "adelaide", ool: "gold coast", cns: "cairns", hba: "hobart",
  // South America
  scl: "santiago", pnt: "puerto natales", cjc: "calama", lim: "lima", gru: "são paulo", gig: "rio de janeiro", eze: "buenos aires",
  // Asia
  hkg: "hong kong", sin: "singapore", bkk: "bangkok", nrt: "tokyo", hnd: "tokyo", icn: "seoul", tpe: "taipei",
  kul: "kuala lumpur", pnh: "phnom penh", han: "hanoi", sgn: "ho chi minh city", dps: "bali", cnx: "chiang mai", hkt: "phuket", rep: "siem reap", del: "delhi", bom: "mumbai",
  // Europe — UK
  lgw: "london", lhr: "london", stn: "london", ltn: "london", lcy: "london",
  // Europe — rest
  cdg: "paris", ory: "paris", agp: "malaga", bcn: "barcelona", mad: "madrid", fco: "rome", mxp: "milan", lin: "milan",
  ams: "amsterdam", dub: "dublin", edi: "edinburgh", lis: "lisbon", opo: "porto", ber: "berlin", muc: "munich", fra: "frankfurt",
  prg: "prague", vie: "vienna", bud: "budapest", waw: "warsaw", krk: "krakow", ath: "athens", ist: "istanbul", cph: "copenhagen",
  arn: "stockholm", osl: "oslo", hel: "helsinki", kef: "reykjavik", rkv: "reykjavik", aey: "akureyri",
  // North America
  jfk: "new york", lga: "new york", ewr: "new york", lax: "los angeles", sfo: "san francisco", ord: "chicago", mia: "miami", sea: "seattle", bos: "boston", iad: "washington", dca: "washington",
  yvr: "vancouver", yyc: "calgary", yul: "montreal", yyz: "toronto", yow: "ottawa", yeg: "edmonton", yhz: "halifax", yfb: "iqaluit",
  // Greenland
  goh: "nuuk", jav: "ilulissat", sfj: "kangerlussuaq",
  // Middle East
  dxb: "dubai", auh: "abu dhabi", doh: "doha",
};

const SEG_ICONS = { flight: "✈", hotel: "🏨", friends_house: "🏠", excursion: "🥾", train: "🚆", transport: "🚗", car_rental: "🚙", bike_hire: "🚲", cycle_ride: "🚴", walk: "🚶", gym: "🏋️", ferry: "⛴", restaurant: "🍽", event: "🎭", other: "📌" };
const TRANSPORT_ICONS = { car: "🚗", taxi: "🚕", train: "🚆", bus: "🚌", tube: "🚇", ferry: "⛴", yacht: "🚤", helicopter: "🚁", tuk_tuk: "🛺", other: "🚗" };
// Anything that covers a stay (date span where the user is "at" a place).
// Hotel and friends_house share the same date-spanning behaviour for
// location lookup, covering windows, weather, etc — but only `hotel`
// gets the AI hotel guide.
const ACCOMMODATION_TYPES = new Set(["hotel", "friends_house"]);
const getSegIcon = (seg) => seg.type === "transport" && seg.transport_mode ? (TRANSPORT_ICONS[seg.transport_mode] || "🚗") : (SEG_ICONS[seg.type] || "📌");
const SEG_COLORS = { flight: "var(--flight-color)", hotel: "var(--hotel-color)", friends_house: "var(--hotel-color)", excursion: "var(--excursion-color)", train: "var(--transport-color)", transport: "var(--transport-color)", car_rental: "var(--purple)", bike_hire: "var(--green)", cycle_ride: "var(--green)", walk: "var(--green)", gym: "var(--green)", ferry: "var(--blue)", event: "var(--amber)" };
// Ordered list of selectable segment types for the Add/Edit Segment modal.
// Order matches the iOS picker (flight first, "other" last). Add new types
// here AND to SEG_ICONS / SEG_COLORS / email-parser.js / iOS SegmentType.
const SEGMENT_TYPE_OPTIONS = [
  { value: "flight",        label: "Flight" },
  { value: "hotel",         label: "Hotel" },
  { value: "friends_house", label: "Friends' house" },
  { value: "train",         label: "Train" },
  { value: "transport",     label: "Transport" },
  { value: "excursion",     label: "Excursion" },
  { value: "car_rental",    label: "Car rental" },
  { value: "bike_hire",     label: "Bike hire" },
  { value: "cycle_ride",    label: "Cycle ride" },
  { value: "walk",          label: "Walk" },
  { value: "ferry",         label: "Ferry" },
  { value: "restaurant",    label: "Restaurant" },
  { value: "event",         label: "Event" },
  { value: "gym",           label: "Gym" },
  { value: "other",         label: "Other" },
];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}
function formatTime(dateStr) {
  if (!dateStr) return "";
  const t = dateStr.split("T")[1];
  return t ? t.slice(0, 5) : "";
}
// Date-aware time range: appends "+1d" / "+2d" when arrival's date is later
// than departure's — but only for FLIGHTS, where it's signalling a timezone
// crossing the traveller actually experiences. Hotels and car rentals also
// span multiple days but their times are check-in/check-out wall-clocks, so a
// "+2d" badge there is meaningless and just creates clutter.
function formatTimeRange(start, end, segType) {
  const st = formatTime(start);
  if (!end) return st;
  const et = formatTime(end);
  if (!et) return st;
  const sd = start.split("T")[0], ed = end.split("T")[0];
  if (sd === ed) return `${st} → ${et}`;
  if (segType !== "flight") return `${st} → ${et}`;
  const diff = Math.round((new Date(ed + "T00:00:00") - new Date(sd + "T00:00:00")) / 86400000);
  return diff > 0 ? `${st} → ${et} +${diff}d` : `${st} → ${et}`;
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target - now) / 86400000);
}

/// Today's date as YYYY-MM-DD in the *local* timezone. The naive
/// `new Date().toISOString().split("T")[0]` antipattern returns UTC,
/// which lands on the wrong day at the edges of midnight for any
/// user not at UTC+0 — every "Open today lands on wrong day" /
/// "calendar highlights wrong day" / "weather looks up wrong day"
/// bug we've had traced back here. This is the single source of
/// truth; do not use toISOString for date keys anywhere.
function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ══════════════════════════════════════════════════════════════════════════
// APP
// ══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tripiq_tokens")); } catch { return null; }
  });
  // Track online/offline so we can warn the user when the data they're seeing
  // is the cached snapshot. navigator.onLine isn't perfectly accurate but
  // it's the standard PWA signal.
  const [isOnline, setIsOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const [tab, setTab] = useState("trips");
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateTrip, setShowCreateTrip] = useState(false);
  const [showAddSegment, setShowAddSegment] = useState(false);
  // Default start-date for the Add Segment modal. Captured when the
  // user clicks + from the timeline so the form opens with the day
  // they were looking at instead of today (which is rarely what they
  // mean when scrolled six weeks into the future).
  const [addSegmentDefaultDate, setAddSegmentDefaultDate] = useState(null);
  // Pending scroll target after a navigation that loads the timeline (e.g.
  // creating a new segment, or clicking a city/alert on the My Trips card).
  // Shape: { segId } to anchor a specific segment, or { city } to match the
  // first segment whose start/end location mentions the city. TimelineView
  // clears it via onScrolledToSegment so it doesn't fire twice.
  const [pendingScroll, setPendingScroll] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  // Today-mode "What's near me?" — opened from the in-progress hero on
  // the trips landing screen. Stores the trip whose today_city the
  // panel should anchor on. Mirrors iOS's TripInProgressHero button.
  const [nearMeTrip, setNearMeTrip] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [excursions, setExcursions] = useState(null);
  const [packing, setPacking] = useState(null);
  const [aiLoading, setAiLoading] = useState("");
  const [googleClientId, setGoogleClientId] = useState(null);
  const [appleConfig, setAppleConfig] = useState(null); // { serviceId, redirectURI }
  const [fxRate, setFxRate] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState(null); // { segments, emailIds }
  const [showTripSelector, setShowTripSelector] = useState(false);

  // ── Auth helpers ─────────────────────────────────────────────────────
  const refreshPromiseRef = useRef(null);
  const authFetch = useCallback(async (url, opts = {}) => {
    if (!tokens?.accessToken) throw new Error("Not authenticated");
    // Let the browser set Content-Type (with boundary) for FormData bodies —
    // forcing application/json breaks multipart uploads.
    const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
    const buildHeaders = (token) => isForm
      ? { ...opts.headers, Authorization: `Bearer ${token}` }
      : { ...opts.headers, Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    let res = await fetch(url, { ...opts, headers: buildHeaders(tokens.accessToken) });
    if (res.status === 401 && tokens?.refreshToken) {
      // Use a shared promise so only one refresh runs at a time
      if (!refreshPromiseRef.current) {
        refreshPromiseRef.current = fetch("/api/auth/refresh", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        }).then(rr => {
          if (!rr.ok) throw new Error("refresh failed");
          return rr.json();
        }).then(newTokens => {
          setTokens(newTokens);
          localStorage.setItem("tripiq_tokens", JSON.stringify(newTokens));
          setUser(newTokens.user);
          return newTokens.accessToken;
        }).catch(() => null)
        .finally(() => { refreshPromiseRef.current = null; });
      }
      const newToken = await refreshPromiseRef.current;
      if (newToken) {
        res = await fetch(url, { ...opts, headers: buildHeaders(newToken) });
      }
    }
    return res;
  }, [tokens]);

  const logout = () => {
    setUser(null); setTokens(null);
    localStorage.removeItem("tripiq_tokens");
  };

  // ── Load auth config ────────────────────────────────────────────────
  useEffect(() => {
    fetch("/api/auth/config").then(r => r.json()).then(c => {
      setGoogleClientId(c.googleClientId);
      if (c.appleServiceId) setAppleConfig({ serviceId: c.appleServiceId, redirectURI: c.appleRedirectURI || window.location.origin + "/" });
    }).catch(() => {});
  }, []);

  // Catch async / event-handler errors that the ErrorBoundary can't see —
  // those keep the UI alive but go silent today. Forward to the same sink.
  useEffect(() => {
    const onError = (e) => reportError("window.error", e.error || e.message, { filename: e.filename, lineno: e.lineno });
    const onRejection = (e) => reportError("unhandledrejection", e.reason);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  // Capture the PWA install prompt globally — the event fires once early in
  // the page lifecycle, so listeners scoped to a specific tab (Profile) would
  // miss it if the user is somewhere else. ProfileView consumes via prop.
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  useEffect(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    if (standalone) setIsInstalled(true);
    const onBefore = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setIsInstalled(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // ── FX rates ────────────────────────────────────────────────────────
  const fetchFxRates = useCallback(async () => {
    setFxLoading(true);
    try {
      // Prefer the selected trip; otherwise the next upcoming trip (skip past ones)
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const upcoming = trips.filter(t => !t.end_date || new Date(t.end_date) >= today);
      const trip = tripData || upcoming[0];
      const currencies = trip?.currencies ? (() => { try { return JSON.parse(trip.currencies); } catch { return []; } })() : [];
      const homeCurrency = "AUD"; // TODO: from user profile
      const pairs = currencies.filter(c => c !== homeCurrency).map(c => `${homeCurrency}${c}`);
      if (pairs.length === 0) { setFxRate({}); setFxLoading(false); return; }
      const r = await fetch(`/api/fx/multi?pairs=${pairs.join(",")}`);
      const data = await r.json();
      setFxRate(data); // now an object like { AUDCLP: { rate, change, ts }, AUDTHB: { ... } }
    } catch {} finally { setFxLoading(false); }
  }, [tripData, trips]);

  // ── Version check (auto-update for PWA) ────────────────────────────
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const appVersion = useRef(null);

  useEffect(() => {
    // Get initial version
    fetch("/api/version").then(r => r.json()).then(d => { appVersion.current = d.version; }).catch(() => {});

    // Poll every 60 seconds for new version
    const interval = setInterval(() => {
      fetch("/api/version").then(r => r.json()).then(d => {
        if (appVersion.current && d.version !== appVersion.current) {
          setUpdateAvailable(true);
        }
      }).catch(() => {});
    }, 60 * 1000);

    // Also listen for service worker updates
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (newSW) {
            newSW.addEventListener("statechange", () => {
              if (newSW.state === "installed" && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      });
      // Check for updates on focus (when user switches back to the app)
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          navigator.serviceWorker.ready.then((reg) => reg.update()).catch(() => {});
        }
      });
    }

    return () => clearInterval(interval);
  }, []);

  const applyUpdate = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        if (reg.waiting) reg.waiting.postMessage("skipWaiting");
      });
    }
    // Clear all caches and reload
    if ("caches" in window) {
      caches.keys().then(names => Promise.all(names.map(n => caches.delete(n)))).then(() => {
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  };
  useEffect(() => { fetchFxRates(); }, [fetchFxRates]);

  // ── Auto-login from stored tokens ──────────────────────────────────
  // One-shot bootstrap against whatever token was in localStorage at mount;
  // re-running on every token change would invalidate the refresh.
  useEffect(() => {
    if (!tokens?.accessToken) return;
    fetch("/api/auth/me", { headers: { Authorization: `Bearer ${tokens.accessToken}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(u => setUser(u))
      .catch(() => {
        // Try refresh — don't logout on failure
        fetch("/api/auth/refresh", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        }).then(r => r.json()).then(d => {
          if (d.accessToken) { setTokens(d); localStorage.setItem("tripiq_tokens", JSON.stringify(d)); setUser(d.user); }
        }).catch(() => {});
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch trips ────────────────────────────────────────────────────
  const fetchTrips = useCallback(async () => {
    try {
      const r = await authFetch("/api/trips");
      if (!r || !r.ok) return;
      const data = await r.json();
      if (Array.isArray(data)) setTrips(data);
    } catch {}
  }, [authFetch]);

  useEffect(() => { if (user) fetchTrips(); }, [user, fetchTrips]);

  // ── Select trip ────────────────────────────────────────────────────
  // Stage all fetches before committing state so the timeline never renders
  // with new tripData + stale timeline (or vice versa). That mismatch was
  // the most likely trigger for the blank-screen-after-save bug.
  const selectTrip = useCallback(async (tripId, opts = {}) => {
    const targetTab = opts.tab || "timeline";
    setSelectedTrip(tripId);
    try {
      const tripRes = await authFetch(`/api/trips/${tripId}`);
      if (!tripRes || !tripRes.ok) { setTab("trips"); fetchTrips(); return; }
      const td = await tripRes.json();
      // Note: generate-transfers used to run on every trip open here. It
      // was undoing user deletes — you'd remove an unwanted auto-transfer
      // on iOS, reopen the trip in the PWA, and the generator would
      // recreate it. Endpoint still exists (call manually if you want a
      // fresh round of suggestions) but no longer fires automatically.
      const timelineRes = await authFetch(`/api/trips/${tripId}/timeline`);
      const tl = (timelineRes && timelineRes.ok) ? await timelineRes.json() : null;
      setTripData(td);
      if (tl) setTimeline(tl);
      setTab(targetTab);
    } catch {}
  }, [authFetch, fetchTrips]);

  // ── Fetch emails ───────────────────────────────────────────────────
  // Defensive: if the API hands back an error object (or the SW serves a
  // stale non-array response) keep the previous list rather than poisoning
  // emails with a non-array — every downstream .filter() blows the UI up
  // through the ErrorBoundary if we don't.
  const fetchEmails = useCallback(async () => {
    try {
      const r = await authFetch("/api/emails");
      if (!r || !r.ok) return;
      const data = await r.json();
      if (Array.isArray(data)) setEmails(data);
    } catch {}
  }, [authFetch]);

  useEffect(() => { if (user && tab === "inbox") fetchEmails(); }, [user, tab, fetchEmails]);

  // ── Create trip ────────────────────────────────────────────────────
  const handleCreateTrip = async (formData) => {
    try {
      const r = await authFetch("/api/trips", { method: "POST", body: JSON.stringify(formData) });
      const trip = await r.json();
      setShowCreateTrip(false);
      fetchTrips();
      selectTrip(trip.id);
    } catch {}
  };

  // ── Edit trip ─────────────────────────────────────────────────────
  const handleEditTrip = async (tripId, data) => {
    try {
      await authFetch(`/api/trips/${tripId}`, { method: "PUT", body: JSON.stringify(data) });
      setEditingTrip(null);
      fetchTrips();
      if (selectedTrip === tripId) selectTrip(tripId);
    } catch {}
  };

  // ── Upload email content ──────────────────────────────────────────
  const handleUploadContent = async (content, subject) => {
    setLoading(true);
    try {
      const r = await authFetch("/api/emails/upload", {
        method: "POST",
        body: JSON.stringify({ content, subject: subject || "Pasted content", contentType: "html" }),
      });
      const data = await r.json();
      fetchEmails();
      if (data.segments?.length > 0) {
        const emailIds = [data.email?.id].filter(Boolean);
        setPendingEmailData({ segments: data.segments, emailIds });
        setShowTripSelector(true);
      }
      return data;
    } catch (err) {
      return { error: err.message };
    } finally { setLoading(false); }
  };

  // ── Upload .eml file ──────────────────────────────────────────────
  const handleUploadFiles = async (files) => {
    setLoading(true);
    try {
      const formData = new FormData();
      for (const file of files) formData.append("file", file);
      const r = await authFetch("/api/emails/upload", { method: "POST", body: formData });
      const data = await r.json();
      fetchEmails();
      if (data.segments?.length > 0) {
        const emailIds = data.emails ? data.emails.map(e => e.id) : [data.email?.id].filter(Boolean);
        setPendingEmailData({ segments: data.segments, emailIds });
        setShowTripSelector(true);
      }
      return data;
    } catch (err) {
      return { error: err.message };
    } finally { setLoading(false); }
  };

  // ── AI features ────────────────────────────────────────────────────
  const getTripDestination = () => {
    if (tripData?.destination) return tripData.destination;
    if (tripData?.route_cities) {
      try {
        const cities = JSON.parse(tripData.route_cities);
        const home = cities[0];
        const dests = [...new Set(cities.filter(c => c.toLowerCase() !== home?.toLowerCase()))];
        return dests.join(", ") || null;
      } catch { return null; }
    }
    return null;
  };

  const fetchBriefing = async () => {
    const dest = getTripDestination();
    if (!dest) return;
    setAiLoading("briefing");
    try {
      const r = await authFetch("/api/ai/briefing", {
        method: "POST",
        body: JSON.stringify({ destination: dest, start_date: tripData.start_date, end_date: tripData.end_date }),
      });
      setBriefing(await r.json());
    } catch {} finally { setAiLoading(""); }
  };

  const fetchExcursions = async () => {
    const dest = getTripDestination();
    if (!dest) return;
    setAiLoading("excursions");
    try {
      const existing = tripData.segments?.filter(s => s.type === "excursion").map(s => s.title) || [];
      const hotels = tripData.segments?.filter(s => ACCOMMODATION_TYPES.has(s.type)).map(s => ({
        name: s.title, location: s.start_location || s.end_location, excursions: s.hotel_excursions || null,
      })) || [];
      const r = await authFetch("/api/ai/excursions", {
        method: "POST",
        body: JSON.stringify({ destination: dest, start_date: tripData.start_date, end_date: tripData.end_date, existing_activities: existing, hotels }),
      });
      setExcursions(await r.json());
    } catch {} finally { setAiLoading(""); }
  };

  const fetchPacking = async () => {
    const dest = getTripDestination();
    if (!dest) return;
    setAiLoading("packing");
    try {
      const activities = tripData.segments?.filter(s => s.type === "excursion").map(s => s.title) || [];
      const r = await authFetch("/api/ai/packing", {
        method: "POST",
        body: JSON.stringify({ destination: dest, start_date: tripData.start_date, end_date: tripData.end_date, activities }),
      });
      setPacking(await r.json());
    } catch {} finally { setAiLoading(""); }
  };

  // ── Delete segment ─────────────────────────────────────────────────
  const deleteSegment = async (segId) => {
    try {
      await authFetch(`/api/segments/${segId}`, { method: "DELETE" });
      if (selectedTrip) selectTrip(selectedTrip);
    } catch {}
  };

  // Replace a single segment inside the current `timeline` state with the
  // server's updated row. Avoids the full /api/trips + /timeline refetch
  // that previously fired after every edit — that refetch tore down and
  // re-rendered every day card, and the resulting layout churn raced the
  // scroll-restore code and bounced the user off their viewing position.
  // For most edits the segment stays on the same day, so a surgical
  // replace covers the common case without disturbing the DOM around it.
  // When start_datetime moves the segment to a different day, we have to
  // re-bucket — handled here.
  const patchSegmentInTimeline = useCallback((updated) => {
    if (!updated || updated.id == null) return;
    setTimeline((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      // Walk every day, replace if present. Track whether we found it AND
      // whether the day matches the updated segment's start_datetime.
      const newDateKey = (updated.start_datetime || "").slice(0, 10);
      let foundOnDay = null;
      const patched = prev.map((day) => {
        const idx = (day.segments || []).findIndex((s) => s.id === updated.id);
        if (idx === -1) return day;
        foundOnDay = day.date;
        // Preserve any client-only fields the server doesn't send back
        // (e.g. `_view` splits for car_rental / ferry).
        const merged = { ...day.segments[idx], ...updated };
        const segments = [...day.segments];
        segments[idx] = merged;
        return { ...day, segments };
      });
      if (!foundOnDay) return prev; // didn't find it; let next refetch resync
      if (newDateKey && newDateKey !== foundOnDay) {
        // start_datetime moved the segment to a different day — pop from
        // its current day and push onto the new one, preserving sort by
        // start_datetime within the day.
        const removed = patched.map((day) => {
          if (day.date !== foundOnDay) return day;
          return { ...day, segments: day.segments.filter((s) => s.id !== updated.id) };
        });
        return removed.map((day) => {
          if (day.date !== newDateKey) return day;
          const segs = [...day.segments, { ...updated }];
          segs.sort((a, b) => String(a.start_datetime || "").localeCompare(String(b.start_datetime || "")));
          return { ...day, segments: segs };
        });
      }
      return patched;
    });
  }, []);

  // ── Update segment notes ──────────────────────────────────────────
  const updateSegmentNotes = async (segId, notes) => {
    try {
      const r = await authFetch(`/api/segments/${segId}`, { method: "PUT", body: JSON.stringify({ notes }) });
      if (r && r.ok) {
        const updated = await r.json();
        patchSegmentInTimeline(updated);
      }
    } catch {}
  };

  // Ref for the scrollable main column. (Pull-to-refresh handlers used to
  // live here but were never wired into the JSX — removed.)
  const mainRef = useRef(null);

  // ══════════════════════════════════════════════════════════════════════
  // PASSWORD RESET — handled at the App level so the modal appears even
  // when the user is already logged in (otherwise clicking a reset link
  // while signed in silently lands you on the main app and the URL is
  // ignored). On success we swap tokens for whichever account the reset
  // belongs to, effectively signing the user in as that account.
  // ══════════════════════════════════════════════════════════════════════
  const resetTokenInUrl = window.location.pathname.match(/\/reset\/([a-f0-9]+)/)?.[1] || "";

  // ══════════════════════════════════════════════════════════════════════
  // LOGIN SCREEN
  // ══════════════════════════════════════════════════════════════════════
  if (!user) return (<>
    <LoginScreen setUser={setUser} setTokens={(t) => { setTokens(t); localStorage.setItem("tripiq_tokens", JSON.stringify(t)); }} googleClientId={googleClientId} appleConfig={appleConfig} />
  </>);

  // ══════════════════════════════════════════════════════════════════════
  // MAIN APP
  // ══════════════════════════════════════════════════════════════════════
  const countdown = tripData?.start_date ? daysUntil(tripData.start_date) : null;

  return (
    <>
      <style>{STYLE}</style>
      {/* Update banner */}
      {updateAvailable && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: "var(--green)", color: "#111",
          padding: "env(safe-area-inset-top, 4px) 12px 4px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontFamily: "DM Sans", fontSize: 13, fontWeight: 500,
        }}>
          <span onClick={applyUpdate} style={{ cursor: "pointer" }}>Update available — <strong>tap here</strong></span>
          <span onClick={(e) => { e.stopPropagation(); setUpdateAvailable(false); }} style={{ cursor: "pointer", padding: "2px 8px", fontSize: 16 }}>✕</span>
        </div>
      )}
      {!isOnline && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1500,
          background: "var(--amber)", color: "#1a1630", padding: "8px 16px",
          fontSize: 13, fontWeight: 600, textAlign: "center",
          paddingTop: "calc(env(safe-area-inset-top) + 8px)",
        }}>
          ⚠ Offline — showing cached data. Edits will fail until you reconnect.
        </div>
      )}
      <div className="app">
        {/* Sidebar */}
        <nav className="sidebar">
          <div className="sidebar-logo">Trip<span>IQ</span></div>
          {TABS.map(t => (
            <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
              <span className="nav-icon">{t.icon}</span> {t.label}
            </div>
          ))}
          <div style={{ flex: 1 }} />
          {/* FX Widget */}
          <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", cursor: "pointer" }} onClick={fetchFxRates} title="Click to refresh">
            {fxRate && typeof fxRate === "object" && Object.keys(fxRate).length > 0 ? (
              <div style={{ fontFamily: "DM Mono", fontSize: 12 }}>
                {Object.entries(fxRate).map(([pair, data]) => {
                  if (!data || !data.rate) return null;
                  const home = pair.slice(0, 3);
                  const dest = pair.slice(3);
                  return (
                    <div key={pair} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ color: "var(--green)", fontFamily: "DM Sans", fontWeight: 600 }}>{home} → {dest}</span>
                        {data.change != null && (
                          <span style={{ color: data.change >= 0 ? "var(--green)" : "var(--red)" }}>
                            {data.change >= 0 ? "▲" : "▼"}{data.change >= 0 ? "+" : ""}{data.change.toFixed(2)}%
                          </span>
                        )}
                      </div>
                      <div style={{ color: "#fff", marginBottom: 2 }}>{home[0]}$1 = <span style={{ color: "var(--green)", fontWeight: 600 }}>{data.rate.toFixed(2)}</span> {dest}</div>
                      <div style={{ color: "#fff" }}>{home[0]}$100 = <span style={{ color: "var(--green)", fontWeight: 600 }}>{(100 * data.rate).toLocaleString("en-AU", { maximumFractionDigits: 0 })}</span> {dest}</div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "DM Sans" }}>
                  {fxLoading ? "updating..." : "click to refresh"}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{fxLoading ? "Loading..." : "No FX data"}</div>
            )}
          </div>
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 4 }}>{user.name || user.email}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", cursor: "pointer" }} onClick={logout}>Sign out</div>
          </div>
        </nav>

        {/* Main content — scrollable area with pull-to-refresh */}
        <main className="main" ref={mainRef}>
          {tab === "trips" && (
            <TripsView trips={trips} onSelect={selectTrip}
              onSelectAt={(tripId, target) => { if (target) setPendingScroll(target); selectTrip(tripId); }}
              onOpenCalendar={(tripId) => { selectTrip(tripId, { tab: "calendar" }); }}
              onOpenNearMe={(trip) => setNearMeTrip(trip)}
              onCreate={() => setShowCreateTrip(true)} authFetch={authFetch} onRefresh={fetchTrips} onEditTrip={(trip) => setEditingTrip(trip)} user={user} />
          )}
          {tab === "timeline" && (() => {
            const isOwner = tripData?.isOwner;
            const memberRole = tripData?.members?.find(m => m.user_id === user?.id)?.role;
            const canEdit = isOwner; // Only owner can edit trip data
            const canAnnotate = isOwner || memberRole === "editor"; // Owner + editors can add personal notes
            return (
            <TimelineView tripData={tripData} timeline={timeline} countdown={countdown}
              canEdit={canEdit} canAnnotate={canAnnotate} userId={user?.id} userName={user?.name}
              onBack={() => setTab("trips")}
              onAddSegment={canEdit ? () => {
                // Find the topmost day card whose bottom edge is past the
                // viewport's top. That's the day the user is looking at —
                // use it as the form's default start date.
                const cards = document.querySelectorAll('[id^="day-"]');
                let pick = null;
                for (const c of cards) {
                  if (c.getBoundingClientRect().bottom > 0) { pick = c.id.replace("day-", ""); break; }
                }
                setAddSegmentDefaultDate(pick || localDateKey());
                setShowAddSegment(true);
              } : null}
              onDeleteSegment={canEdit ? deleteSegment : null} onUpdateNotes={canEdit ? updateSegmentNotes : null}
              onInvite={isOwner ? () => setShowInvite(true) : null}
              onEditTrip={isOwner ? () => setEditingTrip(tripData) : null}
              onReorder={canEdit ? async (segmentIds) => {
                await authFetch("/api/segments/reorder", { method: "POST", body: JSON.stringify({ segment_ids: segmentIds }) });
                if (selectedTrip) selectTrip(selectedTrip);
              } : null}
              authFetch={authFetch} selectedTrip={selectedTrip}
              pendingScroll={pendingScroll} onScrolledToSegment={() => setPendingScroll(null)}
              onRefreshTrip={() => { if (selectedTrip) selectTrip(selectedTrip); }}
              onOpenAiTab={() => setTab("ai")}
              patchSegmentInTimeline={patchSegmentInTimeline} />
          );})()}
          {tab === "calendar" && (
            <CalendarView tripData={tripData} timeline={timeline} onSelectTrip={() => setTab("trips")}
              onNavigateToDay={(date) => { setPendingScroll({ date }); setTab("timeline"); }} />
          )}
          {tab === "inbox" && (
            <InboxView emails={emails} loading={loading} selectedTrip={selectedTrip} trips={trips}
              onUploadContent={handleUploadContent} onUploadFiles={handleUploadFiles} onRefresh={fetchEmails}
              authFetch={authFetch} onSelectTrip={(tripId, target) => { if (target) setPendingScroll(target); selectTrip(tripId); }} />
          )}
          {tab === "ai" && (
            <AIView tripData={tripData} briefing={briefing} excursions={excursions} packing={packing}
              aiLoading={aiLoading} onBriefing={fetchBriefing} onExcursions={fetchExcursions} onPacking={fetchPacking} authFetch={authFetch} />
          )}
          {tab === "profile" && (
            <ProfileView user={user} authFetch={authFetch} onLogout={logout}
              installPrompt={installPrompt} isInstalled={isInstalled}
              onPromptUsed={() => setInstallPrompt(null)} />
          )}
        </main>

        {/* Mobile bottom bar — AFTER main so it's pinned at bottom via flexbox */}
        <div className="mobile-bottom">
          {fxRate && typeof fxRate === "object" && Object.keys(fxRate).length > 0 && (() => {
            const pairs = Object.entries(fxRate).filter(([, d]) => d && d.rate);
            if (pairs.length === 0) return null;
            // Cycle through pairs — tap advances to next
            const fxIdx = window._fxIdx || 0;
            const [pair, data] = pairs[fxIdx % pairs.length];
            const home = pair.slice(0, 3);
            const dest = pair.slice(3);
            return (
              <div className="mobile-fx" onClick={() => { window._fxIdx = ((window._fxIdx || 0) + 1) % pairs.length; fetchFxRates(); }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 10, color: "var(--green)", fontWeight: 600 }}>{home}→{dest}</span>
                  <span style={{ fontFamily: "DM Mono", fontSize: 13, fontWeight: 600, color: "#fff" }}>A$1={data.rate.toFixed(2)}</span>
                  {data.change != null && (
                    <span style={{ fontSize: 10, color: data.change >= 0 ? "var(--green)" : "var(--red)" }}>
                      {data.change >= 0 ? "▲" : "▼"}{Math.abs(data.change).toFixed(2)}%
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 9, color: "#fff" }}>{pairs.length > 1 ? `${fxIdx % pairs.length + 1}/${pairs.length} tap ▶` : "tap ↻"}</span>
              </div>
            );
          })()}
          <nav className="mobile-nav">
            {TABS.map(t => (
              <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                <span className="nav-icon">{t.icon}</span><span>{t.label}</span>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Modals */}
      {resetTokenInUrl && (
        <ResetPasswordModal token={resetTokenInUrl}
          onClose={() => window.history.replaceState({}, "", "/")}
          onDone={(data) => {
            setTokens(data); localStorage.setItem("tripiq_tokens", JSON.stringify(data));
            setUser(data.user);
            window.history.replaceState({}, "", "/");
          }}
          currentUserEmail={user.email} />
      )}
      {showCreateTrip && <CreateTripModal onClose={() => setShowCreateTrip(false)} onCreate={handleCreateTrip} />}
      {showAddSegment && selectedTrip && (
        <AddSegmentModal tripId={selectedTrip} defaultDate={addSegmentDefaultDate}
          onClose={() => setShowAddSegment(false)} authFetch={authFetch}
          onDone={(newId) => { setShowAddSegment(false); if (newId) setPendingScroll({ segId: newId }); selectTrip(selectedTrip); }} />
      )}
      {showInvite && tripData && (
        <InviteModal trip={tripData} onClose={() => setShowInvite(false)} authFetch={authFetch} />
      )}
      {editingTrip && (
        <EditTripModal trip={editingTrip} onClose={() => setEditingTrip(null)}
          onSave={(data) => handleEditTrip(editingTrip.id, data)}
          authFetch={authFetch} onRegenerated={fetchTrips} />
      )}
      {nearMeTrip && (
        <Modal onClose={() => setNearMeTrip(null)} style={{ maxWidth: 600 }}>
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>🔎 Near me — {nearMeTrip.today_city || nearMeTrip.title}</h3>
              <button onClick={() => setNearMeTrip(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
            <NearMyHotelPanel
              segmentId={0}
              tripId={nearMeTrip.id}
              hotelDate={localDateKey()}
              todayMode={true}
              authFetch={authFetch}
              onClose={() => setNearMeTrip(null)}
              onAdded={() => { /* trip list refresh not needed here — sheet only writes new segments */ }}
              onOpenAiTab={() => { setNearMeTrip(null); setTab("ai"); }}
            />
          </div>
        </Modal>
      )}
      {showTripSelector && pendingEmailData && (
        <TripSelectorModal
          trips={trips}
          segments={pendingEmailData.segments}
          onClose={() => { setShowTripSelector(false); setPendingEmailData(null); }}
          onSelectExisting={async (tripId) => {
            for (const eid of pendingEmailData.emailIds) {
              await authFetch(`/api/emails/${eid}/assign`, { method: "POST", body: JSON.stringify({ trip_id: tripId }) });
            }
            setShowTripSelector(false);
            setPendingEmailData(null);
            selectTrip(tripId);
            fetchTrips();
          }}
          onCreateNew={async (tripData) => {
            const r = await authFetch("/api/trips", { method: "POST", body: JSON.stringify(tripData) });
            const trip = await r.json();
            for (const eid of pendingEmailData.emailIds) {
              await authFetch(`/api/emails/${eid}/assign`, { method: "POST", body: JSON.stringify({ trip_id: trip.id }) });
            }
            setShowTripSelector(false);
            setPendingEmailData(null);
            selectTrip(trip.id);
            fetchTrips();
          }}
        />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════════════════════
function LoginScreen({ setUser, setTokens, googleClientId, appleConfig }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  // "Forgot password?" modal — the reset-with-token URL is handled at the
  // App level so the modal also appears when the user is already signed in.
  const [showForgot, setShowForgot] = useState(false);

  // Extract invite code from URL (e.g. /invite/abc123)
  const inviteCode = window.location.pathname.match(/\/invite\/([a-f0-9]+)/)?.[1] || "";
  const isInvite = !!inviteCode;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    const url = mode === "login" ? "/api/auth/login" : "/api/auth/register";
    const body = mode === "login" ? { email, password } : { email, password, name, inviteCode };
    try {
      const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) { setError(data.error); return; }
      setTokens(data);
      setUser(data.user);
      // Clear invite URL
      if (isInvite) window.history.replaceState({}, "", "/");
    } catch { setError("Connection failed"); }
  };

  // Google Sign-In — inviteCode / isInvite / setTokens / setUser are
  // captured by the script's callback and don't justify re-injecting the
  // Google SDK on each render.
  useEffect(() => {
    if (!googleClientId) return;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          try {
            const r = await fetch("/api/auth/google", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ credential: response.credential, inviteCode }),
            });
            const data = await r.json();
            if (!r.ok) { setError(data.error); return; }
            setTokens(data);
            setUser(data.user);
            if (isInvite) window.history.replaceState({}, "", "/");
          } catch { setError("Google sign-in failed"); }
        },
      });
      window.google?.accounts.id.renderButton(document.getElementById("google-btn"), {
        theme: "filled_black", size: "large", width: "300", text: "continue_with",
      });
    };
    document.head.appendChild(script);
  }, [googleClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apple Sign-In (web). Apple's JS SDK exposes AppleID.auth.signIn()
  // which returns the same identity-JWT format the iOS SDK produces, so
  // we POST it to the same /api/auth/apple endpoint. Only initialise when
  // appleConfig is set — server returns null appleServiceId until Tim
  // configures APPLE_SERVICE_ID in .env.
  useEffect(() => {
    if (!appleConfig) return;
    const existing = document.getElementById("apple-auth-script");
    const init = () => {
      window.AppleID?.auth.init({
        clientId: appleConfig.serviceId,
        scope: "name email",
        redirectURI: appleConfig.redirectURI,
        usePopup: true,
      });
    };
    if (existing) { init(); return; }
    const script = document.createElement("script");
    script.id = "apple-auth-script";
    script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.onload = init;
    document.head.appendChild(script);
  }, [appleConfig]);

  const signInWithApple = async () => {
    setError("");
    try {
      const data = await window.AppleID.auth.signIn();
      // Apple only returns user.name / user.email on the very first
      // sign-in for a given app+account pair; subsequent calls don't.
      const u = data.user || {};
      const fullName = u.name ? [u.name.firstName, u.name.lastName].filter(Boolean).join(" ") : null;
      const r = await fetch("/api/auth/apple", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identityToken: data.authorization?.id_token,
          name: fullName,
          email: u.email || null,
          inviteCode,
        }),
      });
      const body = await r.json();
      if (!r.ok) { setError(body.error || "Apple sign-in failed"); return; }
      setTokens(body);
      setUser(body.user);
      if (isInvite) window.history.replaceState({}, "", "/");
    } catch (err) {
      // popup_closed_by_user / user_cancelled_authorize are normal cancellations
      const code = err?.error || err?.message || "";
      if (!/cancel|closed/i.test(code)) {
        setError("Apple sign-in failed");
      }
    }
  };

  return (
    <div className="login-screen">
      <style>{STYLE}</style>
      <div className="login-box">
        <h1>Trip<span className="accent">IQ</span></h1>
        <p>AI-powered travel intelligence</p>
        {isInvite && (
          <div style={{ padding: "8px 12px", background: "rgba(0,230,118,0.08)", borderRadius: 8, marginBottom: 12, fontSize: 13, color: "var(--green)", border: "1px solid rgba(0,230,118,0.2)" }}>
            You've been invited to join a trip. Sign in or create an account to continue.
          </div>
        )}
        <div id="google-btn" style={{ display: "flex", justifyContent: "center", marginBottom: 8 }} />
        {appleConfig && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <button type="button" onClick={signInWithApple}
              style={{
                width: 300, height: 40, borderRadius: 6, border: "none",
                background: "#fff", color: "#000", cursor: "pointer",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontSize: 14, fontWeight: 500,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
              <span style={{ fontSize: 18, lineHeight: 1, marginTop: -2 }}></span>
              <span>Sign in with Apple</span>
            </button>
          </div>
        )}
        {(googleClientId || appleConfig) && <div className="login-divider">or</div>}
        <form onSubmit={handleSubmit}>
          {mode === "register" && (
            <div className="form-group">
              <input className="input" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
            </div>
          )}
          <div className="form-group">
            <input className="input" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <input className="input" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          {error && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <button className="btn btn-primary" style={{ width: "100%", marginBottom: 12 }} type="submit">
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
          {mode === "login" && (
            <div style={{ textAlign: "right", marginTop: -6, marginBottom: 8 }}>
              <span onClick={() => setShowForgot(true)}
                style={{ fontSize: 12, color: "var(--text-muted)", cursor: "pointer", textDecoration: "underline dotted" }}>
                Forgot password?
              </span>
            </div>
          )}
        </form>
        {showForgot && <ForgotPasswordModal initialEmail={email} onClose={() => setShowForgot(false)} />}
        {/* The /reset/<token> modal is rendered at the App level so it
            works regardless of whether the user is already signed in. */}
        <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
          {mode === "login" ? (
            isInvite
              ? <>Don't have an account? <span style={{ color: "var(--green)", cursor: "pointer" }} onClick={() => setMode("register")}>Sign up with invite</span></>
              : <span style={{ color: "var(--text-muted)" }}>Sign in to your account</span>
          ) : (
            <>Already have an account? <span style={{ color: "var(--green)", cursor: "pointer" }} onClick={() => setMode("login")}>Sign in</span></>
          )}
        </div>
      </div>
    </div>
  );
}

// Step 1 of the reset flow — user enters email. We always show the same
// "if it's in our system, check your email" message regardless of whether
// the email exists, to avoid leaking which addresses have accounts.
function ForgotPasswordModal({ initialEmail, onClose }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try { await fetch("/api/auth/forgot-password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim() }) }); } catch {}
    setBusy(false); setSubmitted(true);
  };
  return (
    <Modal onClose={onClose}>
      <h2>Reset password</h2>
      {submitted ? (
        <>
          <p style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 16 }}>
            If <strong>{email}</strong> matches an account, a reset link is on its way. It's valid for 1 hour.
          </p>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
            Check your inbox (and spam folder). If nothing arrives within a couple of minutes, double-check the address and try again.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>
            Enter the email on your account. If it's in the system, you'll get a reset link. Works for Google-created accounts too (sets a password you can use as a fallback).
          </p>
          <div className="form-group">
            <input className="input" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>{busy ? "Sending…" : "Send reset link"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// Step 2 — landed via /reset/<token>. User picks a new password.
// When the user is already signed in (currentUserEmail set) we warn them
// that completing this will swap their session over to the account the
// reset link belongs to.
function ResetPasswordModal({ token, onClose, onDone, currentUserEmail }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch("/api/auth/reset-password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.error || "Reset failed"); setBusy(false); return; }
      onDone(data);
    } catch { setErr("Connection failed"); setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h2>Set a new password</h2>
      {currentUserEmail && (
        <div style={{ padding: "8px 12px", background: "rgba(255,171,64,0.08)", borderRadius: 8, marginBottom: 14, fontSize: 12, color: "var(--amber)", border: "1px solid rgba(255,171,64,0.2)" }}>
          You're currently signed in as <strong>{currentUserEmail}</strong>. Completing this reset will sign you out and sign you in as the account this link belongs to. Cancel and refresh if that's not what you want.
        </div>
      )}
      <form onSubmit={submit}>
        <div className="form-group">
          <input className="input" type="password" placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
        </div>
        <div className="form-group">
          <input className="input" type="password" placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
          15+ characters, or 8+ with a number and a lowercase letter.
        </div>
        {err && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !password || !confirm}>{busy ? "Saving…" : "Set password"}</button>
        </div>
      </form>
    </Modal>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// TRIPS VIEW
// ══════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════
// ALERTS DASHBOARD — aggregated travel alerts across all upcoming trips
// (Phase 2 Smart Alerts: hotels + flights + transport + passport validity)
// ══════════════════════════════════════════════════════════════════════════
// Convert VAPID base64 URL-encoded public key into the Uint8Array
// pushManager.subscribe expects.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function enablePushNotifications(authFetch) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" };
  }
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };
  const reg = await navigator.serviceWorker.ready;
  const keyRes = await fetch("/api/push/vapid-public-key");
  const { publicKey } = await keyRes.json();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const r = await authFetch("/api/push/subscribe", { method: "POST", body: JSON.stringify({ subscription: sub }) });
  if (!r || !r.ok) return { ok: false, reason: "subscribe-failed" };
  // Fire a test push so the user sees confirmation immediately
  await authFetch("/api/push/test", { method: "POST" });
  return { ok: true };
}

async function disablePushNotifications(authFetch) {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await authFetch("/api/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: sub.endpoint }) });
    await sub.unsubscribe();
  }
}

function AlertsPanel({ authFetch, onSelectTrip }) {
  const [alerts, setAlerts] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // pushState: "unknown" | "unsupported" | "denied" | "on" | "off"
  const [pushState, setPushState] = useState("unknown");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch("/api/alerts");
        if (!r || !r.ok) { if (!cancelled) setAlerts([]); return; }
        const data = await r.json();
        if (!cancelled) setAlerts(data.alerts || []);
      } catch { if (!cancelled) setAlerts([]); }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Detect current push subscription state on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        if (!cancelled) setPushState("unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        if (!cancelled) setPushState("denied");
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setPushState(sub ? "on" : "off");
      } catch { if (!cancelled) setPushState("off"); }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    const res = await enablePushNotifications(authFetch);
    setPushBusy(false);
    if (res.ok) setPushState("on");
    else if (res.reason === "denied") setPushState("denied");
    else if (res.reason === "unsupported") setPushState("unsupported");
  };
  const handleDisablePush = async () => {
    setPushBusy(true);
    await disablePushNotifications(authFetch);
    setPushBusy(false);
    setPushState("off");
  };

  if (alerts === null) return null; // loading: stay quiet
  if (alerts.length === 0) return null; // nothing actionable

  const reds = alerts.filter(a => a.status === "red");
  const ambers = alerts.filter(a => a.status === "amber");
  const greens = alerts.filter(a => a.status === "green");
  const visible = expanded ? alerts : alerts.slice(0, 5);

  const dot = (s) => ({
    width: 10, height: 10, borderRadius: 5, flexShrink: 0,
    background: s === "red" ? "var(--red)" : s === "amber" ? "var(--amber)" : "var(--green)",
  });

  const iconFor = (segType, category) => {
    if (segType === "passport") return "🛂";
    if (segType === "travel_doc" || category === "travel_doc") return "🪪";
    if (category === "olci") return "🪪";
    if (category === "departure") return "✈";
    if (category === "pickup") return "🚗";
    if (category === "cancellation") return "⌛";
    if (category === "prepayment") return "💳";
    if (category === "checkin") return "🏨";
    if (category === "checkout") return "🚪";
    if (category === "unbooked") return "❓";
    if (category === "park_pass") return "🏞";
    return "•";
  };

  return (
    <div style={{
      background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12,
      padding: 16, marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: panelOpen ? 12 : 0, flexWrap: "wrap", gap: 8 }}>
        <div onClick={() => setPanelOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{panelOpen ? "▾" : "▸"}</span>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>Smart alerts</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {reds.length > 0 && <span style={{ fontSize: 11, color: "var(--red)", fontFamily: "var(--ff-mono)" }}>● {reds.length}</span>}
            {ambers.length > 0 && <span style={{ fontSize: 11, color: "var(--amber)", fontFamily: "var(--ff-mono)" }}>● {ambers.length}</span>}
            {greens.length > 0 && <span style={{ fontSize: 11, color: "var(--green)", fontFamily: "var(--ff-mono)" }}>● {greens.length}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {pushState === "off" && (
            <button onClick={handleEnablePush} disabled={pushBusy} style={{
              background: "var(--green-bg, rgba(0,230,118,0.1))", border: "1px solid var(--green)",
              color: "var(--green)", borderRadius: 6, padding: "4px 10px", fontSize: 11,
              fontFamily: "var(--ff-mono)", cursor: pushBusy ? "wait" : "pointer",
            }}>{pushBusy ? "…" : "🔔 Enable push"}</button>
          )}
          {pushState === "on" && (
            <button onClick={handleDisablePush} disabled={pushBusy} title="Click to disable push notifications" style={{
              background: "none", border: "1px solid var(--border)", color: "var(--text-muted)",
              borderRadius: 6, padding: "4px 10px", fontSize: 11, fontFamily: "var(--ff-mono)",
              cursor: pushBusy ? "wait" : "pointer",
            }}>{pushBusy ? "…" : "🔔 On"}</button>
          )}
          {pushState === "denied" && (
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }} title="Re-enable in browser settings">🔕 blocked</span>
          )}
          {panelOpen && alerts.length > 5 && (
            <button onClick={() => setExpanded(e => !e)} style={{
              background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer",
              fontSize: 12, fontFamily: "var(--ff-mono)",
            }}>{expanded ? "Show less" : `+${alerts.length - 5} more`}</button>
          )}
        </div>
      </div>
      {panelOpen && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visible.map((a, i) => (
          <div key={`${a.segment_type}-${a.segment_id || a.passport_index}-${i}`}
            onClick={() => a.trip_id && onSelectTrip(a.trip_id)}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
              background: "var(--sidebar)", borderRadius: 8,
              cursor: a.trip_id ? "pointer" : "default",
              borderLeft: `3px solid ${a.status === "red" ? "var(--red)" : a.status === "amber" ? "var(--amber)" : "var(--green)"}`,
            }}>
            <span style={dot(a.status)} />
            <span style={{ fontSize: 13, flexShrink: 0 }}>{iconFor(a.segment_type, a.category)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "#e8e6f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.title}{a.trip_title && a.segment_type !== "passport" ? <span style={{ color: "var(--text-muted)", marginLeft: 6, fontSize: 11 }}>· {a.trip_title}</span> : null}
              </div>
              <div style={{
                fontSize: 12, lineHeight: 1.45,
                color: a.status === "red" ? "var(--red)" : a.status === "amber" ? "var(--amber)" : "var(--text-muted)",
                marginTop: 2, whiteSpace: "normal", wordBreak: "break-word",
              }}>{a.message}</div>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}

/// ISO 3166-1 alpha-2 → flag emoji via the regional-indicator-symbol
/// trick (U+1F1E6 + offset). Single shared helper so the HeroTripCard
/// and country pills stay in sync.
function flagFromCode(code) {
  if (!code) return "";
  const cc = code.toUpperCase();
  if (cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(0x1F1E6 + cc.charCodeAt(0) - 65, 0x1F1E6 + cc.charCodeAt(1) - 65);
}

// Display name for a country code. Uses the platform Intl.DisplayNames
// in browsers that support it (every modern one); falls back to the code.
const COUNTRY_NAME_FALLBACKS = {
  GB: "UK", US: "USA", AE: "UAE", KR: "South Korea",
};
function countryName(code) {
  if (!code) return "";
  try {
    const n = new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase());
    if (n && n.toUpperCase() !== code.toUpperCase()) return n;
  } catch {}
  return COUNTRY_NAME_FALLBACKS[code.toUpperCase()] || code.toUpperCase();
}

function tripInProgress(trip) {
  if (!trip?.start_date || !trip?.end_date) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const s = new Date(trip.start_date); s.setHours(0, 0, 0, 0);
  const e = new Date(trip.end_date); e.setHours(0, 0, 0, 0);
  return today >= s && today <= e;
}

/// Hero card for the trip currently in progress. Owns the top of the
/// My Trips landing screen — pulls in today's weather (lazy fetch) plus
/// today's segments + day-of-trip progress, so the user lands on
/// "what's happening NOW" not "browse the list".
function HeroTripCard({ trip, onOpen, onOpenCalendar, onOpenNearMe, authFetch }) {
  const [weather, setWeather] = React.useState(null);
  const todayIso = localDateKey();
  const cityForWeather = trip.today_city || (trip.destination ? trip.destination.split(",")[0].trim() : null);

  React.useEffect(() => {
    if (!cityForWeather || !authFetch) return;
    let cancelled = false;
    authFetch(`/api/weather?location=${encodeURIComponent(cityForWeather)}&date=${todayIso}`)
      .then(r => r && r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d && !d.error) setWeather(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cityForWeather, todayIso, authFetch]);

  // Always render exactly ONE flag on the hero. Without this clamp, the
  // fallback used trip.flag_emoji which is every visited country's flag
  // concatenated (e.g. 🇬🇧🇭🇺🇪🇸🇨🇦🇬🇱🇮🇸🇩🇰🇦🇺 — ~400px wide).
  // On a phone viewport that pushed the title column to width 0, and
  // `minWidth: 0` let it shrink so the title wrapped at every character
  // — rendering as a vertical column of single letters on the right.
  const heroFlag = (() => {
    if (trip.today_country_code) return flagFromCode(trip.today_country_code);
    const match = (trip.flag_emoji || "").match(/[\u{1F1E6}-\u{1F1FF}]{2}/u);
    return match ? match[0] : "";
  })();

  const dayInfo = (() => {
    if (!trip.start_date || !trip.end_date) return null;
    const today = new Date(todayIso + "T00:00:00Z");
    const s = new Date(trip.start_date + "T00:00:00Z");
    const e = new Date(trip.end_date + "T00:00:00Z");
    const total = Math.round((e - s) / 86400000) + 1;
    const dayNum = Math.round((today - s) / 86400000) + 1;
    const pct = Math.max(0, Math.min(100, (dayNum / total) * 100));
    return { dayNum: Math.max(1, Math.min(dayNum, total)), total, pct };
  })();

  const segIcon = (t) => ({
    flight: "✈️", hotel: "🏨", friends_house: "🏡", train: "🚆",
    transport: "🚗", car_rental: "🚗", bike_hire: "🚲",
    ferry: "⛴", event: "🎭", excursion: "🎟", restaurant: "🍽",
    cycle_ride: "🚴", walk: "🚶", gym: "💪", run: "🏃",
  }[t] || "📌");
  const segColor = (t) => ({
    flight: "var(--flight-color)", hotel: "var(--hotel-color)", friends_house: "var(--hotel-color)",
    excursion: "var(--excursion-color)", transport: "var(--purple)", train: "var(--purple)",
    car_rental: "var(--purple)", ferry: "var(--blue)", event: "var(--amber)",
    bike_hire: "var(--green)", cycle_ride: "var(--green)", walk: "var(--green)",
    gym: "var(--green)", run: "var(--green)",
  }[t] || "var(--text-muted)");

  const fmtTime = (iso) => iso ? iso.split("T")[1]?.slice(0, 5) : "";
  const todays = trip.today_segments || [];

  return (
    <div
      onClick={() => onOpen(trip.id)}
      style={{
        padding: 24, background: "linear-gradient(160deg, #2a2447, #1f1b38)",
        border: "1px solid rgba(0,230,118,0.35)", borderRadius: 18,
        boxShadow: "0 12px 40px rgba(0,230,118,0.08)",
        marginBottom: 16, cursor: "pointer",
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
        {heroFlag && <div style={{ fontSize: 38, lineHeight: 1, flexShrink: 0,
          maxWidth: 56, overflow: "hidden", whiteSpace: "nowrap" }}>{heroFlag}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)",
              boxShadow: "0 0 6px rgba(0,230,118,0.7)" }} />
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: 1.2,
              textTransform: "uppercase", color: "var(--green)" }}>Trip in progress</span>
          </div>
          <h2 style={{ fontFamily: "Outfit, sans-serif", fontSize: 22, fontWeight: 600, margin: 0,
            letterSpacing: -0.3, lineHeight: 1.2, color: "var(--text)" }}>{trip.title}</h2>
        </div>
      </div>

      {(trip.today_city || weather) && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 10, fontSize: 14, color: "var(--text-dim)" }}>
          {trip.today_city && (
            <span>📍 You're in <strong style={{ color: "var(--text)", fontWeight: 500 }}>{trip.today_city}</strong></span>
          )}
          {weather && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", background: "rgba(255,255,255,0.06)", borderRadius: 100,
              fontFamily: "DM Mono, monospace", fontSize: 12, color: "var(--text)",
            }}>{weather.icon} {weather.high}° / {weather.low}°</span>
          )}
          {/* Today-mode Near-my-hotel — only when we know today_city.
              Mirrors the iOS in-progress hero button (PR #159). */}
          {trip.today_city && onOpenNearMe && (
            <button onClick={(e) => { e.stopPropagation(); onOpenNearMe(trip); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "3px 12px", background: "transparent",
                border: "1px solid rgba(68,138,255,0.5)", borderRadius: 100,
                fontSize: 12, color: "var(--blue)", cursor: "pointer",
              }}>
              🔎 What's near me?
            </button>
          )}
        </div>
      )}

      {dayInfo && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            fontFamily: "DM Mono, monospace", fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
            <span><span style={{ color: "var(--green)" }}>Day {dayInfo.dayNum}</span> · started {trip.start_date}</span>
            <span>{dayInfo.total} days total</span>
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${dayInfo.pct}%`, height: "100%", background: "var(--green)",
              boxShadow: "0 0 8px rgba(0,230,118,0.5)", borderRadius: 2 }} />
          </div>
        </div>
      )}

      {todays.length > 0 && (
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-muted)" }}>Today</span>
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "var(--text-dim)" }}>
              {todays.length} {todays.length === 1 ? "booking" : "bookings"}
            </span>
          </div>
          {todays.slice(0, 3).map((seg, i) => (
            <div key={seg.id} style={{ display: "flex", gap: 12, padding: "8px 0",
              borderTop: i > 0 ? "1px solid rgba(255,255,255,0.04)" : "none",
              marginTop: i > 0 ? 4 : 0 }}>
              <div style={{ width: 3, borderRadius: 2, background: segColor(seg.type), flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "var(--text-dim)", marginBottom: 2 }}>
                  {fmtTime(seg.start_datetime)}{seg.end_datetime ? ` – ${fmtTime(seg.end_datetime)}` : ""}
                </div>
                <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {segIcon(seg.type)} {seg.title}
                </div>
                {seg.start_location && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {seg.start_location}
                  </div>
                )}
              </div>
            </div>
          ))}
          {todays.length > 3 && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8, textAlign: "right" }}>
              +{todays.length - 3} more
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 20, display: "flex", gap: 8, alignItems: "stretch" }}>
        <button onClick={(e) => { e.stopPropagation(); onOpen(trip.id, { today: true }); }}
          style={{ flex: 1, padding: "12px 16px", background: "var(--green)", color: "#0a0a14",
            border: "none", borderRadius: 12, fontFamily: "DM Sans, sans-serif",
            fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Open today  →</button>
        {onOpenCalendar && (
          <button onClick={(e) => { e.stopPropagation(); onOpenCalendar(trip.id); }}
            title="Open calendar"
            aria-label="Open calendar"
            className="cal-btn"
            style={{ width: 60, padding: 0, background: "rgba(0,230,118,0.12)",
              color: "var(--green)", border: "1px solid rgba(0,230,118,0.3)",
              borderRadius: 12, fontSize: 28, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
            📅
          </button>
        )}
      </div>
    </div>
  );
}

/// Pill row of every country on this trip, with the country the user is
/// currently in highlighted in green. Falls back gracefully when the
/// trip's `countries` array is empty.
function CountriesStrip({ trip }) {
  const codes = (() => {
    try { return JSON.parse(trip.countries || "[]"); } catch { return []; }
  })();
  if (codes.length === 0) return null;
  const current = trip.today_country_code;
  return (
    <div style={{ padding: "14px 16px", background: "var(--card)",
      border: "1px solid var(--border)", borderRadius: 12, marginBottom: 20 }}>
      <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: 1,
        textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 10 }}>
        Countries this trip · {codes.length}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {codes.map(code => {
          const isCurrent = current && code.toUpperCase() === current.toUpperCase();
          return (
            <span key={code} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 10px",
              background: isCurrent ? "rgba(0,230,118,0.12)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${isCurrent ? "rgba(0,230,118,0.35)" : "rgba(255,255,255,0.05)"}`,
              borderRadius: 100, fontSize: 12,
              color: isCurrent ? "var(--text)" : "var(--text-dim)",
            }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>{flagFromCode(code)}</span>
              {countryName(code)}
              {isCurrent && <span style={{ color: "var(--green)", fontSize: 9, marginLeft: 2 }}>●</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function TripsView({ trips, onSelect, onSelectAt, onOpenCalendar, onOpenNearMe, onCreate, authFetch, onRefresh, onEditTrip, user }) {
  const [duplicating, setDuplicating] = useState(null); // { trip } or null
  const [showPast, setShowPast] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // id of the trip whose card the user just tapped — toggled on for ~600ms
  // so the dim/scale-down feedback persists past finger-release even if the
  // route change takes a moment to land. CSS :active alone disappears the
  // instant the user lifts their finger; Tim wants the cue to last through
  // the network round-trip to /api/trips/:id.
  const [pressingId, setPressingId] = useState(null);
  const flashPress = (id) => {
    setPressingId(id);
    setTimeout(() => setPressingId(curr => (curr === id ? null : curr)), 600);
  };
  // Same idea but per route-leg: keyed by `${tripId}|${city}` so the
  // highlight persists past tap-release until the trip detail loads and
  // scrolls to that city's day. Otherwise the screen "jumps" to a new
  // day with no visible trace of which pill was tapped.
  const [pressingLegKey, setPressingLegKey] = useState(null);
  const flashLegPress = (key) => {
    setPressingLegKey(key);
    setTimeout(() => setPressingLegKey(curr => (curr === key ? null : curr)), 600);
  };
  // Per-trip "show all booking alerts" toggle. By default we hide green
  // (done) rows and only show red + amber so the user sees what's still
  // outstanding at a glance.
  const [showAllAlerts, setShowAllAlerts] = useState({}); // { tripId: bool }
  const goTo = (tripId, target) => (onSelectAt ? onSelectAt(tripId, target) : onSelect(tripId));

  // Pre-fetch endpoints the SW caches so the trip is fully usable offline.
  // Hits /api/trips/:id (timeline + countries) plus per-country reference
  // cards, alerts, profile and passport. Each authFetch goes through the
  // service worker, which writes the response to the api-v2 cache.
  const [offlineState, setOfflineState] = useState({}); // { [tripId]: "saving" | "done" | "error" }
  const saveOffline = async (tripId) => {
    setOfflineState(s => ({ ...s, [tripId]: "saving" }));
    try {
      const [tripR] = await Promise.all([
        authFetch(`/api/trips/${tripId}`),
        authFetch(`/api/trips/${tripId}/timeline`),
        authFetch(`/api/alerts`),
        authFetch(`/api/profile`),
        authFetch(`/api/passport`),
      ]);
      // Country reference: depends on the trip detail's countries[] field.
      try {
        if (tripR && tripR.ok) {
          const td = await tripR.json();
          const codes = (() => { try { return JSON.parse(td.countries || "[]"); } catch { return []; } })();
          await Promise.all(codes.map(c => authFetch(`/api/country/${c}`).catch(() => null)));
        }
      } catch {}
      setOfflineState(s => ({ ...s, [tripId]: "done" }));
      // Reset the badge to its idle state after a few seconds.
      setTimeout(() => setOfflineState(s => ({ ...s, [tripId]: null })), 4000);
    } catch {
      setOfflineState(s => ({ ...s, [tripId]: "error" }));
      setTimeout(() => setOfflineState(s => ({ ...s, [tripId]: null })), 4000);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Archived trips are pulled out of the upcoming/past split entirely —
  // the user actively flagged them as done-and-dusted, so they don't
  // belong in either headline section. They get their own collapsible
  // bucket at the bottom of the list and can be unarchived any time.
  const activeTrips = trips.filter(trip => !trip.archived_at);

  const upcomingTrips = activeTrips.filter(trip => {
    if (!trip.end_date) return true;
    const end = new Date(trip.end_date);
    end.setHours(0, 0, 0, 0);
    return end >= today;
  });

  const pastTrips = activeTrips.filter(trip => {
    if (!trip.end_date) return false;
    const end = new Date(trip.end_date);
    end.setHours(0, 0, 0, 0);
    return end < today;
  });

  const archivedTrips = trips.filter(trip => !!trip.archived_at);

  const handleDelete = async (e, tripId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this trip? This cannot be undone.")) return;
    try {
      await authFetch(`/api/trips/${tripId}`, { method: "DELETE" });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to delete trip:", err);
    }
  };

  const handleArchiveToggle = async (e, trip) => {
    e.stopPropagation();
    try {
      await authFetch(`/api/trips/${trip.id}`, {
        method: "PUT",
        body: JSON.stringify({ archived: !trip.archived_at }),
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to toggle archive:", err);
    }
  };

  const renderTripCard = (trip) => {
    const days = daysUntil(trip.start_date);
    const routeCities = trip.route_cities ? (() => { try { return JSON.parse(trip.route_cities); } catch { return []; } })() : [];
    const legs = routeCities.map(c => ({ label: c }));

    const nightCount = trip.start_date && trip.end_date
      ? Math.ceil((new Date(trip.end_date) - new Date(trip.start_date)) / 86400000) : 0;

    return (
      <div key={trip.id} className={`card trip-card${pressingId === trip.id ? " pressing" : ""}`}
        onClick={() => { flashPress(trip.id); goTo(trip.id, { today: true }); }}
        style={{ marginBottom: 16, padding: 0, overflow: "hidden", cursor: "pointer" }}>

        {/* Hero header with flag */}
        <div style={{ padding: "24px 28px 16px", display: "flex", gap: 24, alignItems: "flex-start", flexDirection: "column" }}>
          {/* Left side */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title row */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              {trip.flag_emoji && <span style={{ fontSize: 32 }}>{trip.flag_emoji}</span>}
              <div>
                <h2 style={{ fontSize: 24, margin: 0, lineHeight: 1.2, fontFamily: "Outfit" }}>{trip.title}</h2>
                <div style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 3 }}>
                  {trip.start_date ? formatDate(trip.start_date) : "TBD"} — {trip.end_date ? formatDate(trip.end_date) : "TBD"}
                  {nightCount > 0 && <span style={{ marginLeft: 8 }}>· {nightCount} nights</span>}
                </div>
              </div>
            </div>

            {/* Route legs — compact pills, click to jump to first segment in that city */}
            {legs.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 0, margin: "14px 0", flexWrap: "wrap", rowGap: 6 }}>
              {legs.map((leg, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <div style={{ margin: "0 4px", color: "var(--flight-color)", fontSize: 11 }}>→</div>
                  )}
                  <div
                    className={`route-leg${pressingLegKey === `${trip.id}|${leg.label}` ? " pressing" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      flashLegPress(`${trip.id}|${leg.label}`);
                      goTo(trip.id, { city: leg.label });
                    }}
                    title={`Jump to ${leg.label} in the timeline`}
                    style={{
                      padding: "3px 8px", borderRadius: 5, fontSize: 11, fontWeight: 500,
                      background: (i === 0 || i === legs.length - 1) ? "rgba(0,230,118,0.1)" : "rgba(68,138,255,0.1)",
                      color: (i === 0 || i === legs.length - 1) ? "var(--green)" : "var(--flight-color)",
                      whiteSpace: "nowrap", cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 2,
                    }}>
                    {leg.label}
                  </div>
                </React.Fragment>
              ))}
            </div>
            )}

            {/* Stats */}
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
              <span>{trip.segmentCount || 0} items</span>
              {trip.primary_airlines && <span>{(() => { try { return JSON.parse(trip.primary_airlines).join(", "); } catch { return trip.primary_airlines; } })()}</span>}
            </div>

            {/* Countdown */}
            {days !== null && days > 0 && (
              <div style={{ display: "inline-flex", alignItems: "baseline", gap: 8, padding: "10px 18px", background: "rgba(0,230,118,0.06)", borderRadius: 10, border: "1px solid rgba(0,230,118,0.15)" }}>
                <span className="countdown" style={{ fontSize: 36 }}>{days}</span>
                <span style={{ fontSize: 14, color: "var(--green)", fontWeight: 500 }}>days to go</span>
              </div>
            )}
            {days !== null && days <= 0 && days >= -(daysUntil(trip.end_date) || 0) && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                fontSize: 14, fontWeight: 600, padding: "8px 18px", borderRadius: 999,
                background: "var(--green)", color: "#0a0a0a",
                boxShadow: "0 0 0 4px rgba(0,230,118,0.18)", cursor: "pointer",
              }}>
                <span style={{ fontSize: 14 }}>📍</span>
                Take me to today
                <span style={{ fontSize: 13 }}>→</span>
              </span>
            )}

            {/* Trip summary */}
            {trip.summary && <div style={{ marginTop: 14 }}><Truncated text={trip.summary} lines={3} style={{ fontSize: 13, color: "#ccc8dd", lineHeight: 1.6 }} /></div>}
          </div>

          {/* Booking status — outstanding only by default, toggle to see everything */}
          {trip.booking_alerts && trip.booking_alerts.length > 0 && (() => {
            const showAll = !!showAllAlerts[trip.id];
            const sorted = trip.booking_alerts.slice().sort((a, b) => {
              const order = { red: 0, amber: 1, green: 2 };
              if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
              return (a.sort_key || 0) - (b.sort_key || 0);
            });
            const outstanding = sorted.filter(a => a.status !== "green");
            const doneCount = sorted.length - outstanding.length;
            const visible = showAll ? sorted : outstanding;
            return (
              <div style={{ width: "100%", maxWidth: 520, margin: "0 auto", padding: "10px 12px", background: "var(--sidebar)", borderRadius: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)" }}>
                    Booking status{!showAll && outstanding.length > 0 && doneCount > 0 && ` · ${outstanding.length} outstanding`}
                  </div>
                  {doneCount > 0 && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setShowAllAlerts(prev => ({ ...prev, [trip.id]: !showAll })); }}
                      style={{ fontSize: "var(--text-xs)", color: "var(--green)", cursor: "pointer", userSelect: "none" }}>
                      {showAll ? "Hide done" : `See all (+${doneCount} done)`}
                    </span>
                  )}
                </div>
                {visible.length === 0 ? (
                  <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", fontStyle: "italic" }}>
                    All set — nothing outstanding.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {visible.map((alert, idx) => (
                      <div key={`${alert.segment_id}-${alert.category}-${idx}`}
                        onClick={(e) => { e.stopPropagation(); goTo(trip.id, alert.segment_id ? { segId: alert.segment_id } : { firstDay: true }); }}
                        title="Open in timeline"
                        style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "var(--text-sm)", cursor: "pointer", padding: "4px 6px", margin: "-4px -6px", borderRadius: 6 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: 5, flexShrink: 0, marginTop: 5,
                          background: alert.status === "red" ? "var(--red)" : alert.status === "amber" ? "var(--amber)" : "var(--green)",
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: "#e8e6f0", fontSize: "var(--text-sm)" }}>{alert.title}</div>
                          <div style={{
                            color: alert.status === "red" ? "var(--red)" : alert.status === "amber" ? "var(--amber)" : "var(--text-muted)",
                            fontSize: "var(--text-xs)", marginTop: 2, lineHeight: 1.4, wordBreak: "break-word",
                          }}>{alert.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Bottom bar with quick info */}
        <div style={{ padding: "10px 28px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.01)" }}>
          <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-muted)" }}>
            <span>{trip.flag_emoji || ""} {trip.primary_airlines ? (() => { try { return JSON.parse(trip.primary_airlines).join(", "); } catch { return trip.primary_airlines; } })() : ""}</span>
            <span>{routeCities.join(" · ")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {trip.isOwner && (
              <span
                onClick={(e) => { e.stopPropagation(); onEditTrip(trip); }}
                style={{ fontSize: 11, color: "var(--text-dim)", cursor: "pointer", opacity: 0.7, transition: "opacity 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
              >Edit</span>
            )}
            <span
              onClick={(e) => { e.stopPropagation(); setDuplicating({ trip }); }}
              title="Make a copy of this trip with new dates"
              style={{ fontSize: 11, color: "var(--text-dim)", cursor: "pointer", opacity: 0.7, transition: "opacity 0.15s" }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
              onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
            >Duplicate</span>
            {trip.isOwner && (
              <span
                onClick={(e) => handleArchiveToggle(e, trip)}
                title={trip.archived_at ? "Move this trip back into your active list" : "Hide this trip from your active list and the inbox picker"}
                style={{ fontSize: 11, color: "var(--text-dim)", cursor: "pointer", opacity: 0.7, transition: "opacity 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
              >{trip.archived_at ? "Unarchive" : "Archive"}</span>
            )}
            {trip.isOwner && (
              <span
                onClick={(e) => handleDelete(e, trip.id)}
                style={{ fontSize: 11, color: "var(--red)", cursor: "pointer", opacity: 0.7, transition: "opacity 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
              >Delete</span>
            )}
            {(() => {
              const st = offlineState[trip.id];
              const label = st === "saving" ? "Saving…" : st === "done" ? "✓ Saved" : st === "error" ? "✕ Failed" : "☁ Save offline";
              const color = st === "done" ? "var(--green)" : st === "error" ? "var(--red)" : "var(--text-dim)";
              return (
                <span
                  onClick={(e) => { e.stopPropagation(); if (!st) saveOffline(trip.id); }}
                  title="Pre-fetch this trip's data so it's readable on a plane / with no signal"
                  style={{ fontSize: 11, color, cursor: st === "saving" ? "wait" : "pointer", opacity: 0.85 }}>
                  {label}
                </span>
              );
            })()}
            <span style={{ fontSize: 12, color: "var(--green)" }}>View timeline →</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="section-header">
        <h2>My Trips</h2>
        <button className="btn btn-primary" onClick={onCreate}>+ New Trip</button>
      </div>
      {trips.length > 0 && <AlertsPanel authFetch={authFetch} onSelectTrip={onSelect} />}
      {(() => {
        const inProgress = upcomingTrips.find(tripInProgress);
        if (!inProgress) return null;
        const heading = (() => {
          const h = new Date().getHours();
          const part = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
          const name = user?.name?.split(" ")?.[0];
          return name ? `${part}, ${name}` : part;
        })();
        const today = new Date().toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
        return (
          <>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "8px 0 12px" }}>
              <h2 style={{ fontFamily: "Outfit, sans-serif", fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: -0.3, color: "var(--text)" }}>{heading}</h2>
              <span style={{ fontFamily: "DM Mono, monospace", fontSize: 12, color: "var(--text-dim)" }}>{today}</span>
            </div>
            <HeroTripCard trip={inProgress} onOpen={goTo} onOpenCalendar={onOpenCalendar} onOpenNearMe={onOpenNearMe} authFetch={authFetch} />
            <CountriesStrip trip={inProgress} />
          </>
        );
      })()}
      {trips.length === 0 ? (
        <div className="empty-state">
          <h2>No trips yet</h2>
          <p>Create a trip or forward a booking email to get started.</p>
        </div>
      ) : (
        <div>
          {/* Other upcoming trips — the in-progress one is shown above
              as the hero. Filter it out so it's not duplicated below. */}
          {upcomingTrips.filter(t => !tripInProgress(t)).length > 0 && (
            <div>
              {upcomingTrips.filter(t => !tripInProgress(t)).map(renderTripCard)}
            </div>
          )}
          {upcomingTrips.length === 0 && pastTrips.length > 0 && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-dim)", fontSize: 14 }}>
              No upcoming trips. Create one or check your past trips below.
            </div>
          )}

          {/* Past trips — collapsible */}
          {pastTrips.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div
                onClick={() => setShowPast(!showPast)}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: showPast ? 16 : 0, padding: "8px 0" }}
              >
                <span style={{ fontSize: 14, color: "var(--text-muted)", transition: "transform 0.2s", transform: showPast ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▶</span>
                <h3 style={{ fontSize: 16, color: "var(--text-dim)", fontFamily: "Outfit", fontWeight: 500, margin: 0 }}>
                  Past Trips ({pastTrips.length})
                </h3>
              </div>
              {showPast && (
                <div style={{ opacity: 0.8 }}>
                  {pastTrips.map(renderTripCard)}
                </div>
              )}
            </div>
          )}

          {/* Archived trips — collapsible, dimmer than past so they're easy to find but easy to skip */}
          {archivedTrips.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div
                onClick={() => setShowArchived(!showArchived)}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: showArchived ? 16 : 0, padding: "8px 0" }}
              >
                <span style={{ fontSize: 14, color: "var(--text-muted)", transition: "transform 0.2s", transform: showArchived ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▶</span>
                <h3 style={{ fontSize: 16, color: "var(--text-dim)", fontFamily: "Outfit", fontWeight: 500, margin: 0 }}>
                  Archived ({archivedTrips.length})
                </h3>
              </div>
              {showArchived && (
                <div style={{ opacity: 0.65 }}>
                  {archivedTrips.map(renderTripCard)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {duplicating && (
        <DuplicateTripModal trip={duplicating.trip} authFetch={authFetch}
          onClose={() => setDuplicating(null)}
          onDuplicated={(newTrip) => { setDuplicating(null); onRefresh(); onSelect(newTrip.id); }} />
      )}
    </div>
  );
}

function DuplicateTripModal({ trip, authFetch, onClose, onDuplicated }) {
  const [title, setTitle] = useState(`Copy of ${trip.title}`);
  const [startDate, setStartDate] = useState(trip.start_date || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!startDate) { setErr("New start date is required."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await authFetch(`/api/trips/${trip.id}/duplicate`, {
        method: "POST",
        body: JSON.stringify({ title: title || undefined, start_date: startDate }),
      });
      if (!r || !r.ok) {
        const j = r ? await r.json().catch(() => ({})) : {};
        setErr(j.error || "Duplicate failed");
        setBusy(false);
        return;
      }
      const newTrip = await r.json();
      onDuplicated(newTrip);
    } catch (e) { setErr(e.message || "Duplicate failed"); setBusy(false); }
  };

  // Best-effort preview of what the shifted end date will be — purely for UI
  // reassurance; the server computes the authoritative value.
  const previewEnd = (() => {
    if (!trip.start_date || !trip.end_date || !startDate) return null;
    const a = new Date(trip.start_date + "T00:00:00Z").getTime();
    const b = new Date(trip.end_date + "T00:00:00Z").getTime();
    const n = new Date(startDate + "T00:00:00Z").getTime();
    if ([a, b, n].some(Number.isNaN)) return null;
    const shifted = new Date(n + (b - a));
    return shifted.toISOString().slice(0, 10);
  })();

  return (
    <Modal onClose={onClose}>
      <h2>Duplicate trip</h2>
      <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>
        Clones the whole itinerary — every segment is copied with its dates and times shifted by the difference between the original start date and the one you pick below.
      </p>
      <div className="form-group">
        <label>New title</label>
        <input className="input" value={title} onChange={e => setTitle(e.target.value)} />
      </div>
      <div className="form-group">
        <label>New start date</label>
        <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        {previewEnd && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            End date will shift to {previewEnd}.
          </div>
        )}
      </div>
      {err && <div style={{ fontSize: 13, color: "var(--red)", marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy || !startDate}>
          {busy ? "Duplicating…" : "Duplicate"}
        </button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MODAL — overlay + sheet with scroll-lock and close-X
// ══════════════════════════════════════════════════════════════════════════
function Modal({ onClose, children, style }) {
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={style} onClick={e => e.stopPropagation()}>
        {onClose && <button className="modal-close" onClick={onClose} aria-label="Close">×</button>}
        {children}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TRUNCATED — multi-line clamp with Show more / Show less
// ══════════════════════════════════════════════════════════════════════════
function Truncated({ text, lines = 3, style }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || expanded) return;
    setOverflowing(ref.current.scrollHeight > ref.current.clientHeight + 1);
  }, [text, lines, expanded]);
  if (!text) return null;
  const clampStyle = expanded ? {} : {
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };
  return (
    <div>
      <div ref={ref} style={{ ...style, ...clampStyle }}>{text}</div>
      {(overflowing || expanded) && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          style={{ background: "none", border: "none", color: "var(--green)", fontSize: 12, cursor: "pointer", padding: "4px 0 0", fontWeight: 500 }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SENSITIVE — masks a value until autoReveal is true or user clicks Reveal
// ══════════════════════════════════════════════════════════════════════════
function Sensitive({ value, autoReveal = false, mono = false, mask = "••••" }) {
  const [revealed, setRevealed] = useState(autoReveal);
  if (!value) return null;
  if (revealed) {
    return mono ? <span className="mono">{value}</span> : <span>{value}</span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span className={mono ? "mono" : ""} style={{ opacity: 0.5, letterSpacing: 1 }}>{mask}</span>
      <button
        onClick={(e) => { e.stopPropagation(); setRevealed(true); }}
        style={{ background: "none", border: "none", color: "var(--green)", cursor: "pointer", fontSize: "var(--text-xs)", padding: 0 }}
      >
        Reveal
      </button>
    </span>
  );
}

// True if today falls in [start - 1 day, end]. Used to auto-show access codes around the stay.
function isStayWindowActive(startDatetime, endDatetime) {
  if (!startDatetime) return false;
  const now = new Date();
  const start = new Date(startDatetime); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - 1);
  const end = endDatetime ? new Date(endDatetime) : new Date(start);
  end.setHours(23, 59, 59, 999);
  return now >= start && now <= end;
}

// ══════════════════════════════════════════════════════════════════════════
// TIMELINE VIEW
// ══════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════
// EVENTS PANEL — Feature 4
// Lists event-type segments with both venue-local "official" times and the
// time the user will actually experience locally (resolved server-side from
// the user's location on that date).
// ══════════════════════════════════════════════════════════════════════════

function EventsPanel({ tripId, authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`/api/trips/${tripId}/events`);
        if (!r || !r.ok) { if (!cancelled) { setData(null); setLoading(false); } return; }
        const j = await r.json();
        if (!cancelled) { setData(j); setLoading(false); }
      } catch { if (!cancelled) { setData(null); setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [tripId, authFetch]);

  if (loading) return null;
  if (!data || !data.events || data.events.length === 0) return null;

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>🎭 Events</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>{data.events.length} scheduled</span>
        </div>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {data.events.map(ev => {
            const c = ev.conversion;
            const sameClock = c && c.same_clock;
            const diffDay = c && c.different_day;
            return (
              <div key={ev.segment_id} style={{
                background: "var(--sidebar)", borderRadius: 8, padding: "10px 12px", marginBottom: 8,
                borderLeft: "3px solid var(--amber)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{ev.title}</div>
                  {ev.provider && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{ev.provider}</div>}
                </div>
                {ev.venue && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{ev.venue}</div>}
                {!c && (
                  <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 6 }}>
                    {ev.official_datetime ? new Date(ev.official_datetime).toLocaleString("en-AU") : "—"}
                    {!ev.venue_tz && <span style={{ color: "var(--amber)", marginLeft: 8 }}>· no venue timezone</span>}
                  </div>
                )}
                {c && (
                  <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
                    <div style={{ color: "#fff" }}>
                      <span style={{ color: "var(--text-muted)", fontFamily: "var(--ff-mono)", fontSize: 10, letterSpacing: "0.06em" }}>OFFICIAL</span>{" "}
                      {c.venue_display}
                    </div>
                    {c.user_display && (
                      <div style={{ color: sameClock ? "var(--text-dim)" : "var(--green)", marginTop: 2 }}>
                        <span style={{ color: "var(--text-muted)", fontFamily: "var(--ff-mono)", fontSize: 10, letterSpacing: "0.06em" }}>YOUR TIME</span>{" "}
                        {c.user_display}
                        {sameClock && <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>(same wall-clock)</span>}
                        {diffDay && <span style={{ marginLeft: 6, color: "var(--amber)" }}>⚠ different day</span>}
                      </div>
                    )}
                    {!c.user_display && (
                      <div style={{ color: "var(--text-muted)", marginTop: 2, fontSize: 11 }}>
                        Your local time will be shown once a hotel covering this date is added to the trip.
                      </div>
                    )}
                  </div>
                )}
                {ev.confirmation_code && (
                  <div style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--ff-mono)", marginTop: 6 }}>Ref: {ev.confirmation_code}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ITINERARY PANEL — Feature 3
// Stop-by-stop summary with driving distance + time between consecutive stops
// and a running totals header. Confirmed/unconfirmed colour coding.
// ══════════════════════════════════════════════════════════════════════════
function ItineraryPanel({ tripId, authFetch, canEdit, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`/api/trips/${tripId}/itinerary`);
        if (!r || !r.ok) { if (!cancelled) { setData(null); setLoading(false); } return; }
        const j = await r.json();
        if (!cancelled) { setData(j); setLoading(false); }
      } catch { if (!cancelled) { setData(null); setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [tripId, authFetch, refreshTick]);

  const deleteSegment = async (segmentId, label) => {
    if (!segmentId || !canEdit) return;
    if (!window.confirm(`Delete "${label}"? This removes the underlying segment from the trip.`)) return;
    try {
      await authFetch(`/api/segments/${segmentId}`, { method: "DELETE" });
      setRefreshTick(t => t + 1);
      if (onRefresh) onRefresh();
    } catch {}
  };

  if (loading) return null;
  if (!data || !data.stops || data.stops.length === 0) return null;

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : "—";
  const fmtDur = (mins) => {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };
  const transportIcon = (mode) => {
    const m = (mode || "").toLowerCase();
    if (m.includes("train")) return "🚆";
    if (m.includes("bus")) return "🚌";
    if (m.includes("ferry")) return "⛴";
    if (m.includes("plane") || m.includes("flight")) return "✈";
    if (m.includes("taxi")) return "🚕";
    return "🚗";
  };

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>🗺 Itinerary</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>
            {data.totals.stops} stops · {data.totals.nights} nights
            {data.totals.distance_km > 0 && <> · 🚗 {data.totals.distance_km}km · {fmtDur(data.totals.duration_minutes)}</>}
            {data.totals.flights > 0 && <> · ✈ {data.totals.flights} flight{data.totals.flights === 1 ? "" : "s"}</>}
            {data.totals.unconfirmed_count > 0 && <span style={{ color: "var(--amber)", marginLeft: 8 }}>· {data.totals.unconfirmed_count} unconfirmed</span>}
          </span>
        </div>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {data.stops.map((stop, i) => (
            <div key={stop.segment_id || i}>
              <div style={{
                background: "var(--sidebar)", borderRadius: 8, padding: "10px 12px", marginBottom: 0,
                borderLeft: `3px solid ${stop.confirmed ? "var(--green)" : "var(--amber)"}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{stop.title}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>
                      {fmtDate(stop.arrival)} → {fmtDate(stop.departure)}{stop.nights ? ` · ${stop.nights}n` : ""}
                    </div>
                    {canEdit && stop.segment_id && (
                      <button onClick={() => deleteSegment(stop.segment_id, stop.title)}
                        style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14, padding: "0 4px", lineHeight: 1 }}
                        title="Delete this stop">×</button>
                    )}
                  </div>
                </div>
                {stop.location !== stop.title && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{stop.location}</div>
                )}
                {!stop.confirmed && <div style={{ fontSize: 11, color: "var(--amber)", marginTop: 4, fontFamily: "var(--ff-mono)" }}>● unconfirmed</div>}
              </div>
              {/* Connector to next stop */}
              {i < data.stops.length - 1 && (() => {
                const leg = data.legs[i];
                if (!leg) return null;
                const isFlight = leg.source === "flight";
                const distLabel = leg.distance_km != null ? `${leg.distance_km}km` : "—";
                const durLabel = fmtDur(leg.duration_minutes);
                const confirmed = leg.confirmed === null ? null : leg.confirmed;
                const color = isFlight ? "var(--flight-color)" : (confirmed === false ? "var(--amber)" : "var(--text-muted)");
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0 6px 22px", margin: "4px 0", borderLeft: `2px dashed ${color}`, marginLeft: 14 }}>
                    <span style={{ fontSize: 14 }}>{isFlight ? "✈" : transportIcon(leg.transport_mode)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: isFlight ? "var(--flight-color)" : "var(--text-dim)" }}>
                        {isFlight && leg.flight_number ? `${leg.flight_number} · ` : ""}{distLabel} · {durLabel}
                        {leg.source === "estimate" && <span style={{ color: "var(--text-muted)", marginLeft: 6, fontSize: 10 }}>(driving est.)</span>}
                        {isFlight && leg.provider && <span style={{ color: "var(--text-muted)", marginLeft: 6, fontSize: 10 }}>· {leg.provider}</span>}
                      </div>
                      {!isFlight && confirmed === false && <div style={{ fontSize: 10, color: "var(--amber)", fontFamily: "var(--ff-mono)" }}>transport not confirmed</div>}
                      {!isFlight && confirmed === null && <div style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>no transport leg booked</div>}
                    </div>
                    {canEdit && leg.transport_segment_id && (
                      <button onClick={() => deleteSegment(leg.transport_segment_id, `${leg.from_title} → ${leg.to_title}`)}
                        style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14, padding: "0 4px", lineHeight: 1, flexShrink: 0 }}
                        title="Delete this transport segment">×</button>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginTop: 8 }}>
            Distances {data.legs.some(l => l.source === "openrouteservice") ? "include real-route data from OpenRouteService" : "are estimates from great-circle distance × 1.3 at 70 km/h. Set OPENROUTESERVICE_API_KEY for real routing"}.
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// VISA REQUIREMENTS PANEL — Feature 5
// Per-trip per-passport requirements matrix. Recommends the best passport
// per destination for dual nationals.
// ══════════════════════════════════════════════════════════════════════════
function VisaPanel({ tripId, authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`/api/trips/${tripId}/visa`);
        if (!r || !r.ok) { if (!cancelled) { setData(null); setLoading(false); } return; }
        const j = await r.json();
        if (!cancelled) { setData(j); setLoading(false); }
      } catch { if (!cancelled) { setData(null); setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [tripId, authFetch]);

  if (loading) return null;
  if (!data) return null;
  if ((!data.matrix || data.matrix.length === 0) && !data.message) return null;

  const COUNTRY_NAMES = {
    GB: "United Kingdom", AU: "Australia", US: "United States", CA: "Canada", NZ: "New Zealand", IE: "Ireland",
    DE: "Germany", FR: "France", IT: "Italy", ES: "Spain", PT: "Portugal", NL: "Netherlands", BE: "Belgium",
    AT: "Austria", CH: "Switzerland", DK: "Denmark", SE: "Sweden", NO: "Norway", FI: "Finland", IS: "Iceland",
    PL: "Poland", CZ: "Czechia", SK: "Slovakia", SI: "Slovenia", HR: "Croatia", HU: "Hungary", GR: "Greece",
    BG: "Bulgaria", RO: "Romania", LV: "Latvia", LT: "Lithuania", EE: "Estonia", LU: "Luxembourg", MT: "Malta",
    LI: "Liechtenstein", JP: "Japan", TH: "Thailand", ID: "Indonesia", SG: "Singapore", IN: "India", CN: "China",
    HK: "Hong Kong", MY: "Malaysia", VN: "Vietnam", PH: "Philippines", KR: "South Korea",
    TR: "Türkiye", AE: "UAE", QA: "Qatar", IL: "Israel", EG: "Egypt", ZA: "South Africa", MA: "Morocco",
    MX: "Mexico", BR: "Brazil", AR: "Argentina", CL: "Chile", PE: "Peru", CO: "Colombia",
    GL: "Greenland", IS_X: "Iceland",
  };
  const countryName = (cc) => COUNTRY_NAMES[cc] || cc;

  const TYPE_LABELS = {
    visa_free: "Visa-free", eta: "eTA", etias: "ETIAS", visa_on_arrival: "Visa on arrival",
    visa: "Visa required", citizen: "Citizen", unknown: "Verify with official source",
  };
  const TYPE_COLORS = {
    visa_free: "var(--green)", citizen: "var(--green)",
    eta: "var(--amber)", etias: "var(--amber)", visa_on_arrival: "var(--amber)",
    visa: "var(--red)", unknown: "var(--text-muted)",
  };

  const formatCost = (c) => {
    if (!c) return null;
    const sym = ({ USD: "$", CAD: "C$", AUD: "A$", NZD: "NZ$", EUR: "€", GBP: "£" }[c.currency]) || "";
    return c.amount === 0 ? "Free" : `${sym}${c.amount}${sym ? "" : " " + c.currency}`;
  };

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>🛂 Entry requirements</span>
          {data.matrix && data.matrix.length > 0 && (
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>
              {data.matrix.length} {data.matrix.length === 1 ? "destination" : "destinations"} · {data.passports.length} {data.passports.length === 1 ? "passport" : "passports"}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {data.message && <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 8 }}>{data.message}</div>}
          {data.matrix && data.matrix.map(row => {
            const best = row.best;
            return (
              <div key={row.destination_country} style={{
                background: "var(--sidebar)", borderRadius: 8, padding: 12, marginBottom: 10,
                borderLeft: `3px solid ${TYPE_COLORS[best?.result?.requirement_type] || "var(--border)"}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{countryName(row.destination_country)}</div>
                  {best && (
                    <div style={{ fontSize: 11, color: TYPE_COLORS[best.result.requirement_type], fontFamily: "var(--ff-mono)" }}>
                      Best: {best.passport_label} · {TYPE_LABELS[best.result.requirement_type] || best.result.requirement_type}
                    </div>
                  )}
                </div>
                {row.options.map(opt => {
                  const r = opt.result;
                  return (
                    <div key={opt.passport_index} style={{
                      marginTop: 8, padding: "8px 10px",
                      background: opt === best ? "rgba(255,255,255,0.03)" : "transparent",
                      borderRadius: 6,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
                          {opt.passport_label}
                          {opt.doc_on_file && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: "var(--green)", fontFamily: "var(--ff-mono)", background: "rgba(0,230,118,0.1)", border: "1px solid var(--green)", borderRadius: 4, padding: "1px 6px" }}>
                              ✓ {opt.doc_on_file.type?.toUpperCase()} on file
                              {opt.doc_on_file.reference_last4 ? ` ····${opt.doc_on_file.reference_last4}` : ""}
                              {opt.doc_on_file.expiry ? ` · exp ${opt.doc_on_file.expiry}` : ""}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: TYPE_COLORS[r.requirement_type], fontFamily: "var(--ff-mono)" }}>
                          {TYPE_LABELS[r.requirement_type] || r.requirement_type}
                          {r.name && ` · ${r.name}`}
                          {r.cost && ` · ${formatCost(r.cost)}`}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.5 }}>
                        {r.max_stay && <div>Max stay: {r.max_stay}</div>}
                        {r.processing_time && <div>Processing: {r.processing_time}</div>}
                        {r.validity_period && <div>Validity: {r.validity_period}</div>}
                        {r.conditions && r.conditions.map((c, i) => <div key={i}>• {c}</div>)}
                        {r.upcoming && (
                          <div style={{ color: "var(--amber)", marginTop: 4 }}>
                            Upcoming: {r.upcoming.name} from {r.upcoming.expected}
                            {r.upcoming.cost ? ` · ${formatCost(r.upcoming.cost)}` : ""}
                          </div>
                        )}
                        {r.warnings && r.warnings.map((w, i) => <div key={i} style={{ color: "var(--red)", marginTop: 4 }}>⚠ {w}</div>)}
                        {r.message && <div style={{ fontStyle: "italic", marginTop: 4 }}>{r.message}</div>}
                      </div>
                      {r.official_url && (
                        <a href={r.official_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{
                          display: "inline-block", marginTop: 6, fontSize: 11, color: "var(--green)",
                          fontFamily: "var(--ff-mono)", textDecoration: "underline",
                        }}>Official source ↗</a>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginTop: 8 }}>
            Source: static rules table (Sherpa adapter when configured). Requirements change frequently — always verify with the official government source before booking.
          </div>
        </div>
      )}
    </div>
  );
}

// Slim push-style banner that surfaces 1-3 scene-level "distinctives"
// for the city the user just arrived in. Phase 1 — no thumbs, no
// per-user profile yet. Stays scene-level (no venue names) so we
// don't need real-time operational data to be trustworthy. Each item
// carries a `bestTime` hint so the user can self-judge feasibility.
function LocalDistinctivesBanner({ city, country, dateKey, authFetch, mode = "arrival", daysUntil = 0, rainChance = null, departureTime = null }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(`distinctives-dismissed-${dateKey}`) === "1"; }
    catch { return false; }
  });
  const [expanded, setExpanded] = React.useState(null);
  // Per-item thumbs state. `rated` (Set of titles) hides items the user
  // has already ±'d on this date — Phase 2 persistence. `fadingTitle`
  // is the title currently mid-fade-out (for the optimistic UX).
  const [rated, setRated] = React.useState(() => {
    try {
      const raw = localStorage.getItem(`distinctives-rated-${dateKey}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [fadingTitle, setFadingTitle] = React.useState(null);

  React.useEffect(() => {
    if (dismissed || !city || !country) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch("/api/ai/local-distinctives", {
          method: "POST",
          body: JSON.stringify({ city, country }),
        });
        if (cancelled) return;
        if (r && r.ok) setData(await r.json());
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [city, country, dateKey, dismissed, authFetch]);

  if (dismissed || loading || !data?.items?.length) return null;

  // Items remaining after stripping ones the user has rated this date.
  // In preview mode we additionally restrict to items the user is
  // already past the ideal lead-time on — there's no point showing
  // a 0-lead "Ruin Bars" preview 5 days before arrival.
  let visibleItems = data.items.filter((it) => !rated.has(it.title));
  if (mode === "preview") {
    visibleItems = visibleItems.filter((it) => (it.actionLeadDays || 0) >= daysUntil);
  }
  if (visibleItems.length === 0) return null;

  const dismiss = () => {
    try { localStorage.setItem(`distinctives-dismissed-${dateKey}`, "1"); } catch {}
    setDismissed(true);
  };

  // Thumbs handler. Optimistic — animate the card out, then mark the
  // title rated locally (which removes it from `visibleItems` on the
  // next render). POST is fire-and-forget; one failed feedback isn't
  // worth surfacing.
  const onRate = (item, signal) => {
    setFadingTitle(item.title);
    setTimeout(() => {
      setRated((prev) => {
        const next = new Set(prev);
        next.add(item.title);
        try {
          localStorage.setItem(`distinctives-rated-${dateKey}`, JSON.stringify([...next]));
        } catch {}
        return next;
      });
      setFadingTitle(null);
    }, 250);
    authFetch("/api/ai/distinctives-feedback", {
      method: "POST",
      body: JSON.stringify({ category: item.category, signal }),
    }).catch(() => {});
  };

  return (
    <div style={{
      margin: "0 16px 12px", padding: "12px 14px",
      background: "linear-gradient(135deg, rgba(0,230,118,0.10), rgba(179,136,255,0.06))",
      border: "1px solid rgba(0,230,118,0.35)", borderRadius: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: "var(--green)", fontWeight: 600 }}>
          {mode === "preview"
            ? `🗓 Coming up in ${city} in ${daysUntil} day${daysUntil === 1 ? "" : "s"} — plan ahead`
            : `✨ Welcome to ${city} — things you'd miss`}
        </span>
        <button onClick={dismiss}
          aria-label="Dismiss"
          style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>
          ✕
        </button>
      </div>
      {departureTime && (
        <div style={{ fontSize: 11, color: "var(--amber)", fontFamily: "var(--ff-mono)", marginBottom: 8 }}>
          ⏰ Outbound at {departureTime} — limited time today
        </div>
      )}
      {visibleItems.map((item, i) => {
        const isOpen = expanded === item.title;
        const isFading = fadingTitle === item.title;
        return (
          <div key={item.title}
            onClick={() => setExpanded(isOpen ? null : item.title)}
            style={{
              padding: "10px 12px", marginBottom: i < visibleItems.length - 1 ? 8 : 0,
              background: "var(--card)", borderRadius: 8, cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.06)",
              opacity: isFading ? 0 : 1,
              transform: isFading ? "scale(0.96)" : "scale(1)",
              transition: "opacity 0.25s ease, transform 0.25s ease",
              pointerEvents: isFading ? "none" : "auto",
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
              <strong style={{ color: "var(--text)", fontSize: 14, flex: 1 }}>{item.title}</strong>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {item.category.replace("-", " · ")}
                </span>
                <button onClick={(e) => { e.stopPropagation(); onRate(item, 1); }}
                  aria-label="Thumbs up"
                  title="More like this"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: "0 4px", lineHeight: 1, color: "var(--green)" }}>
                  👍
                </button>
                <button onClick={(e) => { e.stopPropagation(); onRate(item, -1); }}
                  aria-label="Thumbs down"
                  title="Less like this"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: "0 4px", lineHeight: 1 }}>
                  👎
                </button>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 4 }}>
              {item.oneLineHook}
            </div>
            {item.provenance && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic", marginTop: 4 }}>
                ✦ {item.provenance}
              </div>
            )}
            <div style={{ fontSize: 11, color: "var(--green)", fontFamily: "var(--ff-mono)", marginTop: 6 }}>
              🕐 {item.bestTime}
            </div>
            {item.weatherSensitive && typeof rainChance === "number" && rainChance >= 50 && (
              <div style={{ fontSize: 11, color: "var(--amber)", fontFamily: "var(--ff-mono)", marginTop: 4 }}>
                ☔ {rainChance}% rain forecast — consider rescheduling
              </div>
            )}
            {isOpen && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.5 }}>{item.twoSentenceHistory}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, fontStyle: "italic" }}>{item.whyHere}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TimelineView({ tripData, timeline, countdown, canEdit, canAnnotate, userId, userName, onBack, onAddSegment, onDeleteSegment, onUpdateNotes, onInvite, onEditTrip, onReorder, authFetch, selectedTrip, onRefreshTrip, pendingScroll, onScrolledToSegment, onOpenAiTab, patchSegmentInTimeline }) {
  // Edits trigger a full trip refetch which re-renders the whole timeline,
  // resetting scroll. Capture the anchor segment's viewport offset before a
  // mutation, then scrollBy(delta) once the new timeline mounts so the user
  // stays put. Cleared after one application.
  const pendingScrollRef = useRef(null); // { segId, offsetBefore } | { segId, mode: "into-view" }
  const preserveScrollFor = useCallback((segId, _view) => {
    if (!segId) return;
    const key = `seg-${segId}${_view ? "-" + _view : ""}`;
    const el = document.getElementById(key);
    if (!el) return;
    pendingScrollRef.current = { key, offsetBefore: el.getBoundingClientRect().top };
  }, []);
  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      const el = document.getElementById(pending.key);
      if (!el) return;
      if (pending.mode === "into-view") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      // App scroll lives on .main (body has overflow:hidden), so walk up to
      // find the nearest scrollable ancestor and scroll that one.
      const scroller = el.closest(".main") || document.scrollingElement || document.documentElement;
      const offsetAfter = el.getBoundingClientRect().top;
      const delta = offsetAfter - pending.offsetBefore;
      if (Math.abs(delta) > 4) scroller.scrollBy({ top: delta, left: 0, behavior: "instant" });
    });
  }, [timeline]);

  // Per-day expansion state for the free-day-run collapser and the
  // FIFA-World-Cup-match rollups. Declared here (instead of further down
  // with the rest of TimelineView's state) so the pendingScroll useEffect
  // can expand a collapsed run/rollup before trying to scroll into it.
  const [expandedFreeRuns, setExpandedFreeRuns] = useState({}); // { runStartDate: true } — collapsed by default
  const [expandedWcDays, setExpandedWcDays] = useState({}); // { date: true } for FIFA World Cup match rollups
  // Heuristic: a segment is a "FIFA World Cup match" if its provider is FIFA or
  // its title carries the FIFA World Cup tag the seed/parser uses. Generic — any
  // future high-volume series tagged with provider=FIFA gets the same rollup.
  const isWcMatch = (s) => s && s.type === "event" && (
    (s.provider && s.provider.toUpperCase() === "FIFA") ||
    (s.title && /FIFA World Cup/i.test(s.title))
  );
  // Compute consecutive runs of free days (segments.length === 0) of length >= 2 → lookup map by date.
  // Memoised so the pendingScroll useEffect (which lists dateToRun in its deps)
  // doesn't refire on every unrelated TimelineView state change.
  const dateToRun = useMemo(() => {
    const runs = [];
    const map = {};
    let cur = null;
    for (const day of timeline) {
      if (day.date !== "unscheduled" && day.segments.length === 0) {
        if (!cur) cur = { start: day.date, end: day.date, dates: [day.date] };
        else { cur.end = day.date; cur.dates.push(day.date); }
      } else {
        if (cur && cur.dates.length >= 2) runs.push(cur);
        cur = null;
      }
    }
    if (cur && cur.dates.length >= 2) runs.push(cur);
    for (const r of runs) for (const d of r.dates) map[d] = r;
    return map;
  }, [timeline]);

  // External pending scroll (just-added segment, or a city/alert click from
  // the My Trips card). Runs once the timeline has rendered, then asks the
  // parent to clear. { segId } anchors a specific row; { city } finds the
  // first segment whose location, title, notes or provider mentions the
  // city. Falls back to the first non-empty day so the user always lands
  // somewhere relevant instead of silently doing nothing.
  useEffect(() => {
    if (!pendingScroll) return;
    // Trip data + timeline load asynchronously after selectTrip(). If we
    // run before there are any segments, every lookup falls back to the
    // first row AND clears pendingScroll — so by the time the real data
    // lands the user has lost the target. Bail until the timeline has
    // at least one segment OR (for a `today`-only scroll) one day card.
    const segmentCount = (timeline || []).reduce((n, d) => n + (d.segments?.length || 0), 0);
    if ((pendingScroll.segId || pendingScroll.city) && segmentCount === 0) return;
    // Same defensive bail for `today`: if the timeline hasn't loaded yet
    // OR if the day-card DOM hasn't committed yet (TimelineView just
    // mounted, day cells are about to render), the effect would lose
    // the pendingScroll to findFirstSegEl's fallback. Tim's 2026-06-16
    // repro — "Open today on the in-progress hero lands on June 11
    // instead of today" — was this exact race: the effect fired before
    // `day-2026-06-16` was in the DOM, the fallback returned an early
    // event-day segment, and pendingScroll cleared before the real
    // mount completed.
    if (pendingScroll.today) {
      if (segmentCount === 0) return;
      const anyDayInDom = (timeline || []).some(d => d.date && document.getElementById(`day-${d.date}`));
      if (!anyDayInDom) return;
    }
    const elFor = (seg) => document.getElementById(`seg-${seg.id}${seg._view ? "-" + seg._view : ""}`);
    const findCitySeg = (city) => {
      const needle = String(city).toLowerCase().trim();
      if (!needle) return null;
      // Match a single location string against the needle, resolving any
      // 3-letter IATA codes (BCN, BUD, CPH …) through AIRPORT_TO_CITY first.
      const locMatches = (loc) => {
        const v = (loc || "").toLowerCase();
        if (!v) return false;
        if (v.includes(needle)) return true;
        const codes = v.match(/\b[a-z]{3}\b/g) || [];
        return codes.some(c => (AIRPORT_TO_CITY[c] || "").includes(needle));
      };
      // Priority order. The user's mental model of clicking a city pill is
      // "show me where I'm AT the city" — not the first segment that
      // happens to mention it. So a departure-from-Copenhagen transfer
      // titled "Transfer to Hotel Keflavik" should NOT win over the actual
      // stay in Copenhagen later in the timeline.
      //
      // 1. STAYS at the city (hotel/event/excursion/restaurant) whose own
      //    location matches — this is the user's strongest "I am here" signal.
      // 2. ARRIVALS into the city — any seg whose end_location matches.
      // 3. DEPARTURES from the city — start_location matches. Last-resort
      //    location match.
      // 4. Pickup/dropoff fields.
      // 5. Title contains the city name.
      // 6. Provider / notes wider fallback.
      const stayTypes = new Set(["hotel", "friends_house", "event", "excursion", "restaurant", "gym"]);
      const passes = [
        (s) => stayTypes.has(s.type) && (locMatches(s.start_location) || locMatches(s.end_location)),
        (s) => locMatches(s.end_location),
        (s) => locMatches(s.start_location),
        (s) => locMatches(s.pickup_location) || locMatches(s.dropoff_location),
        (s) => (s.title || "").toLowerCase().includes(needle),
        (s) => `${s.provider || ""} ${s.notes || ""}`.toLowerCase().includes(needle),
      ];
      for (const match of passes) {
        for (const day of timeline || []) {
          for (const seg of day.segments || []) {
            if (match(seg)) return seg;
          }
        }
      }
      return null;
    };
    const findFirstSegEl = () => {
      for (const day of timeline || []) {
        for (const seg of day.segments || []) {
          const el = elFor(seg);
          if (el) return el;
        }
      }
      return null;
    };
    const findSegElById = (id) => {
      return document.getElementById(`seg-${id}`)
        || document.getElementById(`seg-${id}-pickup`)
        || document.getElementById(`seg-${id}-return`);
    };
    // Clamp today's date to the trip range so a card-click on an upcoming
    // or already-finished trip lands on a sensible day (start / end).
    const todayKey = () => {
      const today = localDateKey();
      const start = tripData?.start_date;
      const end = tripData?.end_date;
      if (start && today < start) return start;
      if (end && today > end) return end;
      return today;
    };
    // Resolve the target segment (if any) up front so we can also check
    // whether it's hidden inside a collapsed free-day run or WC rollup.
    // If we just call scrollIntoView with no expansion pass first, the
    // DOM element doesn't exist and the fallback dumps the user on the
    // first segment of the trip — which is what was happening for any
    // alert pointing at a segment buried in a multi-day gap.
    let targetSeg = null;
    if (pendingScroll.segId) {
      for (const day of timeline || []) {
        for (const s of day.segments || []) {
          if (s.id === pendingScroll.segId) { targetSeg = s; break; }
        }
        if (targetSeg) break;
      }
    } else if (pendingScroll.city) {
      targetSeg = findCitySeg(pendingScroll.city);
    }

    // If the target's day is inside a collapsed free-day run, expand
    // that run first and bail — the dep change re-runs this effect and
    // the scroll happens once the DOM has the new rows.
    if (targetSeg) {
      const segDate = (targetSeg.start_datetime || "").split("T")[0];
      const run = segDate ? dateToRun[segDate] : null;
      if (run && !expandedFreeRuns[run.start]) {
        setExpandedFreeRuns(prev => ({ ...prev, [run.start]: true }));
        return;
      }
      // Same story for World Cup rollups — the segment's day might be
      // collapsed into a "N FIFA World Cup matches" card.
      if (isWcMatch(targetSeg) && segDate && !expandedWcDays[segDate]) {
        setExpandedWcDays(prev => ({ ...prev, [segDate]: true }));
        return;
      }
    }
    // "Today" target: the day card for today may itself sit inside a
    // collapsed free-day run (e.g. Tim's trip is "in progress" but he
    // has nothing booked for today). Expand that run before trying to
    // scroll, otherwise findFirstSegEl fires and dumps the user on day 1.
    if (pendingScroll.today) {
      const tk = (() => {
        const today = localDateKey();
        if (tripData?.start_date && today < tripData.start_date) return tripData.start_date;
        if (tripData?.end_date && today > tripData.end_date) return tripData.end_date;
        return today;
      })();
      const run = dateToRun[tk];
      if (run && !expandedFreeRuns[run.start]) {
        setExpandedFreeRuns(prev => ({ ...prev, [run.start]: true }));
        return;
      }
    }
    // Explicit date target (from calendar day click). Same collapsed-run
    // dance: expand first so the day-${date} element exists, then scroll.
    if (pendingScroll.date) {
      const run = dateToRun[pendingScroll.date];
      const inDom = !!document.getElementById(`day-${pendingScroll.date}`);
      console.log(`[scroll date] requested=${pendingScroll.date} run=${run ? `${run.start}..${run.end}` : "none"} runExpanded=${run ? !!expandedFreeRuns[run.start] : "n/a"} inDom=${inDom}`);
      if (run && !expandedFreeRuns[run.start]) {
        setExpandedFreeRuns(prev => ({ ...prev, [run.start]: true }));
        return;
      }
    }

    requestAnimationFrame(() => {
      let el = null;
      if (pendingScroll.segId) {
        el = findSegElById(pendingScroll.segId);
        if (!el && targetSeg) {
          // Segment row hasn't rendered yet (lazy timeline for 200+ segment
          // trips), but the day header always exists for any populated day.
          // Anchor there instead of cascading to findFirstSegEl which dumps
          // the user on whatever section happens to be in view — typically
          // today's segments, not the alert's target day.
          const segDate = (targetSeg.start_datetime || "").split("T")[0];
          if (segDate) el = document.getElementById(`day-${segDate}`);
        }
        if (!el) console.warn(`[scroll] no segment or day found for id ${pendingScroll.segId}`);
      } else if (pendingScroll.city) {
        const seg = targetSeg || findCitySeg(pendingScroll.city);
        if (seg) el = elFor(seg);
        else console.warn(`[scroll] no segment matched city "${pendingScroll.city}" — falling back to first day`);
      } else if (pendingScroll.today) {
        const date = todayKey();
        el = document.getElementById(`day-${date}`);
        console.log(`[scroll today] local=${localDateKey()} resolved=${date} found=${!!el} start=${tripData?.start_date} end=${tripData?.end_date}`);
        if (!el) {
          // Today's day card isn't in the DOM. Walk timeline forward
          // from today looking for the nearest later day card, then
          // backward for the nearest earlier one — whichever lands
          // closer in date. DO NOT cascade to findFirstSegEl: that
          // dumps the user on May 11 (the trip's first segment), or
          // worse, on an arbitrary event-day segment further forward.
          // Tim's 2026-06-16 PWA repro landed on June 11 (the first
          // FIFA WC match day) because the old fallback walked the
          // segments array and returned the first one whose DOM
          // element happened to render in time.
          const dates = (timeline || []).map(d => d.date);
          const candidates = nearestDayFallbackCandidates(dates, date);
          for (const c of candidates) {
            const alt = document.getElementById(`day-${c}`);
            if (alt) { el = alt; console.warn(`[scroll today] fell back to nearest day card ${c}`); break; }
          }
          if (!el) console.warn(`[scroll] no day card found for ${date} (no nearest fallback either)`);
        }
      } else if (pendingScroll.date) {
        el = document.getElementById(`day-${pendingScroll.date}`);
        console.log(`[scroll date final] target=day-${pendingScroll.date} found=${!!el}`);
        if (!el) console.warn(`[scroll] no day card found for ${pendingScroll.date}`);
      }
      // Always land somewhere useful — fall back to the first segment so a
      // missed match doesn't leave the user staring at whatever was on screen.
      // For `today` we already did a chronological fallback above; skip the
      // findFirstSegEl cascade so we don't blow past today's intended land.
      if (!el && !pendingScroll.today) el = findFirstSegEl();
      if (el) {
        // CRITICAL: do NOT use behavior: "smooth" here. The day cards
        // load weather chips and other async content via their own
        // useEffects; during a smooth scroll animation, those renders
        // push the target day DOWN in the document. The smooth scroll
        // already committed to the OLD Y position, so the user ends up
        // a few day cards above the intended target. Tim saw "click
        // 28 May → lands on 25/26 May" caused exactly by this.
        //
        // Strategy:
        //   1. instant scroll now to land near the target (cheap)
        //   2. wait 350ms for async per-day renders to settle
        //   3. instant scroll again as the corrective. Loop a couple
        //      of times because some async content (events, AI
        //      suggestions) takes even longer.
        el.scrollIntoView({ block: "start" });
        const elId = el.id;
        const correct = (delay) => setTimeout(() => {
          const fresh = elId ? document.getElementById(elId) : null;
          if (fresh) fresh.scrollIntoView({ block: "start" });
        }, delay);
        correct(150);
        correct(400);
        correct(900);
        // The fixed 150/400/900 ms passes only cover the FAST async
        // surfaces (weather chip API, attachments list). Slower ones —
        // DayPhotosStrip thumbnails, DayEventsDrawer's AI fetch,
        // DaySuggestionDrawer, LocalDistinctivesBanner — resolve at
        // 1.5–3 s and keep pushing the target downward. Tim's repro
        // 2026-06-14: tap 14 Jun in Calendar → lands on 10 Jun (~4
        // day cards too high) because the corrections finished
        // before the async content above settled.
        //
        // ResizeObserver watches the document for height changes for
        // up to 5 s after the click. Any change re-pins the target
        // to scroll-start. Tear-down happens on stability (no growth
        // for 600 ms) or the 5 s budget — whichever lands first.
        if (typeof ResizeObserver !== "undefined") {
          // App scroll lives on `.main` (body has overflow:hidden — see
          // line 3086). Observing document.body never fires because the
          // body is capped at the viewport height; the growth happens
          // INSIDE `.main`. Watch the actual scroller AND every existing
          // day card so we catch async content (DayPhotos / DayEvents /
          // DaySuggestion / LocalDistinctives) loading above the target
          // and pushing it down. Tim's iPhone Chrome repro 2026-06-15:
          // Open today landed on June 10 because day cards above grew
          // by hundreds of pixels after the initial scroll and nothing
          // re-pinned the target.
          const scroller = el.closest(".main") || document.scrollingElement || document.documentElement;
          let lastHeight = scroller.scrollHeight;
          let stabilityTimer = null;
          const ro = new ResizeObserver(() => {
            const h = scroller.scrollHeight;
            if (h === lastHeight) return;
            lastHeight = h;
            const fresh = elId ? document.getElementById(elId) : null;
            if (fresh) fresh.scrollIntoView({ block: "start" });
            // Reset stability window — anything still growing means
            // more async content is incoming.
            if (stabilityTimer) clearTimeout(stabilityTimer);
            stabilityTimer = setTimeout(() => ro.disconnect(), 600);
          });
          // Observe the scroller itself AND every day card currently in
          // the document. A single ResizeObserver can observe multiple
          // elements; observing `.main` plus every day-* div ensures we
          // catch growth from async content INSIDE day cards (which is
          // what was missed when only watching document.body).
          ro.observe(scroller);
          for (const d of document.querySelectorAll('[id^="day-"], [id^="run-"]')) ro.observe(d);
          // Hard cap so we never leak the observer if the page keeps
          // mutating for some other reason.
          setTimeout(() => ro.disconnect(), 5000);
        }
      }
      if (onScrolledToSegment) onScrolledToSegment();
    });
  }, [pendingScroll, timeline, onScrolledToSegment, tripData?.start_date, tripData?.end_date, expandedFreeRuns, expandedWcDays, dateToRun]);

  const [expandedSeg, setExpandedSeg] = useState(null);
  const [hotelOverviews, setHotelOverviews] = useState({}); // { segId: { loading, text, error } }
  const [nearHotelOpen, setNearHotelOpen] = useState({}); // { segId: true }
  const [selectedPassengerIdx, setSelectedPassengerIdx] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [editNotesText, setEditNotesText] = useState("");
  const [personalNotes, setPersonalNotes] = useState({});
  const [editingPersonalNote, setEditingPersonalNote] = useState(null);
  const [personalNoteText, setPersonalNoteText] = useState("");
  const [votes, setVotes] = useState({}); // { segId: { up: N, down: N } }
  const [myVotes, setMyVotes] = useState({}); // { segId: 1 or -1 }
  // expandedFreeRuns / expandedWcDays / isWcMatch / dateToRun moved above
  // the pendingScroll useEffect (which now expands collapsed runs before
  // scrolling, so it needs them in scope).

  // ── Per-traveller filter ────────────────────────────────────────────────
  // Server returns each traveller's segment_ids (passenger names + flight
  // location tracking). Single-select model: click a chip → show only that
  // person; click the same chip → back to Everyone.
  const [travellers, setTravellers] = useState([]);
  const [selectedTraveller, setSelectedTraveller] = useState(null);
  useEffect(() => {
    if (!tripData?.id) return;
    let cancelled = false;
    authFetch(`/api/trips/${tripData.id}/travellers`)
      .then(r => r && r.ok ? r.json() : { travellers: [] })
      .then(d => { if (!cancelled) setTravellers(d.travellers || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tripData?.id, authFetch]);
  const selectedSegmentIds = (() => {
    if (!selectedTraveller) return null;
    const t = travellers.find(x => x.name === selectedTraveller);
    return t ? new Set(t.segment_ids || []) : new Set();
  })();
  const segVisibleToSelection = (seg) => {
    if (!selectedSegmentIds) return true;
    return selectedSegmentIds.has(seg.id);
  };
  const toggleTraveller = (name) => setSelectedTraveller(prev => prev === name ? null : name);
  const showAllTravellers = () => setSelectedTraveller(null);

  // ── Print / PDF export ────────────────────────────────────────────────
  // Opens a print-friendly window scoped to one traveller (or everyone) and
  // triggers the browser print dialog. On iPad/iOS the user can pick "Save
  // to Files" / "Save as PDF" from the share sheet; on desktop it's a
  // one-click PDF save. No extra deps and the same template adapts to A4
  // and Letter via @page.
  // includeTvEvents=false (default) drops events where the user is NOT in the
  // venue's timezone on that date — i.e. matches you'd watch on TV rather
  // than attend. Cuts a Trip-16-with-104-WC-matches PDF from 45+ pages to a
  // handful. Caller opts in via the menu option to keep the full set.
  const exportItineraryPdf = (traveller /* null = everyone */, opts = {}) => {
    const includeTvEvents = !!opts.includeTvEvents;
    if (!timeline || timeline.length === 0) return;
    const t = traveller ? travellers.find(x => x.name === traveller) : null;
    const tSegIds = t ? new Set(t.segment_ids || []) : null;
    const segVisible = (seg) => {
      if (tSegIds && !tSegIds.has(seg.id)) return false;
      if (!includeTvEvents && seg.type === "event" && seg.venue_timezone && seg.user_tz && seg.user_tz !== seg.venue_timezone) return false;
      return true;
    };
    // Window bounds (date-only) for a filtered traveller — drops days before
    // they arrive and after they leave so we don't render a "30 free days"
    // block covering Tim's solo May/early-June in Diana's PDF.
    const winStartDate = t ? t.first_flight.split("T")[0] : null;
    const winEndDate = t ? t.last_flight.split("T")[0] : null;

    const fmtDay = (date) => new Date(date + "T12:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const fmtDayShort = (date) => new Date(date + "T12:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
    const fmtTime = (s) => s ? (s.split("T")[1] || "").slice(0, 5) : "";
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
    const dayDiff = (start, end) => {
      if (!start || !end) return 0;
      const sd = start.split("T")[0], ed = end.split("T")[0];
      if (sd === ed) return 0;
      return Math.round((new Date(ed + "T00:00:00") - new Date(sd + "T00:00:00")) / 86400000);
    };

    const renderSeg = (seg) => {
      const sTime = fmtTime(seg.start_datetime);
      const eTime = fmtTime(seg.end_datetime);
      const dd = dayDiff(seg.start_datetime, seg.end_datetime);
      // Timezone-aware decorations only apply to flights — for hotels and
      // car rentals "+2d" / "arrives" is meaningless because they naturally
      // span multiple days and the times shown are wall-clock not transit.
      const isFlight = seg.type === "flight";
      let timeLabel = "";
      if (sTime && eTime) {
        timeLabel = `${sTime}–${eTime}`;
        if (isFlight && dd > 0) timeLabel += ` +${dd}d`;
      } else if (sTime) timeLabel = sTime;
      const arrivalDateNote = isFlight && dd > 0 && seg.end_datetime ? fmtDayShort(seg.end_datetime.split("T")[0]) : null;

      const labelMap = { flight: "✈ Flight", hotel: "🏨 Hotel", car_rental: "🚙 Car rental", bike_hire: "🚲 Bike hire", ferry: "⛴ Ferry", transport: "🚗 Transport", excursion: "🥾 Excursion", event: "🎭 Event", restaurant: "🍽 Restaurant", other: "•" };
      const isPickup = seg.type === "car_rental" && seg._view === "pickup";
      const isReturn = seg.type === "car_rental" && seg._view === "return";
      const titlePrefix = isPickup ? "Pickup: " : isReturn ? "Return: " : "";
      const loc = isReturn ? (seg.dropoff_location || seg.end_location || "") :
                  isPickup ? (seg.pickup_location || seg.start_location || "") :
                  (seg.start_location && seg.end_location && seg.start_location !== seg.end_location ? `${seg.start_location} → ${seg.end_location}` : (seg.start_location || seg.end_location || ""));
      const detail = [];
      if (seg.flight_number) detail.push(esc(seg.flight_number));
      if (seg.provider) detail.push(esc(seg.provider));
      if (seg.confirmation_code) detail.push(`Ref: ${esc(seg.confirmation_code)}`);
      if (seg.type === "hotel" && seg.start_datetime && seg.end_datetime) {
        const inDate = seg.start_datetime.split("T")[0];
        const outDate = seg.end_datetime.split("T")[0];
        const nights = Math.max(1, Math.round((new Date(outDate) - new Date(inDate)) / 86400000));
        detail.push(`${nights} night${nights === 1 ? "" : "s"} · check out ${fmtDay(outDate).split(",")[0]}`);
      }
      if (arrivalDateNote) detail.push(`<span style="color:#b06d00">arrives ${esc(arrivalDateNote)}</span>`);
      const passengers = (() => { try { const p = JSON.parse(seg.passengers || "[]"); return p.length ? `Passengers: ${p.map(esc).join(", ")}` : ""; } catch { return ""; } })();
      return `<div class="seg ${esc(seg.type)}">
        <div class="seg-row">
          <div class="seg-time">${esc(timeLabel)}</div>
          <div class="seg-main">
            <div class="seg-title">${esc(labelMap[seg.type] || "•")} · ${titlePrefix}${esc(seg.title)}</div>
            ${loc ? `<div class="seg-loc">${esc(loc)}</div>` : ""}
            ${detail.length ? `<div class="seg-detail">${detail.join(" · ")}</div>` : ""}
            ${passengers ? `<div class="seg-pax">${passengers}</div>` : ""}
            ${seg.notes ? `<div class="seg-notes">${esc(seg.notes)}</div>` : ""}
          </div>
        </div>
      </div>`;
    };

    // Walk the timeline. Filter segments by traveller, cap days to their
    // window, and collapse consecutive empty days within the window into a
    // single rollup line.
    const dayInWindow = (date) => {
      if (!winStartDate) return true;
      return date >= winStartDate && date <= winEndDate;
    };
    const filteredTimeline = timeline
      .filter(d => dayInWindow(d.date))
      .map(d => ({ ...d, segments: (d.segments || []).filter(segVisible) }));

    const blocks = [];
    let runStart = null, runEnd = null;
    const flushRun = () => {
      if (runStart) {
        const span = runStart === runEnd ? fmtDay(runStart) : `${fmtDay(runStart)} — ${fmtDay(runEnd)}`;
        const count = (new Date(runEnd) - new Date(runStart)) / 86400000 + 1;
        blocks.push(`<div class="free-run">${count} free day${count === 1 ? "" : "s"} · ${esc(span)}</div>`);
        runStart = null; runEnd = null;
      }
    };
    for (const day of filteredTimeline) {
      const segs = day.segments;
      if (segs.length === 0) {
        if (!runStart) runStart = day.date;
        runEnd = day.date;
        continue;
      }
      flushRun();
      blocks.push(`<section class="day"><h2>${fmtDay(day.date)}</h2>${segs.map(renderSeg).join("")}</section>`);
    }
    flushRun();

    // Derive header from the FILTERED segments — countries the traveller
    // actually visits — rather than the trip-level title which lists every
    // country in the trip.
    const visitedLocations = (() => {
      const set = new Set();
      for (const day of filteredTimeline) {
        for (const seg of day.segments) {
          const cands = [seg.start_location, seg.end_location, seg.pickup_location, seg.dropoff_location];
          for (const loc of cands) {
            if (!loc) continue;
            // Take the last comma-separated token, often the country/region name
            const tail = String(loc).split(/[,·]/).map(x => x.trim()).filter(Boolean).pop();
            if (tail && tail.length > 1) set.add(tail);
          }
        }
      }
      return [...set];
    })();

    const subject = t ? `${t.name}'s itinerary` : "Full itinerary";
    const dateRange = t
      ? `${fmtDay(t.first_flight.split("T")[0])} — ${fmtDay(t.last_flight.split("T")[0])}`
      : `${tripData.start_date ? fmtDay(tripData.start_date) : ""} — ${tripData.end_date ? fmtDay(tripData.end_date) : ""}`;
    // When filtering for one traveller, use a tighter header derived from their
    // segments — not the full trip title (which lists every country in the trip).
    const headerH1 = t ? t.name : esc(tripData.title);
    const headerSub = t
      ? `${esc(subject)} · ${esc(dateRange)}${visitedLocations.length ? " · " + esc(visitedLocations.slice(0, 6).join(" · ")) : ""}`
      : `${esc(subject)} · ${esc(dateRange)}`;

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(tripData.title)} — ${esc(subject)}</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #1a1630; margin: 0; padding: 0; line-height: 1.45; font-size: 11pt; }
  .header { border-bottom: 2px solid #1a1630; padding-bottom: 10px; margin-bottom: 18px; }
  .header h1 { font-size: 20pt; margin: 0 0 4px 0; }
  .header .meta { font-size: 10pt; color: #555; }
  .day { margin: 16px 0; page-break-inside: avoid; }
  .day h2 { font-size: 13pt; margin: 0 0 8px 0; padding: 4px 0; border-bottom: 1px solid #ccc; color: #1a1630; }
  .seg { margin: 6px 0; page-break-inside: avoid; }
  .seg-row { display: flex; gap: 12px; }
  .seg-time { width: 70px; font-family: "SF Mono", "Menlo", monospace; font-size: 9.5pt; color: #555; padding-top: 2px; flex-shrink: 0; }
  .seg-main { flex: 1; min-width: 0; }
  .seg-title { font-weight: 600; font-size: 11pt; }
  .seg-loc { font-size: 10pt; color: #444; }
  .seg-detail { font-size: 9.5pt; color: #666; margin-top: 2px; }
  .seg-pax { font-size: 9.5pt; color: #666; margin-top: 2px; font-style: italic; }
  .seg-notes { font-size: 9.5pt; color: #444; margin-top: 4px; padding: 4px 8px; background: #f4f1ea; border-left: 2px solid #b8b4cc; }
  .seg.hotel .seg-title { color: #b06d00; }
  .seg.flight .seg-title { color: #0a4ea1; }
  .seg.car_rental .seg-title { color: #5a2e9c; }
  .seg.event .seg-title { color: #8a5a00; }
  .free-run { margin: 8px 0; padding: 6px 10px; background: #f0f0f0; border-radius: 4px; font-size: 10pt; color: #555; font-style: italic; }
  .footer { margin-top: 30px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 9pt; color: #888; text-align: center; }
  @media print { .no-print { display: none; } body { background: #fff; } }
  .no-print { position: fixed; top: 12px; right: 12px; background: #1a1630; color: #fff; padding: 8px 14px; border-radius: 6px; cursor: pointer; border: none; font-size: 12pt; }
</style></head><body>
<button class="no-print" onclick="window.print()">Print / Save PDF</button>
<div class="header">
  <h1>${esc(headerH1)}</h1>
  <div class="meta">${headerSub}</div>
</div>
${blocks.join("")}
<div class="footer">Generated by TripIQ · ${new Date().toLocaleDateString("en-AU")}</div>
</body></html>`;
    // In-app modal overlay rather than window.open — installed PWAs have no
    // tab chrome to close from, and a new window strands the user with no
    // back-to-app affordance. The modal mounts an iframe with the HTML and
    // triggers print from a clearly visible Close × button + Print button.
    setExportHtml(html);
  };
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportHtml, setExportHtml] = useState(null);

  // dateToRun moved above the pendingScroll useEffect — see that block.

  // Load votes on trip load
  useEffect(() => {
    if (!selectedTrip || !authFetch) return;
    authFetch(`/api/trips/${selectedTrip}/votes`).then(r => r.json()).then(d => {
      setVotes(d.votes || {});
      setMyVotes(d.myVotes || {});
    }).catch(() => {});
  }, [selectedTrip, authFetch]);

  const handleVote = async (segId, vote) => {
    const currentVote = myVotes[segId] || 0;
    const newVote = currentVote === vote ? 0 : vote; // toggle off if same
    try {
      await authFetch(`/api/segments/${segId}/vote`, { method: "POST", body: JSON.stringify({ vote: newVote }) });
      // Optimistic update
      setMyVotes(prev => ({ ...prev, [segId]: newVote }));
      setVotes(prev => {
        const current = prev[segId] || { up: 0, down: 0 };
        const updated = { ...current };
        if (currentVote === 1) updated.up = Math.max(0, updated.up - 1);
        if (currentVote === -1) updated.down = Math.max(0, updated.down - 1);
        if (newVote === 1) updated.up++;
        if (newVote === -1) updated.down++;
        return { ...prev, [segId]: updated };
      });
    } catch {}
  };

  // Load personal notes
  useEffect(() => {
    if (!selectedTrip || !authFetch) return;
    authFetch(`/api/trips/${selectedTrip}/personal-notes`).then(r => r.json()).then(setPersonalNotes).catch(() => {});
  }, [selectedTrip, authFetch]);

  const savePersonalNote = async (segId) => {
    try {
      await authFetch(`/api/segments/${segId}/personal-note`, { method: "PUT", body: JSON.stringify({ note: personalNoteText }) });
      setPersonalNotes(prev => ({ ...prev, [segId]: personalNoteText }));
      setEditingPersonalNote(null);
      setPersonalNoteText("");
    } catch {}
  };
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [dragDate, setDragDate] = useState(null);
  const [dayNotes, setDayNotes] = useState({});
  const [editingNoteDate, setEditingNoteDate] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [daySuggestions, setDaySuggestions] = useState({});
  const [expandedPlace, setExpandedPlace] = useState(null); // { name, brief, detail?, hours?, tip?, date? }
  const [loadingPlaceDetail, setLoadingPlaceDetail] = useState(false);
  const [suggestingDate, setSuggestingDate] = useState(null);
  const [suggestInterest, setSuggestInterest] = useState({}); // date → true if interest picker is showing
  const [suggestInterestText, setSuggestInterestText] = useState({}); // date → custom text
  const [dayWeather, setDayWeather] = useState({});
  // Morning weather for travel days only — date → { location, weather }.
  // The chip renders "<city> 30°/18° → 26°/20°" when an entry is here,
  // so a day where the traveller moves from Girona to Barcelona shows
  // both forecasts instead of just the hotel city's.
  const [morningWeather, setMorningWeather] = useState({});
  const [dayEvents, setDayEvents] = useState({});
  const [loadingEvents, setLoadingEvents] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null); // "date-index" key
  const [hotelDetail, setHotelDetail] = useState(null); // { segId, data, loading }
  const [expandedWeather, setExpandedWeather] = useState(null); // date string
  const [hourlyWeather, setHourlyWeather] = useState({}); // { date: { hourly: [...] } }
  // Morning hourly for travel days — date → { location, hourly }.
  // The inline expanded hourly view splits into two labelled rows on
  // any day where the daily chip is also split.
  const [morningHourlyWeather, setMorningHourlyWeather] = useState({});
  const [editingSeg, setEditingSeg] = useState(null); // segment id being fully edited
  const [editSegData, setEditSegData] = useState({}); // field values for editing

  const fetchHotelDetail = async (seg) => {
    setHotelDetail({ segId: seg.id, data: null, loading: true });
    try {
      const r = await authFetch("/api/ai/hotel-detail", {
        method: "POST",
        body: JSON.stringify({ hotel_name: seg.title, location: seg.start_location, room_type: seg.room_type, dates: `${seg.start_datetime?.split("T")[0]} to ${seg.end_datetime?.split("T")[0]}` }),
      });
      const data = await r.json();
      setHotelDetail({ segId: seg.id, data, loading: false });
    } catch { setHotelDetail(null); }
  };

  // Determine location for a given date based on hotel segments
  const getLocationForDate = useCallback((date) => {
    if (!tripData?.segments) return tripData?.destination?.split(",")[0]?.trim() || "";
    const allSegs = [...tripData.segments].sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));

    // First check: is there an accommodation (hotel OR friends_house)
    // covering this date? Use the one with the LATEST check-in.
    const hotels = allSegs.filter(s => ACCOMMODATION_TYPES.has(s.type));
    let bestHotel = null;
    for (const h of hotels) {
      const checkIn = h.start_datetime?.split("T")[0];
      const checkOut = h.end_datetime?.split("T")[0];
      if (checkIn && checkOut && date >= checkIn && date < checkOut) {
        if (!bestHotel || checkIn > (bestHotel.start_datetime?.split("T")[0] || "")) bestHotel = h;
      }
    }
    if (bestHotel) return bestHotel.start_location || bestHotel.title;

    // Second check: find the most recent segment that ended before or on this date
    // and infer location from its arrival/end location
    let lastLocation = "";
    for (const s of allSegs) {
      const segDate = (s.end_datetime || s.start_datetime || "").split("T")[0];
      if (segDate && segDate <= date) {
        // Use the end/arrival location of this segment
        lastLocation = s.end_location || s.start_location || s.title || lastLocation;
      }
    }
    if (lastLocation) return lastLocation;

    return tripData?.destination?.split(",")[0]?.trim() || "";
  }, [tripData]);

  // Morning city = the place the traveller wakes up before any
  // movement segments fire. Returns null when the day has no
  // inter-city movement OR when the morning city resolves to the same
  // place as the hotel-covering city. Mirrors iOS's
  // morningLocationForDate so the split chip is symmetrical.
  const getMorningLocationForDate = useCallback((date) => {
    if (!tripData?.segments) return null;
    const evening = getLocationForDate(date);
    const movementTypes = new Set(["flight", "train", "ferry", "transport", "car_rental", "bike_hire"]);
    const daySegments = tripData.segments
      .filter(s => (s.start_datetime || "").slice(0, 10) === date)
      .filter(s => movementTypes.has(s.type) && s.start_location)
      .sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));
    if (!daySegments.length) return null;
    const origin = daySegments[0].start_location;
    if (!origin) return null;
    const norm = (s) => {
      const lower = (s || "").toLowerCase();
      for (const chunk of lower.split(",").map(c => c.trim())) {
        const alpha = chunk.replace(/[^a-z \-]/g, "").trim();
        const first = (alpha.split("-")[0] || alpha).trim();
        if (first.length >= 4) return first;
      }
      return lower.trim();
    };
    if (norm(origin) === norm(evening)) return null;
    return origin;
  }, [tripData, getLocationForDate]);

  // Fetch weather for visible days
  const fetchWeather = useCallback(async (date) => {
    const morning = getMorningLocationForDate(date);
    // Evening forecast (hotel-covering city, default)
    if (!dayWeather[date]) {
      const location = getLocationForDate(date);
      try {
        const r = await authFetch(`/api/weather?location=${encodeURIComponent(location)}&date=${date}`);
        const data = await r.json();
        if (!data.error) setDayWeather(prev => ({ ...prev, [date]: data }));
      } catch {}
    }
    // Morning forecast for travel days
    if (morning && !morningWeather[date]) {
      try {
        const r = await authFetch(`/api/weather?location=${encodeURIComponent(morning)}&date=${date}`);
        const data = await r.json();
        if (!data.error) setMorningWeather(prev => ({ ...prev, [date]: { location: morning, weather: data } }));
      } catch {}
    }
  }, [authFetch, dayWeather, morningWeather, getLocationForDate, getMorningLocationForDate]);

  // Auto-fetch weather for all trip days
  useEffect(() => {
    if (!timeline?.length || !authFetch) return;
    timeline.forEach(day => { if (day.date !== "unscheduled") fetchWeather(day.date); });
  }, [timeline, authFetch, fetchWeather]);

  // Fetch local events
  const fetchEvents = async (date) => {
    if (dayEvents[date]) return;
    setLoadingEvents(date);
    const location = getLocationForDate(date);
    try {
      const r = await authFetch("/api/ai/local-events", { method: "POST", body: JSON.stringify({ location, date }) });
      const data = await r.json();
      setDayEvents(prev => ({ ...prev, [date]: data }));
    } catch {} finally { setLoadingEvents(null); }
  };

  // Fetch hourly weather — evening city always, morning city too on
  // travel days so the inline expansion splits the same way the chip
  // does.
  const fetchHourlyWeather = async (date) => {
    const morning = getMorningLocationForDate(date);
    if (!hourlyWeather[date]) {
      const location = getLocationForDate(date);
      try {
        const r = await authFetch(`/api/weather/hourly?location=${encodeURIComponent(location)}&date=${date}`);
        const data = await r.json();
        if (!data.error) setHourlyWeather(prev => ({ ...prev, [date]: data }));
      } catch {}
    }
    if (morning && !morningHourlyWeather[date]) {
      try {
        const r = await authFetch(`/api/weather/hourly?location=${encodeURIComponent(morning)}&date=${date}`);
        const data = await r.json();
        if (!data.error) setMorningHourlyWeather(prev => ({ ...prev, [date]: { location: morning, ...data } }));
      } catch {}
    }
  };

  const toggleWeatherExpand = (date) => {
    if (expandedWeather === date) {
      setExpandedWeather(null);
    } else {
      setExpandedWeather(date);
      fetchHourlyWeather(date);
    }
  };

  // Save edited segment. The server's PUT response IS the updated row,
  // so we apply it surgically to the existing `timeline` state via
  // `patchSegmentInTimeline` instead of refetching the whole trip. The
  // previous refetch tore down every day card and the resulting layout
  // churn raced the scroll-restore code — a save on day N would bounce
  // the user several days forward. Surgical replace = no DOM teardown =
  // the user stays exactly where they were.
  const saveSegmentEdit = async (segId) => {
    try {
      const { passengers_text, ...rest } = editSegData;
      const payload = { ...rest };
      if (passengers_text !== undefined) {
        payload.passengers = passengers_text.split(",").map(s => s.trim()).filter(Boolean);
      }
      const r = await authFetch(`/api/segments/${segId}`, { method: "PUT", body: JSON.stringify(payload) });
      if (r && r.ok && patchSegmentInTimeline) {
        const updated = await r.json();
        patchSegmentInTimeline(updated);
      } else if (onRefreshTrip) {
        // No patcher available (shouldn't happen) — fall back to refetch.
        onRefreshTrip();
      }
      setEditingSeg(null);
      setEditSegData({});
      // Collapse the segment back to its compact card after save —
      // staying expanded leaves the user staring at the same details
      // panel they just closed the edit form inside.
      setExpandedSeg(null);
    } catch {}
  };

  const startEditSeg = (seg) => {
    setEditingSeg(seg.id);
    setEditSegData({
      title: seg.title || "", provider: seg.provider || "", type: seg.type || "",
      start_datetime: seg.start_datetime || "", end_datetime: seg.end_datetime || "",
      start_location: seg.start_location || "", end_location: seg.end_location || "",
      confirmation_code: seg.confirmation_code || "", notes: seg.notes || "",
      room_type: seg.room_type || "", flight_number: seg.flight_number || "",
      vehicle_type: seg.vehicle_type || "", pickup_location: seg.pickup_location || "",
      dropoff_location: seg.dropoff_location || "", check_in_time: seg.check_in_time || "",
      check_out_time: seg.check_out_time || "", seat: seg.seat || "",
      contact_name: seg.contact_name || "", contact_phone: seg.contact_phone || "",
      contact_email: seg.contact_email || "", contact_whatsapp: seg.contact_whatsapp || "",
      status: seg.status || "confirmed",
      passengers_text: (() => { try { return JSON.parse(seg.passengers || "[]").join(", "); } catch { return seg.passengers || ""; } })(),
    });
  };

  // Load day notes when trip changes
  useEffect(() => {
    if (!selectedTrip || !authFetch) return;
    authFetch(`/api/trips/${selectedTrip}/day-notes`).then(r => r.json()).then(setDayNotes).catch(() => {});
  }, [selectedTrip, authFetch]);

  const handleDragStart = (e, segId, date) => {
    setDragItem(segId);
    setDragDate(date);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, segId, date) => {
    e.preventDefault();
    if (date !== dragDate) return; // Only allow within same day
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(segId);
  };

  const handleDrop = async (e, daySegments, date) => {
    e.preventDefault();
    if (date !== dragDate || dragItem === null) { setDragItem(null); setDragOverItem(null); setDragDate(null); return; }
    const ids = daySegments.map(s => s.id);
    const fromIdx = ids.indexOf(dragItem);
    const toIdx = ids.indexOf(dragOverItem);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) { setDragItem(null); setDragOverItem(null); setDragDate(null); return; }
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragItem);
    setDragItem(null);
    setDragOverItem(null);
    setDragDate(null);
    if (onReorder) await onReorder(ids);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverItem(null);
    setDragDate(null);
  };

  const saveDayNote = async (date) => {
    try {
      await authFetch(`/api/trips/${selectedTrip}/day-note`, {
        method: "PUT", body: JSON.stringify({ date, note: noteText }),
      });
      setDayNotes(prev => {
        const next = { ...prev };
        if (noteText.trim()) next[date] = noteText.trim();
        else delete next[date];
        return next;
      });
    } catch {}
    setEditingNoteDate(null);
    setNoteText("");
  };

  const fetchDaySuggestion = async (date, interest) => {
    setSuggestingDate(date);
    setSuggestInterest(prev => ({ ...prev, [date]: false }));
    try {
      // Gather segments from nearby days (±3 days) for context
      const nearbyDays = timeline.filter(d => {
        const diff = Math.abs((new Date(d.date) - new Date(date)) / 86400000);
        return diff <= 3;
      });
      const nearbySegs = nearbyDays.flatMap(d => d.segments);
      // Find the specific hotel covering this date
      const allSegs = tripData?.segments || [];
      const coveringHotel = allSegs.filter(s => ACCOMMODATION_TYPES.has(s.type)).find(h => {
        const checkIn = h.start_datetime?.split("T")[0];
        const checkOut = h.end_datetime?.split("T")[0];
        return checkIn && checkOut && date >= checkIn && date < checkOut;
      });
      // Find excursions already done at this hotel (by provider match or same location)
      const hotelExcursions = coveringHotel ? allSegs.filter(s =>
        s.type === "excursion" && (
          (s.provider && coveringHotel.title && s.provider.toLowerCase().includes(coveringHotel.title.split(" ")[0].toLowerCase())) ||
          (s.start_location && coveringHotel.start_location && s.start_location === coveringHotel.start_location)
        )
      ).map(s => s.title) : [];
      const r = await authFetch("/api/ai/day-suggestion", {
        method: "POST",
        body: JSON.stringify({ trip_id: selectedTrip, date, destination: getLocationForDate(date) || tripData.destination || "", existing_segments: nearbySegs, covering_hotel: coveringHotel || null, hotel_excursions_done: hotelExcursions, previous_suggestion: (typeof daySuggestions[date] === "object" ? daySuggestions[date]?.text : daySuggestions[date]) || "", interest: interest || "" }),
      });
      const data = await r.json();
      setDaySuggestions(prev => ({ ...prev, [date]: { text: data.suggestion || data.error || "No suggestion.", places: data.places || [] } }));
      setExpandedPlace(null);
    } catch (err) {
      setDaySuggestions(prev => ({ ...prev, [date]: { text: "Failed to get suggestion.", places: [] } }));
    } finally { setSuggestingDate(null); }
  };

  const fetchPlaceDetail = async (place, city, date) => {
    setExpandedPlace({ ...place, date });
    setLoadingPlaceDetail(true);
    try {
      // Resolve the hotel covering this date so Claude can position the
      // place relative to it (e.g. "10-min walk from your hotel").
      const allSegs = tripData?.segments || [];
      const coveringHotel = allSegs.filter(s => ACCOMMODATION_TYPES.has(s.type)).find(h => {
        const checkIn = h.start_datetime?.split("T")[0];
        const checkOut = h.end_datetime?.split("T")[0];
        return checkIn && checkOut && date && date >= checkIn && date < checkOut;
      });
      const r = await authFetch("/api/ai/place-detail", {
        method: "POST",
        body: JSON.stringify({
          name: place.name,
          city: city || "",
          date: date || "",
          hotel_name: coveringHotel?.title || "",
          hotel_location: coveringHotel?.start_location || "",
        }),
      });
      const data = await r.json();
      setExpandedPlace(prev => prev && prev.name === place.name ? { ...prev, location: data.location, detail: data.detail, hours: data.hours, tip: data.tip } : prev);
    } catch {
      setExpandedPlace(prev => prev && prev.name === place.name ? { ...prev, detail: "Could not load details." } : prev);
    } finally { setLoadingPlaceDetail(false); }
  };

  const renderSuggestionWithLinks = (suggestion, places, onPlaceClick) => {
    if (!places || places.length === 0) return suggestion;
    const text = suggestion;
    const parts = [];
    let lastIdx = 0;
    const matches = [];
    for (const place of places) {
      const idx = text.toLowerCase().indexOf(place.name.toLowerCase());
      if (idx >= 0) matches.push({ ...place, idx, len: place.name.length });
    }
    matches.sort((a, b) => a.idx - b.idx);
    // Remove overlapping matches
    const filtered = [];
    let prevEnd = 0;
    for (const m of matches) {
      if (m.idx >= prevEnd) { filtered.push(m); prevEnd = m.idx + m.len; }
    }
    for (const m of filtered) {
      if (m.idx > lastIdx) parts.push(text.slice(lastIdx, m.idx));
      parts.push({ name: text.slice(m.idx, m.idx + m.len), place: m });
      lastIdx = m.idx + m.len;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts.map((p, i) => typeof p === "string" ? p : (
      <span key={i} onClick={(e) => { e.stopPropagation(); onPlaceClick(p.place); }}
        style={{ color: "var(--green)", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>
        {p.name}
      </span>
    ));
  };

  if (!tripData) return (
    <div className="empty-state">
      <h2>Select a trip</h2>
      <p>Choose a trip from "My Trips" to see the timeline.</p>
    </div>
  );

  return (
    <div>
      <div className="timeline-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <span style={{ cursor: "pointer", color: "var(--text-dim)", fontSize: 14 }} onClick={onBack}>← My Trips</span>
        </div>
        <div className="timeline-header-row">
          <div className="timeline-header-title">
            <h2 style={{ fontSize: 24, marginBottom: 4, wordBreak: "break-word" }}>{tripData.title}</h2>
            <div style={{ fontSize: 14, color: "var(--text-dim)" }}>{tripData.destination}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
              {tripData.start_date ? formatDate(tripData.start_date) : "TBD"} — {tripData.end_date ? formatDate(tripData.end_date) : "TBD"}
              {tripData.segments && <span style={{ marginLeft: 12 }}>{tripData.segments.length} items</span>}
            </div>
          </div>
          <div className="timeline-header-actions">
            {countdown !== null && countdown > 0 && (
              <div className="timeline-countdown">
                <div className="countdown">{countdown}</div>
                <div className="countdown-label">days to go</div>
              </div>
            )}
            {onEditTrip && <span onClick={onEditTrip} title="Edit trip" style={{ cursor: "pointer", fontSize: 16, color: "var(--text-dim)", opacity: 0.7, transition: "opacity 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}>⚙</span>}
            {onInvite && <button className="btn btn-secondary btn-sm" onClick={onInvite}>Share</button>}
            <div style={{ position: "relative", display: "inline-block" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowExportMenu(o => !o)}>📄 Export</button>
              {showExportMenu && (() => {
                const menuItem = (label, onClick) => (
                  <button onClick={onClick}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 10px", fontSize: 13, color: "var(--text)", cursor: "pointer", borderRadius: 4 }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--sidebar)"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    {label}
                  </button>
                );
                return (
                <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: 6, minWidth: 260, zIndex: 50, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                  <div style={{ fontSize: 10, fontFamily: "var(--ff-mono)", color: "var(--text-muted)", padding: "4px 10px", letterSpacing: "0.06em" }}>YOUR-EVENTS ONLY</div>
                  {menuItem("📋 Whole itinerary", () => { setShowExportMenu(false); exportItineraryPdf(null); })}
                  {travellers.map(t => (
                    <React.Fragment key={t.name}>{menuItem(`👤 ${t.name}'s itinerary`, () => { setShowExportMenu(false); exportItineraryPdf(t.name); })}</React.Fragment>
                  ))}
                  <div style={{ fontSize: 10, fontFamily: "var(--ff-mono)", color: "var(--text-muted)", padding: "8px 10px 4px", letterSpacing: "0.06em", borderTop: "1px solid var(--border)", marginTop: 4 }}>WITH ALL EVENTS (TV-ONLY INCLUDED)</div>
                  {menuItem("📋 Whole itinerary + all events", () => { setShowExportMenu(false); exportItineraryPdf(null, { includeTvEvents: true }); })}
                  {travellers.map(t => (
                    <React.Fragment key={t.name + "-full"}>{menuItem(`👤 ${t.name}'s itinerary + all events`, () => { setShowExportMenu(false); exportItineraryPdf(t.name, { includeTvEvents: true }); })}</React.Fragment>
                  ))}
                  <div style={{ fontSize: 10, fontFamily: "var(--ff-mono)", color: "var(--text-muted)", padding: "8px 10px 4px", letterSpacing: "0.06em", borderTop: "1px solid var(--border)", marginTop: 4 }}>CALENDAR (.ICS)</div>
                  {menuItem("🔗 Copy subscribe URL (auto-refreshes)", async () => {
                    setShowExportMenu(false);
                    try {
                      const r = await authFetch("/api/calendar-token", { method: "POST" });
                      if (r && r.ok) {
                        const { token } = await r.json();
                        const url = `${window.location.origin}/ical/${token}/${tripData.id}.ics`;
                        try { await navigator.clipboard.writeText(url); window.alert(`Subscribe URL copied to clipboard.\n\nPaste into:\n• Apple Calendar → File → New Calendar Subscription\n• Google Calendar → + → From URL\n\nThe calendar auto-refreshes as you edit segments.\n\n${url}`); }
                        catch { window.prompt("Subscribe URL — copy into Apple/Google Calendar:", url); }
                      }
                    } catch {}
                  })}
                  {menuItem("📅 Whole itinerary → .ics", async () => {
                    setShowExportMenu(false);
                    // Use authFetch to download since the endpoint is auth-gated
                    const r = await authFetch(`/api/trips/${tripData.id}/calendar.ics`);
                    if (r && r.ok) {
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = `${tripData.title.replace(/[^a-z0-9]+/gi, "_")}.ics`;
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }
                  })}
                  {travellers.map(t => (
                    <React.Fragment key={t.name + "-ics"}>{menuItem(`📅 ${t.name}'s itinerary → .ics`, async () => {
                      setShowExportMenu(false);
                      const r = await authFetch(`/api/trips/${tripData.id}/calendar.ics?traveller=${encodeURIComponent(t.name)}`);
                      if (r && r.ok) {
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = url; a.download = `${tripData.title.replace(/[^a-z0-9]+/gi, "_")}_${t.name.split(" ")[0]}.ics`;
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      }
                    })}</React.Fragment>
                  ))}
                </div>
                );
              })()}
            </div>
            {onAddSegment && <button className="btn btn-primary btn-sm" onClick={onAddSegment}>+ Add</button>}
            {!canEdit && canAnnotate && <span className="badge" style={{ padding: "4px 10px", background: "rgba(179,136,255,0.15)", color: "var(--purple)" }}>Editor — personal notes only</span>}
            {!canAnnotate && <span className="badge badge-tentative" style={{ padding: "4px 10px" }}>View only</span>}
          </div>
        </div>
        {/* Floating Add Item button — follows the user as they scroll
            so they don't have to jump to the top to add a segment for
            the day they're currently looking at. The header button
            above stays for parity / discoverability. */}
        {onAddSegment && (
          <button
            onClick={onAddSegment}
            aria-label="Add item to the day you're viewing"
            title="Add item"
            style={{
              position: "fixed",
              right: 20,
              bottom: "calc(env(safe-area-inset-bottom) + 20px)",
              width: 56, height: 56, borderRadius: 28,
              border: "none",
              background: "var(--green)",
              color: "#0a0a14",
              fontSize: 28, fontWeight: 700, lineHeight: "56px",
              boxShadow: "0 6px 18px rgba(0,0,0,0.4), 0 2px 6px rgba(0,230,118,0.5)",
              cursor: "pointer", zIndex: 900,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >+</button>
        )}
      </div>

      {/* Per-traveller filter chips — only show when there's more than one named traveller */}
      {travellers.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12, padding: "8px 12px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10 }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)", letterSpacing: "0.06em" }}>SHOW</span>
          <button onClick={showAllTravellers} style={{
            background: selectedTraveller === null ? "var(--green)" : "transparent",
            color: selectedTraveller === null ? "var(--bg)" : "var(--text-dim)",
            border: `1px solid ${selectedTraveller === null ? "var(--green)" : "var(--border)"}`,
            borderRadius: 16, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "var(--ff-mono)",
          }}>Everyone</button>
          {travellers.map(t => {
            const active = selectedTraveller === t.name;
            return (
              <button key={t.name} onClick={() => toggleTraveller(t.name)}
                title={`${t.first_flight.split("T")[0]} → ${t.last_flight.split("T")[0]} · ${t.segment_ids?.length || 0} segments`}
                style={{
                  background: active ? "var(--green)" : "transparent",
                  color: active ? "var(--bg)" : "var(--text-dim)",
                  border: `1px solid ${active ? "var(--green)" : "var(--border)"}`,
                  borderRadius: 16, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "var(--ff-mono)",
                }}>{t.name.split(" ")[0]}</button>
            );
          })}
        </div>
      )}

      {/* Members */}
      {tripData.members?.length > 0 && (
        <div style={{ marginBottom: 16, fontSize: 12, color: "var(--text-dim)" }}>
          Shared with: {tripData.members.map(m => m.name || m.email).join(", ")}
        </div>
      )}

      {/* Itinerary — stop-by-stop with driving legs */}
      <ItineraryPanel tripId={tripData.id} authFetch={authFetch} canEdit={canEdit} onRefresh={onRefreshTrip} />

      {/* Weather strip — next 14 days at the upcoming destination */}
      <WeatherStripPanel tripData={tripData} timeline={timeline} authFetch={authFetch} />

      {/* Per-country reference cards (plug, voltage, emergency, etc.) */}
      <CountryCardsPanel tripData={tripData} authFetch={authFetch} />

      {/* Events — venue & user-local times */}
      <EventsPanel tripId={tripData.id} authFetch={authFetch} />

      {/* Visa & entry requirements */}
      <VisaPanel tripId={tripData.id} authFetch={authFetch} />

      {/* Timeline */}
      {timeline.map((day, dayIdx) => {
        // When a traveller filter is active, hide segments not in their set and
        // skip the day entirely if nothing's left to show.
        if (selectedTraveller) {
          const filtered = (day.segments || []).filter(segVisibleToSelection);
          if (filtered.length === 0) return null;
          day = { ...day, segments: filtered };
        }
        // Hide auto-generated "Transfer needs to be arranged" rows on any date
        // a multi-day hire car covers — alerts are already suppressed, this
        // just stops them visually cluttering the timeline.
        {
          const tripRentals = (tripData.segments || []).filter(s =>
            s.start_datetime && s.end_datetime &&
            s.start_datetime.split("T")[0] !== s.end_datetime.split("T")[0] &&
            (s.type === "car_rental" || (s.type === "transport" && (s.vehicle_type || "").toLowerCase().includes("car")))
          );
          const covers = (date) => tripRentals.some(r => {
            const s = r.start_datetime.split("T")[0];
            const e = r.end_datetime.split("T")[0];
            return date >= s && date <= e;
          });
          if (covers(day.date)) {
            const cleaned = (day.segments || []).filter(s =>
              !(s.type === "transport" && /needs.*confirmation|transfer.*to.*be.*arranged/i.test(s.notes || ""))
            );
            if (cleaned.length !== (day.segments || []).length) {
              day = { ...day, segments: cleaned };
            }
          }
        }
        const d = new Date(day.date + "T00:00:00");
        const dayName = d.toLocaleDateString("en-AU", { weekday: "long" });
        const dayLabel = d.toLocaleDateString("en-AU", { day: "numeric", month: "long" });
        const isToday = day.date === localDateKey();
        const run = dateToRun[day.date];
        const runExpanded = run && expandedFreeRuns[run.start];
        // Collapsed run: render the rollup card on the start date and skip the rest
        if (run && !runExpanded) {
          if (day.date !== run.start) return null;
          const startD = new Date(run.start + "T00:00:00");
          const endD = new Date(run.end + "T00:00:00");
          const fmt = (dt) => dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
          return (
            <div key={`run-${run.start}`} className="timeline-day"
              style={{ cursor: "pointer" }}
              onClick={() => setExpandedFreeRuns(prev => ({ ...prev, [run.start]: true }))}>
              <div className="timeline-date" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", transition: "transform 0.2s", display: "inline-block" }}>▶</span>
                  <span className="day-name">{fmt(startD)}</span>
                  <span style={{ color: "var(--text-dim)" }}>→</span>
                  <span className="day-name">{fmt(endD)}</span>
                </div>
                <span style={{ fontSize: 12, color: "var(--green)", fontStyle: "italic" }}>{run.dates.length} free days</span>
              </div>
            </div>
          );
        }
        // Arrival = hotel check-in today OR transport arrival today.
        // Heuristic only — over-fires are fine, the user can dismiss; the
        // higher-cost mistake is missing a legitimate arrival.
        const isArrivalDay = (day.segments || []).some((s) => {
          const startsToday = (s.start_datetime || "").startsWith(day.date);
          const endsToday = (s.end_datetime || "").startsWith(day.date);
          return (s.type === "hotel" && startsToday) ||
            (["flight", "ferry", "transport", "train"].includes(s.type) && endsToday);
        });
        // Phase 5b — earliest outbound flight/ferry departing later
        // today. Surfaces as "⏰ Outbound at HH:MM" on the arrival
        // banner so the user knows the day's time-constrained.
        let departureTimeToday = null;
        if (isToday) {
          const nowMs = Date.now();
          const outbound = (day.segments || [])
            .filter((s) => ["flight", "ferry"].includes(s.type) && (s.start_datetime || "").startsWith(day.date))
            .map((s) => ({ s, ms: new Date(s.start_datetime).getTime() }))
            .filter(({ ms }) => Number.isFinite(ms) && ms > nowMs)
            .sort((a, b) => a.ms - b.ms)[0];
          if (outbound) departureTimeToday = outbound.s.start_datetime.slice(11, 16);
        }
        return (
          <div key={day.date} id={`day-${day.date}`} className="timeline-day">
            {/* Local-distinctives push for arrival on today's date.
                Only fires on today AND only when today is an arrival day —
                we don't back-fill banners for past arrivals or pre-show
                future ones. */}
            {isToday && isArrivalDay && tripData?.today_city && tripData?.today_country_code && (
              <LocalDistinctivesBanner
                city={tripData.today_city}
                country={tripData.today_country_code}
                dateKey={day.date}
                authFetch={authFetch}
                rainChance={dayWeather[day.date]?.rain_chance ?? null}
                departureTime={departureTimeToday}
              />
            )}
            {/* Phase 4b — early-preview banner. Fires on today's day
                card when an upcoming future_arrival is within the
                MAX lead-time window (7 days) AND we're not already
                showing an arrival-day banner for today. The banner
                itself further filters to items where actionLeadDays
                ≥ daysUntil — only surfacing the truly time-sensitive
                ones (cycling rentals, event tickets, etc.) */}
            {isToday && !isArrivalDay && (() => {
              const upcoming = (tripData?.future_arrivals || []).find((a) => {
                const today = new Date(localDateKey() + "T00:00:00Z").getTime();
                const arr = new Date(a.date + "T00:00:00Z").getTime();
                const days = Math.round((arr - today) / 86400000);
                return days > 0 && days <= 7;
              });
              if (!upcoming) return null;
              const today = new Date(localDateKey() + "T00:00:00Z").getTime();
              const arr = new Date(upcoming.date + "T00:00:00Z").getTime();
              const daysUntil = Math.round((arr - today) / 86400000);
              return (
                <LocalDistinctivesBanner
                  city={upcoming.city}
                  country={upcoming.country_code}
                  dateKey={upcoming.date}
                  authFetch={authFetch}
                  mode="preview"
                  daysUntil={daysUntil}
                  rainChance={dayWeather[upcoming.date]?.rain_chance ?? null}
                />
              );
            })()}
            {/* Collapse-run affordance at the start of an expanded run */}
            {run && runExpanded && day.date === run.start && (
              <div style={{ padding: "6px 16px", fontSize: 11, color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => setExpandedFreeRuns(prev => { const n = { ...prev }; delete n[run.start]; return n; })}>
                <span style={{ display: "inline-block", transform: "rotate(90deg)" }}>▶</span>
                <span>Collapse {run.dates.length} free days</span>
              </div>
            )}
            <div className="timeline-date" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="day-name" style={isToday ? { background: "var(--green)", color: "#111", padding: "2px 8px", borderRadius: 4 } : {}}>{dayName}</span>
                <span className="day-num">{dayLabel}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Weather — clickable for hourly. Splits across morning
                    → evening cities on travel days. */}
                {dayWeather[day.date] && !dayWeather[day.date].error && (() => {
                  const evening = dayWeather[day.date];
                  const morning = morningWeather[day.date];
                  const shortCity = (s) => {
                    if (!s) return "";
                    for (const chunk of s.split(",").map(c => c.trim())) {
                      const alpha = chunk.replace(/[^a-zA-Z \-]/g, "").trim();
                      if (alpha.length >= 3) return (alpha.split("-")[0] || alpha).trim();
                    }
                    return s.slice(0, 12);
                  };
                  const half = (w, cityLabel) => (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                      {cityLabel && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-dim)" }}>{cityLabel}</span>}
                      <span style={{ fontSize: 11 }}>{w.icon}</span>
                      <span style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--green)" }}>{w.high}°/{w.low}°</span>
                      {w.rain_chance > 20 && <span style={{ fontSize: 9, color: "var(--blue)" }}>💧{w.rain_chance}%</span>}
                      {w.source === "historical" && <span style={{ fontSize: 8, color: "var(--amber)" }}>avg</span>}
                    </span>
                  );
                  if (morning && morning.weather && !morning.weather.error) {
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#fff", background: "var(--sidebar)", padding: "3px 8px", borderRadius: 6, cursor: "pointer" }}
                        onClick={(e) => { e.stopPropagation(); toggleWeatherExpand(day.date); }}
                        title="Travel day — morning city → evening city">
                        {half(morning.weather, shortCity(morning.location))}
                        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>→</span>
                        {half(evening, shortCity(getLocationForDate(day.date)))}
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#fff", background: "var(--sidebar)", padding: "3px 8px", borderRadius: 6, cursor: "pointer" }}
                      onClick={(e) => { e.stopPropagation(); toggleWeatherExpand(day.date); }}
                      title={evening.source === "historical" ? "Based on last year's weather for this date" : "Live forecast — click for hourly details"}>
                      <span>{evening.icon}</span>
                      <span style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--green)" }}>{evening.high}°/{evening.low}°</span>
                      {evening.rain_chance > 20 && <span style={{ fontSize: 10, color: "var(--blue)" }}>💧{evening.rain_chance}%</span>}
                      {evening.source === "historical" && <span style={{ fontSize: 9, color: "var(--amber)", marginLeft: 2 }}>avg</span>}
                    </div>
                  );
                })()}
              </div>
            </div>
            {/* Hourly weather expanded — splits into morning + evening
                strips on travel days so each city's hourly is labelled. */}
            {expandedWeather === day.date && hourlyWeather[day.date]?.hourly && (() => {
              const shortCity = (s) => {
                if (!s) return "";
                for (const chunk of s.split(",").map(c => c.trim())) {
                  const alpha = chunk.replace(/[^a-zA-Z \-]/g, "").trim();
                  if (alpha.length >= 3) return (alpha.split("-")[0] || alpha).trim();
                }
                return s.slice(0, 12);
              };
              const renderStrip = (hours) => (
                <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
                  {hours.map((h, hi) => (
                    <div key={hi} style={{ minWidth: 70, padding: "8px 10px", background: "var(--sidebar)", borderRadius: 8, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--green)", fontFamily: "DM Mono", marginBottom: 4 }}>{h.hour}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#fff" }}>{h.temp}°</div>
                      <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>💨 {h.wind} km/h</div>
                      {h.precip > 0 && <div style={{ fontSize: 10, color: "var(--blue)", marginTop: 2 }}>💧 {h.precip}mm</div>}
                    </div>
                  ))}
                </div>
              );
              const m = morningHourlyWeather[day.date];
              if (m && m.hourly?.length) {
                return (
                  <div style={{ padding: "8px 16px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--green)", marginBottom: 4 }}>{shortCity(m.location)}</div>
                      {renderStrip(m.hourly)}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--green)", marginBottom: 4 }}>{shortCity(getLocationForDate(day.date))}</div>
                      {renderStrip(hourlyWeather[day.date].hourly)}
                    </div>
                  </div>
                );
              }
              return (
                <div style={{ padding: "8px 16px 10px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--green)", marginBottom: 4 }}>{shortCity(getLocationForDate(day.date))}</div>
                  {renderStrip(hourlyWeather[day.date].hourly)}
                </div>
              );
            })()}
            {expandedWeather === day.date && !hourlyWeather[day.date]?.hourly && (
              <div style={{ padding: "6px 16px", fontSize: 12, color: "var(--text-dim)" }}>Loading hourly weather...</div>
            )}
            {/* Events for this day */}
            {dayEvents[day.date]?.events?.length > 0 && (
              <div style={{ padding: "6px 16px 10px", fontSize: 12 }}>
                <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Tap any event for more details</div>
                {dayEvents[day.date].events.map((ev, ei) => {
                  const eventKey = `${day.date}-${ei}`;
                  const isExpanded = expandedEvent === eventKey;
                  return (
                    <div key={ei}>
                      <div style={{ display: "flex", gap: 8, padding: "3px 0", color: "var(--text-dim)", cursor: "pointer" }}
                        onClick={() => setExpandedEvent(isExpanded ? null : eventKey)}>
                        <span style={{ color: "var(--amber)" }}>{ev.type === "festival" ? "🎉" : ev.type === "sport" ? "⚽" : ev.type === "market" ? "🛍" : ev.type === "cultural" ? "🎭" : "📌"}</span>
                        <span><strong>{ev.title}</strong> — {ev.description}{ev.location ? ` (${ev.location})` : ""}{ev.time ? ` · ${ev.time}` : ""}</span>
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          await authFetch(`/api/trips/${selectedTrip}/segments`, {
                            method: "POST",
                            body: JSON.stringify({ type: "event", title: ev.title, start_datetime: `${day.date}T${ev.time?.match(/\d{2}:\d{2}/)?.[0] || "09:00"}`, start_location: ev.location || "", notes: `${ev.description || ""}\n${ev.detail || ""}`.trim(), status: "confirmed" }),
                          });
                          if (onRefreshTrip) onRefreshTrip();
                        }} style={{ background: "none", border: "none", color: "var(--green)", cursor: "pointer", fontSize: 14, padding: "0 4px", flexShrink: 0 }}
                          title="Save to itinerary">+</button>
                      </div>
                      {isExpanded && ev.detail && (
                        <div style={{ marginLeft: 28, padding: "4px 0 6px 10px", borderLeft: "2px solid var(--green)", fontSize: 11, color: "var(--text-dim)", lineHeight: 1.5 }}>
                          {ev.detail}
                        </div>
                      )}
                    </div>
                  );
                })}
                {dayEvents[day.date].tip && <div style={{ marginTop: 4, fontSize: 11, color: "var(--green)", fontStyle: "italic" }}>{dayEvents[day.date].tip}</div>}
              </div>
            )}
            {/* AI suggestion for this day */}
            {daySuggestions[day.date] && (
              <div className="ai-suggestion">
                <span style={{ fontSize: 11, color: "var(--green)", textTransform: "uppercase", letterSpacing: 0.5 }}>AI Suggestion</span>
                {(() => {
                  const sug = daySuggestions[day.date];
                  const text = typeof sug === "string" ? sug : sug?.text || "";
                  const places = typeof sug === "object" ? sug?.places || [] : [];
                  const city = getLocationForDate(day.date) || tripData?.destination || "";
                  if (places.length > 0) {
                    return (<>
                      {text && <div style={{ marginTop: 4, fontSize: 13, color: "var(--text-dim)" }}>{text}</div>}
                      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                        {places.map((place, pi) => (
                          <div key={pi} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", background: "rgba(0,230,118,0.06)", borderRadius: 8, borderLeft: "2px solid var(--green)" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, cursor: "pointer", color: "var(--green)" }}
                                onClick={() => {
                                  if (expandedPlace && expandedPlace.name === place.name && expandedPlace.date === day.date) {
                                    setExpandedPlace(null);
                                  } else {
                                    fetchPlaceDetail(place, city, day.date);
                                  }
                                }}>
                                {place.name}
                              </div>
                              {place.brief && <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>{place.brief}</div>}
                              {place.duration && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{place.duration}</div>}
                            </div>
                            <button onPointerDown={async (e) => {
                              e.stopPropagation(); e.preventDefault();
                              await authFetch(`/api/trips/${selectedTrip}/segments`, {
                                method: "POST",
                                body: JSON.stringify({ type: "excursion", title: place.name, start_datetime: `${day.date}T09:00`, notes: place.brief || "", status: "suggested" }),
                              });
                              if (onRefreshTrip) onRefreshTrip();
                            }} onClick={(e) => e.stopPropagation()} style={{ background: "none", border: "none", color: "var(--green)", cursor: "pointer", fontSize: 18, padding: "8px 12px", minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                              title="Save to itinerary">+</button>
                          </div>
                        ))}
                      </div>
                      <button onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); setDaySuggestions(prev => { const next = { ...prev }; delete next[day.date]; return next; }); }}
                        onClick={(e) => e.stopPropagation()}
                        className="btn btn-secondary btn-sm" style={{ marginTop: 8, fontSize: 11 }}>Done</button>
                    </>);
                  }
                  return (<>
                    <div style={{ marginTop: 4 }}>{renderSuggestionWithLinks(text, places, (place) => {
                      if (expandedPlace && expandedPlace.name === place.name && expandedPlace.date === day.date) {
                        setExpandedPlace(null);
                      } else {
                        fetchPlaceDetail(place, city, day.date);
                      }
                    })}</div>
                    <button className="btn btn-secondary btn-sm" style={{ marginTop: 6, fontSize: 11 }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const title = text.slice(0, 50) + (text.length > 50 ? "..." : "");
                        await authFetch(`/api/trips/${selectedTrip}/segments`, {
                          method: "POST",
                          body: JSON.stringify({ type: "excursion", title, start_datetime: `${day.date}T09:00`, notes: text, status: "suggested" }),
                        });
                        if (onRefreshTrip) onRefreshTrip();
                      }}>
                      Save as Excursion
                    </button>
                  </>);
                })()}
                {expandedPlace && expandedPlace.date === day.date && (
                  <div style={{ marginTop: 8, padding: "10px 12px", background: "rgba(0,230,118,0.06)", borderRadius: 8, borderLeft: "3px solid var(--green)" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{expandedPlace.name}</div>
                    {expandedPlace.brief && <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{expandedPlace.brief}</div>}
                    {loadingPlaceDetail && !expandedPlace.detail && <div style={{ fontSize: 12, opacity: 0.5 }}>Loading details...</div>}
                    {expandedPlace.detail && (
                      <>
                        {expandedPlace.location && <div style={{ fontSize: 12, color: "var(--blue)", marginBottom: 4 }}>📍 {expandedPlace.location}</div>}
                        <div style={{ fontSize: 13, marginBottom: 4 }}>{expandedPlace.detail}</div>
                        {expandedPlace.hours && <div style={{ fontSize: 11, opacity: 0.6 }}>Hours: {expandedPlace.hours}</div>}
                        {expandedPlace.tip && <div style={{ fontSize: 12, color: "var(--green)", fontStyle: "italic", marginTop: 4 }}>{expandedPlace.tip}</div>}
                      </>
                    )}
                    <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                      {!expandedPlace.detail && !loadingPlaceDetail && (
                        <button onClick={(e) => { e.stopPropagation(); fetchPlaceDetail(expandedPlace, getLocationForDate(day.date) || tripData?.destination || "", day.date); }}
                          style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "1px solid var(--green)", background: "transparent", color: "var(--green)", cursor: "pointer" }}>
                          Tell me more
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setExpandedPlace(null); }}
                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Segments — WC matches roll up into a single card per day */}
            {day.segments.length > 0 && (() => {
              const wcSegs = day.segments.filter(isWcMatch);
              const otherSegs = day.segments.filter(s => !isWcMatch(s));
              const wcOpen = !!expandedWcDays[day.date];
              const showRollup = wcSegs.length > 1;
              const visibleSegs = showRollup && !wcOpen ? otherSegs : day.segments;
              return (
                <>
                  {showRollup && (
                    <div className="timeline-segment event"
                      onClick={() => setExpandedWcDays(p => ({ ...p, [day.date]: !p[day.date] }))}
                      style={{ cursor: "pointer", borderLeftColor: "var(--amber)" }}>
                      <div className="seg-icon">🏆</div>
                      <div className="seg-body">
                        <div className="seg-title">
                          {wcOpen ? "▾ " : "▸ "}{wcSegs.length} FIFA World Cup matches
                        </div>
                        <div className="seg-detail" style={{ color: "var(--text-muted)" }}>
                          Click to {wcOpen ? "hide" : "show"} the day's matches
                        </div>
                      </div>
                    </div>
                  )}
                  {visibleSegs.map(seg => (
                <div key={`${seg.id}${seg._view ? "-" + seg._view : ""}`} id={`seg-${seg.id}${seg._view ? "-" + seg._view : ""}`}>
                  <div className={`timeline-segment ${seg.type}${dragItem === seg.id ? " dragging" : ""}${dragOverItem === seg.id && dragDate === day.date ? " drag-over" : ""}`}
                    onDragOver={(e) => handleDragOver(e, seg.id, day.date)}
                    onDrop={(e) => handleDrop(e, day.segments, day.date)}
                    onClick={() => {
                      const willExpand = expandedSeg !== seg.id;
                      setExpandedSeg(willExpand ? seg.id : null);
                      // Hotel overview: prefer the value already on the segment (auto-generated
                      // when the hotel was added). Only fetch as a fallback for legacy segments
                      // that don't yet have one.
                      if (willExpand && seg.type === "hotel" && !seg.overview && !hotelOverviews[seg.id]) {
                        setHotelOverviews(prev => ({ ...prev, [seg.id]: { loading: true } }));
                        authFetch("/api/ai/hotel-overview", {
                          method: "POST",
                          body: JSON.stringify({ segment_id: seg.id }),
                        })
                          .then(r => r.json())
                          .then(data => setHotelOverviews(prev => ({ ...prev, [seg.id]: { text: data.overview || "", error: data.error } })))
                          .catch(() => setHotelOverviews(prev => ({ ...prev, [seg.id]: { error: "Failed to load overview" } })));
                      }
                    }}>
                    {canEdit && <>
                      {/* Desktop: drag handle */}
                      <div className="drag-handle desktop-only" draggable="true"
                        onDragStart={(e) => { e.stopPropagation(); handleDragStart(e, seg.id, day.date); }}
                        onDragEnd={handleDragEnd}
                        title="Drag to reorder">⠿</div>
                      {/* Mobile: up/down buttons */}
                      <div className="reorder-buttons mobile-only" style={{ display: "flex", flexDirection: "column", gap: 0, flexShrink: 0 }}>
                        <button onPointerDown={async (e) => { e.stopPropagation(); e.preventDefault();
                          const idx = day.segments.findIndex(s => s.id === seg.id);
                          if (idx > 0) {
                            const ids = day.segments.map(s => s.id); [ids[idx], ids[idx-1]] = [ids[idx-1], ids[idx]]; onReorder && onReorder(ids);
                          } else if (dayIdx > 0) {
                            const prevDay = timeline[dayIdx - 1];
                            const time = seg.start_datetime ? seg.start_datetime.split("T")[1] || "09:00" : "09:00";
                            await authFetch(`/api/segments/${seg.id}`, { method: "PUT", body: JSON.stringify({ start_datetime: `${prevDay.date}T${time}` }) });
                            if (onRefreshTrip) onRefreshTrip();
                          }
                        }} onClick={(e) => e.stopPropagation()} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 16, cursor: "pointer", padding: "8px 12px", lineHeight: 1, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>▲</button>
                        <button onPointerDown={async (e) => { e.stopPropagation(); e.preventDefault();
                          const idx = day.segments.findIndex(s => s.id === seg.id);
                          if (idx < day.segments.length - 1) {
                            const ids = day.segments.map(s => s.id); [ids[idx], ids[idx+1]] = [ids[idx+1], ids[idx]]; onReorder && onReorder(ids);
                          } else if (dayIdx < timeline.length - 1) {
                            const nextDay = timeline[dayIdx + 1];
                            const time = seg.start_datetime ? seg.start_datetime.split("T")[1] || "09:00" : "09:00";
                            await authFetch(`/api/segments/${seg.id}`, { method: "PUT", body: JSON.stringify({ start_datetime: `${nextDay.date}T${time}` }) });
                            if (onRefreshTrip) onRefreshTrip();
                          }
                        }} onClick={(e) => e.stopPropagation()} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 16, cursor: "pointer", padding: "8px 12px", lineHeight: 1, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>▼</button>
                      </div>
                    </>}
                    <div className="seg-icon">{getSegIcon(seg)}</div>
                    <div className="seg-body">
                      <div className="seg-title">
                        {(seg.type === "car_rental" || seg.type === "bike_hire") && seg._view === "pickup" ? `Pickup: ${seg.title}` :
                         (seg.type === "car_rental" || seg.type === "bike_hire") && seg._view === "return" ? `Return: ${seg.title}` :
                         seg.type === "ferry" && seg._view === "pickup" ? `Depart: ${seg.title}` :
                         seg.type === "ferry" && seg._view === "return" ? `Arrive: ${seg.title}` :
                         (seg.type === "hotel" || seg.type === "friends_house") && seg._view === "checkout" ? `Check out from ${seg.title}` :
                         seg.title}
                      </div>
                      <div className="seg-detail">
                        {(seg.type === "car_rental" || seg.type === "bike_hire") && seg._view === "return"
                          ? (seg.dropoff_location || seg.end_location || "")
                          : (seg.type === "car_rental" || seg.type === "bike_hire") && seg._view === "pickup"
                          ? (seg.pickup_location || seg.start_location || "")
                          : seg.type === "ferry" && seg._view === "return"
                          ? (seg.end_location || "")
                          : seg.type === "ferry" && seg._view === "pickup"
                          ? (seg.start_location || "")
                          : (seg.start_location && seg.end_location && seg.start_location !== seg.end_location
                              ? `${seg.start_location} → ${seg.end_location}`
                              : seg.start_location || seg.end_location || "")}
                        {seg.provider && <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>· {seg.provider}</span>}
                      </div>
                      {seg.type === "hotel" && seg._view !== "checkout" && seg.start_datetime && seg.end_datetime && (() => {
                        const inDate = seg.start_datetime.split("T")[0];
                        const outDate = seg.end_datetime.split("T")[0];
                        const nights = Math.max(1, Math.round((new Date(outDate) - new Date(inDate)) / 86400000));
                        return (
                          <div style={{
                            fontSize: 12, fontFamily: "var(--ff-mono)", marginTop: 6,
                            padding: "4px 8px", display: "inline-block",
                            background: "rgba(255,171,64,0.12)", borderRadius: 6,
                            border: "1px solid rgba(255,171,64,0.3)", color: "var(--hotel-color)",
                          }}>
                            <strong style={{ color: "#fff" }}>{formatDate(seg.start_datetime)}</strong>
                            <span style={{ margin: "0 6px", opacity: 0.7 }}>→</span>
                            <strong style={{ color: "#fff" }}>{formatDate(seg.end_datetime)}</strong>
                            <span style={{ marginLeft: 8 }}>· {nights} night{nights === 1 ? "" : "s"}</span>
                          </div>
                        );
                      })()}
                      {seg.notes && <div style={{ marginTop: 4 }}><Truncated text={seg.notes} lines={2} style={{ fontSize: 12, color: "#b8b4cc" }} /></div>}
                      {seg.passengers && (() => {
                        try {
                          const p = JSON.parse(seg.passengers);
                          let pd = [];
                          try { pd = JSON.parse(seg.passenger_details || "[]"); } catch {}
                          return p.length > 0 ? (
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                              {p.map((name, idx) => (
                                <span key={idx}>
                                  {idx > 0 && ", "}
                                  <span style={{ color: "var(--green)", cursor: "pointer", textDecoration: "underline dotted" }}
                                    onClick={(e) => { e.stopPropagation(); setExpandedSeg(seg.id); setSelectedPassengerIdx({segId: seg.id, idx}); }}>
                                    {name}
                                  </span>
                                  {pd[idx] && pd[idx].checked_in && <span style={{ color: "var(--green)", fontSize: 10 }}> ✓</span>}
                                </span>
                              ))}
                            </div>
                          ) : null;
                        } catch { return null; }
                      })()}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, maxWidth: "40%" }}>
                      {seg.type === "event" && seg.user_local_datetime ? (
                        // User-local kickoff is what matters when the
                        // venue's in a different TZ — show the user
                        // their wall-clock time as the headline, and
                        // venue time as a secondary line. Tim was
                        // showing up an hour late because the headline
                        // was the venue's 1pm not his 12pm Vancouver.
                        <>
                          <div className="seg-time"
                            title={`Venue: ${seg.venue_display || ""}  ·  You: ${seg.user_local_display || ""}`}>
                            {formatTime(seg.user_local_datetime)}
                            <span style={{ fontSize: 10, color: "var(--green)", marginLeft: 4 }}>your time</span>
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2, fontFamily: "var(--ff-mono)" }}>
                            venue: {formatTime(seg.start_datetime)}
                          </div>
                        </>
                      ) : (
                        <div className="seg-time">
                          {formatTimeRange(seg.start_datetime, seg.end_datetime, seg.type)}
                        </div>
                      )}
                      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "flex-end" }}>
                        <span className={`badge badge-${seg.type}`}
                          style={seg.type === "hotel" ? { cursor: "pointer", textDecoration: "underline" } : {}}
                          onClick={seg.type === "hotel" ? (e) => { e.stopPropagation(); fetchHotelDetail(seg); } : undefined}
                          title={seg.type === "hotel" ? "Click for full hotel details" : ""}>{seg.type}</span>
                        {seg.status === "tentative" && <span className="badge badge-tentative" style={{ marginLeft: 4 }}>tentative</span>}
                        {seg.status === "unconfirmed" && <span className="badge badge-unconfirmed" style={{ marginLeft: 4 }}>needs confirmation</span>}
                        {seg.status === "suggested" && <span className="badge badge-suggested" style={{ marginLeft: 4 }}>suggested</span>}
                        {seg.status === "suggested" && canEdit && (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm("Remove this suggested activity?")) {
                              await authFetch(`/api/segments/${seg.id}`, { method: "DELETE" });
                              if (onRefreshTrip) onRefreshTrip();
                            }
                          }} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 11, marginLeft: 4 }}>
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 12, justifyContent: "flex-end" }}>
                        <span onClick={(e) => { e.stopPropagation(); handleVote(seg.id, 1); }}
                          style={{ cursor: "pointer", opacity: myVotes[seg.id] === 1 ? 1 : 0.5, color: "var(--green)" }}>
                          👍 {votes[seg.id]?.up || 0}
                        </span>
                        <span onClick={(e) => { e.stopPropagation(); handleVote(seg.id, -1); }}
                          style={{ cursor: "pointer", opacity: myVotes[seg.id] === -1 ? 1 : 0.5, color: "var(--red)" }}>
                          👎 {votes[seg.id]?.down || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  {expandedSeg === seg.id && (
                    <div className="card seg-detail-card" style={{ marginBottom: 8, marginLeft: 44, padding: 0, overflow: "hidden" }}>
                      {editingSeg === seg.id ? (
                        /* ── Editable segment form ── */
                        <div style={{ padding: 16, fontSize: 13 }} onClick={e => e.stopPropagation()}>
                          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 12 }}>Editing Segment</div>

                          <div className="form-section-title">Type</div>
                          <div className="type-chip-grid" role="radiogroup" aria-label="Segment type" style={{ marginBottom: 12 }}>
                            {SEGMENT_TYPE_OPTIONS.map(opt => {
                              const selected = (editSegData.type || seg.type) === opt.value;
                              const color = SEG_COLORS[opt.value] || "var(--text)";
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={selected}
                                  className={`type-chip${selected ? " selected" : ""}`}
                                  style={selected ? { "--chip-color": color, "--chip-bg": `color-mix(in srgb, ${color} 18%, transparent)` } : undefined}
                                  onClick={() => setEditSegData(d => ({ ...d, type: opt.value }))}
                                >
                                  <span className="type-chip-emoji">{SEG_ICONS[opt.value] || "📌"}</span>
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>

                          <div className="form-section-title">Booking</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div className="form-group"><label>Title</label><input className="input" value={editSegData.title} onChange={e => setEditSegData(d => ({...d, title: e.target.value}))} /></div>
                            <div className="form-group"><label>Provider</label><input className="input" value={editSegData.provider} onChange={e => setEditSegData(d => ({...d, provider: e.target.value}))} /></div>
                            <div className="form-group"><label>Confirmation Code</label><input className="input" value={editSegData.confirmation_code} onChange={e => setEditSegData(d => ({...d, confirmation_code: e.target.value}))} /></div>
                            <div className="form-group"><label>Status</label>
                              <select className="input" value={editSegData.status} onChange={e => setEditSegData(d => ({...d, status: e.target.value}))}>
                                <option value="confirmed">Confirmed</option><option value="tentative">Tentative</option><option value="unconfirmed">Unconfirmed</option><option value="suggested">Suggested</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-section-title">Dates &amp; Locations</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div className="form-group">
                              <label>{seg.type === "hotel" ? "Check-in Date/Time" : seg.type === "event" ? "Kickoff (venue-local)" : (seg.type === "car_rental" || seg.type === "bike_hire") ? "Pickup Date/Time" : seg.type === "ferry" ? "Departure Date/Time" : "Start Date/Time"}</label>
                              <input className="input" type="datetime-local" value={editSegData.start_datetime?.replace(" ", "T")} onChange={e => setEditSegData(d => ({...d, start_datetime: e.target.value}))} />
                              {seg.type === "event" && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Treated as wall-clock in venue timezone — no conversion is applied on save.</div>}
                            </div>
                            <div className="form-group"><label>{seg.type === "hotel" ? "Check-out Date/Time" : (seg.type === "car_rental" || seg.type === "bike_hire") ? "Return Date/Time" : seg.type === "ferry" ? "Arrival Date/Time" : "End Date/Time"}</label><input className="input" type="datetime-local" value={editSegData.end_datetime?.replace(" ", "T")} onChange={e => setEditSegData(d => ({...d, end_datetime: e.target.value}))} /></div>
                            <div className="form-group"><label>{seg.type === "hotel" ? "Address" : seg.type === "transport" ? "Pickup Location" : seg.type === "flight" ? "Departure Airport" : (seg.type === "car_rental" || seg.type === "bike_hire") ? "Pickup Location" : seg.type === "ferry" ? "Departure Port" : "Location"}</label><input className="input" value={editSegData.start_location} onChange={e => setEditSegData(d => ({...d, start_location: e.target.value}))} /></div>
                            {seg.type !== "hotel" && <div className="form-group"><label>{seg.type === "transport" ? "Dropoff Location" : seg.type === "flight" ? "Arrival Airport" : (seg.type === "car_rental" || seg.type === "bike_hire") ? "Dropoff Location" : seg.type === "ferry" ? "Arrival Port" : "End Location"}</label><input className="input" value={editSegData.end_location} onChange={e => setEditSegData(d => ({...d, end_location: e.target.value}))} /></div>}
                          </div>

                          {(seg.type === "flight" || seg.type === "hotel" || seg.type === "transport" || seg.type === "car_rental" || seg.type === "bike_hire" || seg.type === "ferry") && (
                            <>
                              <div className="form-section-title">Details</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                {seg.type === "flight" && <>
                                  <div className="form-group"><label>Flight Number</label><input className="input" value={editSegData.flight_number} onChange={e => setEditSegData(d => ({...d, flight_number: e.target.value}))} /></div>
                                  <div className="form-group"><label>Seat</label><input className="input" value={editSegData.seat} onChange={e => setEditSegData(d => ({...d, seat: e.target.value}))} /></div>
                                </>}
                                {seg.type === "hotel" && <>
                                  <div className="form-group"><label>Room Type</label><input className="input" value={editSegData.room_type} onChange={e => setEditSegData(d => ({...d, room_type: e.target.value}))} /></div>
                                  <div className="form-group"><label>Check-in Time</label><input className="input" value={editSegData.check_in_time} onChange={e => setEditSegData(d => ({...d, check_in_time: e.target.value}))} /></div>
                                  <div className="form-group"><label>Check-out Time</label><input className="input" value={editSegData.check_out_time} onChange={e => setEditSegData(d => ({...d, check_out_time: e.target.value}))} /></div>
                                </>}
                                {seg.type === "transport" && <>
                                  <div className="form-group"><label>Vehicle Type</label><input className="input" value={editSegData.vehicle_type} onChange={e => setEditSegData(d => ({...d, vehicle_type: e.target.value}))} /></div>
                                  <div className="form-group"><label>Pickup</label><input className="input" value={editSegData.pickup_location} onChange={e => setEditSegData(d => ({...d, pickup_location: e.target.value}))} /></div>
                                  <div className="form-group"><label>Dropoff</label><input className="input" value={editSegData.dropoff_location} onChange={e => setEditSegData(d => ({...d, dropoff_location: e.target.value}))} /></div>
                                </>}
                                {seg.type === "car_rental" && <>
                                  <div className="form-group"><label>Vehicle / Car Group</label><input className="input" value={editSegData.vehicle_type || ""} onChange={e => setEditSegData(d => ({...d, vehicle_type: e.target.value}))} placeholder="e.g. Toyota RAV4" /></div>
                                  <div className="form-group"><label>Pickup Address</label><input className="input" value={editSegData.pickup_location || ""} onChange={e => setEditSegData(d => ({...d, pickup_location: e.target.value}))} /></div>
                                  <div className="form-group"><label>Dropoff Address</label><input className="input" value={editSegData.dropoff_location || ""} onChange={e => setEditSegData(d => ({...d, dropoff_location: e.target.value}))} /></div>
                                </>}
                                {seg.type === "bike_hire" && <>
                                  <div className="form-group"><label>Bike Model</label><input className="input" value={editSegData.vehicle_type || ""} onChange={e => setEditSegData(d => ({...d, vehicle_type: e.target.value}))} placeholder="e.g. Hybrid Road E-Bike (Shimano Ultegra di2)" /></div>
                                  <div className="form-group"><label>Pickup Address</label><input className="input" value={editSegData.pickup_location || ""} onChange={e => setEditSegData(d => ({...d, pickup_location: e.target.value}))} /></div>
                                  <div className="form-group"><label>Dropoff Address</label><input className="input" value={editSegData.dropoff_location || ""} onChange={e => setEditSegData(d => ({...d, dropoff_location: e.target.value}))} /></div>
                                </>}
                                {seg.type === "ferry" && <>
                                  <div className="form-group"><label>Vessel Name</label><input className="input" value={editSegData.vehicle_type || ""} onChange={e => setEditSegData(d => ({...d, vehicle_type: e.target.value}))} placeholder="e.g. Sarfaq Ittuk" /></div>
                                  <div className="form-group"><label>Cabin / Accommodation</label><input className="input" value={editSegData.room_type || ""} onChange={e => setEditSegData(d => ({...d, room_type: e.target.value}))} placeholder="e.g. Junior Suite Twin Bed, deck 4" /></div>
                                </>}
                              </div>
                              {(seg.type === "bike_hire" || seg.type === "ferry") && (
                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                                  Rider / passenger / extras details live in Notes below — edit them there.
                                </div>
                              )}
                            </>
                          )}

                          {seg.type === "event" && (
                            <div className="form-group" style={{ marginTop: 8 }}>
                              <label>Venue Timezone (IANA)</label>
                              <input className="input" value={editSegData.venue_timezone || ""} onChange={e => setEditSegData(d => ({...d, venue_timezone: e.target.value}))} placeholder="e.g. America/Vancouver, Europe/Budapest" list="iana-tz-list" />
                              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Used by the dual-time display. Leave as-is unless the venue moved.</div>
                            </div>
                          )}

                          {seg.type !== "flight" && (
                            <>
                              <div className="form-section-title">{ACCOMMODATION_TYPES.has(seg.type) ? "Guests" : seg.type === "event" ? "Attendees" : "Passengers"}</div>
                              <div className="form-group">
                                <input className="input" placeholder="Comma-separated names" value={editSegData.passengers_text || ""} onChange={e => setEditSegData(d => ({...d, passengers_text: e.target.value}))} />
                              </div>
                            </>
                          )}

                          <div className="form-section-title">Contact</div>
                          <div className="form-group"><label>Name</label><input className="input" value={editSegData.contact_name} onChange={e => setEditSegData(d => ({...d, contact_name: e.target.value}))} /></div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div className="form-group"><label>Phone</label><input className="input" value={editSegData.contact_phone} onChange={e => setEditSegData(d => ({...d, contact_phone: e.target.value}))} /></div>
                            <div className="form-group"><label>Email</label><input className="input" value={editSegData.contact_email} onChange={e => setEditSegData(d => ({...d, contact_email: e.target.value}))} /></div>
                          </div>
                          <div className="form-group"><label>WhatsApp</label><input className="input" value={editSegData.contact_whatsapp} onChange={e => setEditSegData(d => ({...d, contact_whatsapp: e.target.value}))} /></div>

                          <div className="form-section-title">Notes</div>
                          <div className="form-group"><textarea className="input" style={{ minHeight: 60 }} value={editSegData.notes} onChange={e => setEditSegData(d => ({...d, notes: e.target.value}))} /></div>
                          <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "space-between" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); saveSegmentEdit(seg.id); }}>Save</button>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingSeg(null); setEditSegData({}); }}>Cancel</button>
                            </div>
                            {onDeleteSegment && <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); if(window.confirm("Delete this segment?")) { onDeleteSegment(seg.id); setEditingSeg(null); } }}>Delete</button>}
                          </div>
                        </div>
                      ) : seg.type === "flight" ? (
                        <FlightDetailCard seg={seg} onDelete={onDeleteSegment}
                          editingNotes={editingNotes} setEditingNotes={canEdit ? setEditingNotes : null}
                          editNotesText={editNotesText} setEditNotesText={setEditNotesText}
                          onUpdateNotes={onUpdateNotes} onEdit={canEdit ? () => startEditSeg(seg) : null}
                          authFetch={authFetch} preserveScrollFor={preserveScrollFor}
                          canAnnotate={canAnnotate} personalNote={personalNotes[seg.id] || ""}
                          editingPersonalNote={editingPersonalNote} setEditingPersonalNote={setEditingPersonalNote}
                          personalNoteText={personalNoteText} setPersonalNoteText={setPersonalNoteText}
                          onSavePersonalNote={savePersonalNote}
                          selectedPassengerIdx={selectedPassengerIdx} setSelectedPassengerIdx={setSelectedPassengerIdx}
                          userName={userName} onRefreshTrip={onRefreshTrip} />
                      ) : (
                        <div style={{ padding: 16, fontSize: 13 }}>
                          {seg.type === "hotel" && (() => {
                            // Prefer overview stored on the segment; fall back to lazy-fetched copy
                            const ov = hotelOverviews[seg.id];
                            const text = seg.overview || ov?.text;
                            if (text) {
                              return (
                                <div style={{ marginBottom: 12, padding: 12, background: "var(--sidebar)", borderRadius: 8, borderLeft: "3px solid var(--amber)" }}>
                                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--amber)", marginBottom: 6 }}>Overview</div>
                                  <div style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.55 }}>{text}</div>
                                </div>
                              );
                            }
                            if (ov?.loading) return <div style={{ marginBottom: 12, fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Loading hotel overview…</div>;
                            return null;
                          })()}
                          {seg.confirmation_code && <div style={{ marginBottom: 6 }}><strong>Confirmation:</strong> <span className="mono">{seg.confirmation_code}</span></div>}
                          {seg.room_type && <div style={{ marginBottom: 6 }}><strong>Room:</strong> {seg.room_type}</div>}
                          {seg.check_in_time && <div style={{ marginBottom: 6 }}><strong>Check-in:</strong> {seg.check_in_time}</div>}
                          {seg.check_out_time && <div style={{ marginBottom: 6 }}><strong>Check-out:</strong> {seg.check_out_time}</div>}
                          {seg.seat && <div style={{ marginBottom: 6 }}><strong>Seat:</strong> {seg.seat}</div>}
                          {seg.vehicle_type && <div style={{ marginBottom: 6 }}><strong>Vehicle:</strong> {seg.vehicle_type}</div>}
                          {seg.pickup_location && <div style={{ marginBottom: 6 }}><strong>Pickup:</strong> {seg.pickup_location}</div>}
                          {seg.dropoff_location && <div style={{ marginBottom: 6 }}><strong>Dropoff:</strong> {seg.dropoff_location}</div>}
                          {/* Tim 2026-10-03: transport segments carry duration_minutes
                              + provider but neither was surfaced in the expanded card.
                              Booking.com transfer stored "35 min" + "E-Life Limo" that
                              only showed in the collapsed row's provider dot. Both now
                              shown here. */}
                          {seg.duration_minutes > 0 && (
                            <div style={{ marginBottom: 6 }}>
                              <strong>Duration:</strong> {seg.duration_minutes >= 60
                                ? `${Math.floor(seg.duration_minutes / 60)}h ${seg.duration_minutes % 60}m`
                                : `${seg.duration_minutes}m`}
                            </div>
                          )}
                          {seg.provider && seg.type !== "flight" && <div style={{ marginBottom: 6 }}><strong>Provider:</strong> {seg.provider}</div>}
                          {seg.passengers && <div style={{ marginBottom: 6 }}><strong>{ACCOMMODATION_TYPES.has(seg.type) ? "Guests" : seg.type === "event" ? "Attendees" : "Passengers"}:</strong> {(() => { try { return JSON.parse(seg.passengers).join(", "); } catch { return seg.passengers; } })()}</div>}
                          {/* Passenger details (inline for non-flight segments) */}
                          {seg.passenger_details && (() => {
                            try {
                              const pd = JSON.parse(seg.passenger_details);
                              if (pd.length === 0) return null;
                              return (
                                <div style={{ marginTop: 8, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 6 }}>Passenger Details</div>
                                  {pd.map((p, i) => (
                                    <div key={i} style={{ marginBottom: i < pd.length - 1 ? 6 : 0, fontSize: 13, color: "var(--text-dim)" }}>
                                      <strong style={{ color: "#e8e6f0" }}>{p.name}</strong>
                                      {p.seat && <span style={{ marginLeft: 8 }}>Seat: {p.seat}</span>}
                                      {p.checkin && <span style={{ marginLeft: 8 }}>Check-in: {p.checkin}</span>}
                                      {p.meal && p.meal !== "Standard" && <span style={{ marginLeft: 8 }}>Meal: {p.meal}</span>}
                                    </div>
                                  ))}
                                </div>
                              );
                            } catch { return null; }
                          })()}
                          {/* Hotel details */}
                          {seg.hotel_inclusions && (
                            <div style={{ marginTop: 8, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 4 }}>Inclusions</div>
                              <Truncated text={seg.hotel_inclusions} lines={3} style={{ color: "var(--text-dim)", lineHeight: 1.6 }} />
                            </div>
                          )}
                          {seg.hotel_facilities && (
                            <div style={{ marginTop: 8, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--amber)", marginBottom: 4 }}>Facilities</div>
                              <Truncated text={seg.hotel_facilities} lines={3} style={{ color: "var(--text-dim)", lineHeight: 1.6 }} />
                            </div>
                          )}
                          {seg.hotel_excursions && (
                            <div style={{ marginTop: 8, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--blue)", marginBottom: 4 }}>Excursions & Activities</div>
                              <Truncated text={seg.hotel_excursions} lines={3} style={{ color: "var(--text-dim)", lineHeight: 1.6 }} />
                            </div>
                          )}
                          {/* Booking policies & fees */}
                          {(seg.cancellation_deadline || seg.prepayment_days_before != null || seg.prepayment_refundable != null || seg.resort_fee || seg.amenity_fee || seg.eco_fee || seg.national_park_pass_required) && (
                            <div style={{ marginTop: 10, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                              <div style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: 0.5, color: "var(--amber)", marginBottom: 6 }}>Policies &amp; Fees</div>
                              {seg.cancellation_deadline && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Cancel by:</strong> {seg.cancellation_deadline.replace("T", " ")}</div>}
                              {seg.prepayment_refundable === 0 && <div style={{ marginBottom: 4, color: "var(--red)" }}><strong>Non-refundable</strong></div>}
                              {seg.prepayment_days_before != null && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Prepayment:</strong> charged {seg.prepayment_days_before} day{seg.prepayment_days_before === 1 ? "" : "s"} before arrival</div>}
                              {seg.resort_fee && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Resort fee:</strong> {seg.resort_fee}</div>}
                              {seg.amenity_fee && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Amenity fee:</strong> {seg.amenity_fee}</div>}
                              {seg.eco_fee && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Eco-fee:</strong> {seg.eco_fee}</div>}
                              {seg.national_park_pass_required ? <div style={{ marginBottom: 4, color: "var(--amber)" }}>⚠ National Park Pass required</div> : null}
                            </div>
                          )}
                          {/* Access codes & wifi — auto-revealed during the stay window, masked otherwise */}
                          {(seg.access_pin || seg.gate_code || seg.wifi_credentials) && (() => {
                            const autoReveal = isStayWindowActive(seg.start_datetime, seg.end_datetime);
                            return (
                              <div style={{ marginTop: 10, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                                <div style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: 0.5, color: "var(--blue)", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span>Access</span>
                                  {!autoReveal && <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>shown automatically from day before check-in</span>}
                                </div>
                                {seg.access_pin && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>PIN:</strong> <Sensitive value={seg.access_pin} autoReveal={autoReveal} mono /></div>}
                                {seg.gate_code && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Gate code:</strong> <Sensitive value={seg.gate_code} autoReveal={autoReveal} mono /></div>}
                                {seg.wifi_credentials && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>WiFi:</strong> <Sensitive value={seg.wifi_credentials} autoReveal={autoReveal} /></div>}
                              </div>
                            );
                          })()}
                          {/* Contact details */}
                          {(seg.contact_name || seg.contact_phone || seg.contact_whatsapp || seg.contact_email) && (
                            <div style={{ marginTop: 10, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 6 }}>Contact</div>
                              {seg.contact_name && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Name:</strong> {seg.contact_name}</div>}
                              {seg.contact_phone && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Phone:</strong> <a href={`tel:${seg.contact_phone}`} style={{ color: "var(--blue)" }}>{seg.contact_phone}</a></div>}
                              {seg.contact_whatsapp && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>WhatsApp:</strong> <a href={`https://wa.me/${seg.contact_whatsapp.replace(/[^0-9]/g, "")}`} style={{ color: "var(--green)" }}>{seg.contact_whatsapp}</a></div>}
                              {seg.contact_email && <div style={{ marginBottom: 4, color: "#e8e6f0" }}><strong>Email:</strong> <a href={`mailto:${seg.contact_email}`} style={{ color: "var(--blue)" }}>{seg.contact_email}</a></div>}
                            </div>
                          )}
                          {/* Notes — editable */}
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 4 }}>Additional Information</div>
                            {canEdit && editingNotes === seg.id ? (
                              <div>
                                <textarea className="input" style={{ minHeight: 60, fontSize: 13 }} value={editNotesText}
                                  onChange={(e) => setEditNotesText(e.target.value)} onClick={(e) => e.stopPropagation()} />
                                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                                  <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); preserveScrollFor(seg.id, seg._view); onUpdateNotes(seg.id, editNotesText); setEditingNotes(null); }}>Save</button>
                                  <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingNotes(null); }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div style={canEdit ? { cursor: "pointer" } : {}} onClick={canEdit ? (e) => { e.stopPropagation(); setEditingNotes(seg.id); setEditNotesText(seg.notes || ""); } : undefined}>
                                {seg.notes ? <Truncated text={seg.notes} lines={4} style={{ color: "#fff", fontSize: 13, lineHeight: 1.5 }} /> : canEdit ? <div style={{ color: "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>Click to add notes…</div> : null}
                              </div>
                            )}
                          </div>
                          {/* Personal notes (for editors + owner) */}
                          {canAnnotate && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--purple)", marginBottom: 4 }}>My Notes</div>
                              {editingPersonalNote === seg.id ? (
                                <div>
                                  <textarea className="input" style={{ minHeight: 50, fontSize: 13 }} value={personalNoteText}
                                    onChange={(e) => setPersonalNoteText(e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="Add your personal notes..." />
                                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                                    <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); savePersonalNote(seg.id); }}>Save</button>
                                    <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingPersonalNote(null); }}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditingPersonalNote(seg.id); setPersonalNoteText(personalNotes[seg.id] || ""); }}>
                                  {personalNotes[seg.id] ? <div style={{ color: "#d4a0ff", fontSize: 13, lineHeight: 1.5 }}>{personalNotes[seg.id]}</div> : <div style={{ color: "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>Click to add your personal notes…</div>}
                                </div>
                              )}
                            </div>
                          )}
                          {/* Show personal notes read-only for viewers */}
                          {!canAnnotate && personalNotes[seg.id] && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--purple)", marginBottom: 4 }}>My Notes</div>
                              <div style={{ color: "#d4a0ff", fontSize: 13 }}>{personalNotes[seg.id]}</div>
                            </div>
                          )}
                          {/* Near my hotel — profile-driven utility lookup
                              (Asian food, craft beer, supermarkets, etc).
                              Only on accommodation segments. */}
                          {(seg.type === "hotel" || seg.type === "friends_house") && (
                            <div style={{ marginTop: 10 }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={(e) => { e.stopPropagation(); setNearHotelOpen(prev => ({ ...prev, [seg.id]: !prev[seg.id] })); }}
                                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <span>📍</span> {nearHotelOpen[seg.id] ? "Hide" : "Near my hotel"}
                              </button>
                              {nearHotelOpen[seg.id] && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <NearMyHotelPanel
                                    segmentId={seg.id}
                                    tripId={tripData.id}
                                    hotelDate={(seg.start_datetime || "").slice(0, 10)}
                                    authFetch={authFetch}
                                    onClose={() => setNearHotelOpen(prev => ({ ...prev, [seg.id]: false }))}
                                    onAdded={() => { if (onRefreshTrip) onRefreshTrip(); }}
                                    onOpenAiTab={onOpenAiTab}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          {/* Attachments — boarding passes, vouchers, visa PDFs, photos */}
                          <SegmentAttachments segmentId={seg.id} tripId={tripData.id} canEdit={canEdit} authFetch={authFetch} />

                          {canEdit && (
                            <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); startEditSeg(seg); }}>Edit</button>
                              {onDeleteSegment && <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); if(window.confirm("Delete this segment?")) onDeleteSegment(seg.id); }}>Delete</button>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
                </>
              );
            })()}
            {/* Day photos / docs — pinned to this date, separate from segments */}
            <DayPhotos tripId={tripData.id} dayDate={day.date} canEdit={canEdit} authFetch={authFetch} />
            {/* Free day / activity suggestions — show when no segments or only hotels */}
            {(day.segments.length === 0 || day.segments.every(s => ACCOMMODATION_TYPES.has(s.type)) || day.segments.every(s => ACCOMMODATION_TYPES.has(s.type) || s.status === "suggested")) && (
              <div style={{ padding: "8px 16px" }}>
                {dayNotes[day.date] && <div className="day-note-text">{dayNotes[day.date]}</div>}
                {editingNoteDate === day.date ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                    <input className="input" style={{ flex: 1, padding: "6px 10px", fontSize: 13 }} placeholder="Add a note for this day..."
                      value={noteText} onChange={e => setNoteText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveDayNote(day.date); if (e.key === "Escape") { setEditingNoteDate(null); setNoteText(""); } }}
                      autoFocus />
                    <button className="btn btn-primary btn-sm" onClick={() => saveDayNote(day.date)}>Save</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingNoteDate(null); setNoteText(""); }}>Cancel</button>
                  </div>
                ) : (
                  <div className="day-actions" style={{ marginTop: dayNotes[day.date] ? 4 : 0 }}>
                    <span style={{ color: "var(--green)", fontSize: 13, fontStyle: "italic" }}>
                      {!dayNotes[day.date] && (day.segments.length === 0 ? "Free day" : "Free for activities")}
                    </span>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 8px" }}
                      onClick={() => { setEditingNoteDate(day.date); setNoteText(dayNotes[day.date] || ""); }}>
                      {dayNotes[day.date] ? "Edit note" : "Add note"}
                    </button>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 8px", color: "var(--green)" }}
                      onClick={() => suggestInterest[day.date] ? setSuggestInterest(prev => ({ ...prev, [day.date]: false })) : setSuggestInterest(prev => ({ ...prev, [day.date]: true }))} disabled={suggestingDate === day.date}>
                      {suggestingDate === day.date ? "Thinking..." : "Suggest activities"}
                    </button>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 8px", color: "var(--green)" }}
                      onClick={() => fetchEvents(day.date)}
                      disabled={loadingEvents === day.date}>
                      {loadingEvents === day.date ? "Loading..." : dayEvents[day.date] ? "📅 What's on?" : "What's on?"}
                    </button>
                  </div>
                )}
                {suggestInterest[day.date] && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                      {["Anything", "Hotel activities", "Restaurants", "Sports", "Day trips", "Cultural", "Nature", "Shopping", "Nightlife"].map(chip => (
                        <button key={chip} className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12 }}
                          onClick={() => fetchDaySuggestion(day.date, chip)}>
                          {chip}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input className="input" style={{ flex: 1, padding: "5px 10px", fontSize: 12 }}
                        placeholder="Or type something specific..."
                        value={suggestInterestText[day.date] || ""}
                        onChange={e => setSuggestInterestText(prev => ({ ...prev, [day.date]: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter" && suggestInterestText[day.date]?.trim()) fetchDaySuggestion(day.date, suggestInterestText[day.date].trim()); }} />
                      <button className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: "4px 10px" }}
                        onClick={() => fetchDaySuggestion(day.date, suggestInterestText[day.date]?.trim() || "Anything")}
                        disabled={suggestingDate === day.date}>Go</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* Hotel detail overlay */}
      {hotelDetail && (
        <Modal onClose={() => setHotelDetail(null)} style={{ maxWidth: 600 }}>
            {hotelDetail.loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-dim)" }}>Loading hotel details...</div>
            ) : hotelDetail.data ? (
              <div>
                <h2 style={{ marginBottom: 4 }}>{tripData?.segments?.find(s => s.id === hotelDetail.segId)?.title || "Hotel"}</h2>
                <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>{hotelDetail.data.style}</div>
                <p style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.6, marginBottom: 16 }}>{hotelDetail.data.description}</p>

                {hotelDetail.data.rooms && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--amber)", fontSize: 13, marginBottom: 4 }}>YOUR ROOM</h4>
                    <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{hotelDetail.data.rooms.description}</div>
                    {hotelDetail.data.rooms.features?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                        {hotelDetail.data.rooms.features.map((f,i) => <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "rgba(255,171,64,0.1)", color: "var(--amber)" }}>{f}</span>)}
                      </div>
                    )}
                  </div>
                )}

                {hotelDetail.data.dining?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--green)", fontSize: 13, marginBottom: 4 }}>DINING</h4>
                    {hotelDetail.data.dining.map((d,i) => <div key={i} style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 4 }}><strong>{d.name}</strong> — {d.cuisine}. {d.notes}</div>)}
                  </div>
                )}

                {hotelDetail.data.facilities?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--blue)", fontSize: 13, marginBottom: 4 }}>FACILITIES</h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {hotelDetail.data.facilities.map((f,i) => <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "rgba(68,138,255,0.1)", color: "var(--blue)" }}>{f}</span>)}
                    </div>
                  </div>
                )}

                {hotelDetail.data.inclusions?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--green)", fontSize: 13, marginBottom: 4 }}>INCLUDED</h4>
                    {hotelDetail.data.inclusions.map((inc,i) => <div key={i} style={{ fontSize: 13, color: "var(--text-dim)" }}>✓ {inc}</div>)}
                  </div>
                )}

                {hotelDetail.data.extras?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--red)", fontSize: 13, marginBottom: 4 }}>EXTRA COST</h4>
                    {hotelDetail.data.extras.map((ex,i) => <div key={i} style={{ fontSize: 13, color: "var(--text-dim)" }}>+ {ex}</div>)}
                  </div>
                )}

                {hotelDetail.data.excursions?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--purple)", fontSize: 13, marginBottom: 4 }}>EXCURSIONS & ACTIVITIES</h4>
                    {hotelDetail.data.excursions.map((ex,i) => <div key={i} style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 4 }}><strong>{ex.name}</strong> — {ex.description}</div>)}
                  </div>
                )}

                {hotelDetail.data.location_tips && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--amber)", fontSize: 13, marginBottom: 4 }}>LOCATION</h4>
                    <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{hotelDetail.data.location_tips}</div>
                  </div>
                )}

                {hotelDetail.data.tips?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ color: "var(--green)", fontSize: 13, marginBottom: 4 }}>INSIDER TIPS</h4>
                    {hotelDetail.data.tips.map((t,i) => <div key={i} style={{ fontSize: 13, color: "var(--text-dim)" }}>→ {t}</div>)}
                  </div>
                )}

                <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>
                  {hotelDetail.data.check_in && <span>Check-in: {hotelDetail.data.check_in}</span>}
                  {hotelDetail.data.check_out && <span>Check-out: {hotelDetail.data.check_out}</span>}
                  {hotelDetail.data.wifi && <span>WiFi: {hotelDetail.data.wifi}</span>}
                </div>
              </div>
            ) : (
              <div style={{ color: "var(--red)" }}>Failed to load hotel details</div>
            )}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => setHotelDetail(null)}>Close</button>
            </div>
        </Modal>
      )}

      {/* Export print modal — keeps the user inside the PWA so they can close
          back to the app instead of being stranded in a new browser window. */}
      {exportHtml && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 16px", background: "var(--card)", borderBottom: "1px solid var(--border)",
            paddingTop: "calc(env(safe-area-inset-top) + 10px)",
          }}>
            <span style={{ fontSize: 12, color: "var(--text-dim)", fontFamily: "var(--ff-mono)" }}>Itinerary preview</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                const f = document.getElementById("export-pdf-iframe");
                if (f && f.contentWindow) f.contentWindow.print();
              }} style={{
                background: "var(--green)", color: "var(--bg)", border: "none",
                borderRadius: 6, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontWeight: 600,
              }}>🖨 Print / Save PDF</button>
              <button onClick={() => setExportHtml(null)} style={{
                background: "var(--red)", color: "#fff", border: "none",
                borderRadius: 6, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontWeight: 600,
              }}>× Close</button>
            </div>
          </div>
          <iframe id="export-pdf-iframe" title="Itinerary" srcDoc={exportHtml}
            style={{ flex: 1, width: "100%", border: "none", background: "#fff" }} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CALENDAR VIEW
// ══════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════
// FLIGHT DETAIL CARD (departure board style)
// ══════════════════════════════════════════════════════════════════════════
function FlightDetailCard({ seg, onDelete, editingNotes, setEditingNotes, editNotesText, setEditNotesText, onUpdateNotes, onEdit, authFetch, preserveScrollFor, canAnnotate, personalNote, editingPersonalNote, setEditingPersonalNote, personalNoteText, setPersonalNoteText, onSavePersonalNote, selectedPassengerIdx, setSelectedPassengerIdx, userName, onRefreshTrip }) {
  const [flightStatus, setFlightStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(0);
  const [passengerEdits, setPassengerEdits] = useState({});
  const [passengerSaving, setPassengerSaving] = useState(false);
  const [passengerEditing, setPassengerEditing] = useState(false);
  const [loungeInfo, setLoungeInfo] = useState(null);
  const [loungeLoading, setLoungeLoading] = useState(false);
  const [loungeEditing, setLoungeEditing] = useState(false);
  const [loungeEditData, setLoungeEditData] = useState(null);
  const loungeLookupTrigger = useRef(0);

  // Auto-select passenger when clicked from collapsed view. seg.id / the
  // setter are stable; we deliberately respond only to selectedPassengerIdx.
  useEffect(() => {
    if (selectedPassengerIdx && selectedPassengerIdx.segId === seg.id) {
      setSelectedPassenger(selectedPassengerIdx.idx);
      setPassengerEdits({});
      setPassengerEditing(false);
      if (setSelectedPassengerIdx) setSelectedPassengerIdx(null);
    }
  }, [selectedPassengerIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the logged-in user's passenger on first render / when the
  // segment changes. Intentionally ignores selectedPassenger/passenger_details
  // so manual selections aren't overwritten on every re-render.
  useEffect(() => {
    if (!userName) return;
    let paxDetails = [];
    try { paxDetails = JSON.parse(seg.passenger_details || "[]"); } catch {}
    if (paxDetails.length === 0) {
      try { const names = JSON.parse(seg.passengers || "[]"); paxDetails = names.map(n => ({ name: n })); } catch {}
    }
    const norm = (s) => (s || "").toLowerCase().trim();
    const matchIdx = paxDetails.findIndex(p => norm(p.name) === norm(userName) || norm(p.name).includes(norm(userName)) || norm(userName).includes(norm(p.name)));
    if (matchIdx >= 0 && matchIdx !== selectedPassenger) setSelectedPassenger(matchIdx);
  }, [userName, seg.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lounge lookup - only triggers when lounge_access is explicitly toggled on in edit mode
  const triggerLoungeLookup = useCallback(() => {
    loungeLookupTrigger.current += 1;
    const currentTrigger = loungeLookupTrigger.current;
    if (!authFetch) return;
    setLoungeInfo(null);
    setLoungeLoading(true);
    let paxDetails = [];
    try { paxDetails = JSON.parse(seg.passenger_details || "[]"); } catch {}
    const pax = paxDetails[selectedPassenger];
    authFetch("/api/ai/lounge-lookup", {
      method: "POST",
      body: JSON.stringify({
        airline: seg.provider,
        airport: seg.start_location,
        terminal: seg.departure_terminal,
        fare_type: pax?.fare_type || "",
      }),
    }).then(r => r.json()).then(data => { if (loungeLookupTrigger.current === currentTrigger) setLoungeInfo(data); }).catch(() => {}).finally(() => { if (loungeLookupTrigger.current === currentTrigger) setLoungeLoading(false); });
  }, [authFetch, seg.passenger_details, seg.provider, seg.start_location, seg.departure_terminal, selectedPassenger]);

  // Auto-fetch lounge info when selected passenger has lounge_access.
  // Including triggerLoungeLookup / loungeInfo / loungeLoading in deps would
  // re-trigger the fetch on every state update mid-lookup; the !loungeInfo
  // && !loungeLoading guard inside is what keeps it single-shot per change.
  useEffect(() => {
    if (!authFetch) return;
    let paxDetails = [];
    try { paxDetails = JSON.parse(seg.passenger_details || "[]"); } catch {}
    const pax = paxDetails[selectedPassenger];
    if (pax?.lounge_access && !loungeInfo && !loungeLoading) {
      triggerLoungeLookup();
    }
  }, [seg.passenger_details, selectedPassenger]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkFlightStatus = async () => {
    if (!seg.flight_number) return;
    setStatusLoading(true);
    try {
      const r = await authFetch(`/api/flights/${seg.id}/status`);
      const data = await r.json();
      setFlightStatus(data);
    } catch {} finally { setStatusLoading(false); }
  };

  const depTime = formatTime(seg.start_datetime);
  const arrTime = formatTime(seg.end_datetime);
  const depCity = seg.start_location?.replace(/\s*\(.*\)/, "") || "";
  const arrCity = seg.end_location?.replace(/\s*\(.*\)/, "") || "";
  const depCode = seg.start_location?.match(/\(([A-Z]{3})\)/)?.[1] || "";
  const arrCode = seg.end_location?.match(/\(([A-Z]{3})\)/)?.[1] || "";
  const depAirport = seg.departure_airport_name || "";
  const arrAirport = seg.arrival_airport_name || "";
  const durationHrs = seg.duration_minutes ? Math.floor(seg.duration_minutes / 60) : null;
  const durationMin = seg.duration_minutes ? seg.duration_minutes % 60 : null;
  const durationStr = durationHrs !== null ? `${durationHrs} hr. ${durationMin} min.` : null;
  let passengers = null;
  if (seg.passengers) { try { passengers = JSON.parse(seg.passengers); } catch { passengers = [seg.passengers]; } }

  let passengerDetails = [];
  try { passengerDetails = JSON.parse(seg.passenger_details || "[]"); } catch {}
  if (passengerDetails.length === 0 && passengers && passengers.length > 0) {
    passengerDetails = passengers.map(n => ({ name: n, seat: "", checkin: "", meal: "Standard", checked_in: false, notes: "" }));
  }
  // Coerce legacy/stringified checked_in into a real boolean. Some
  // historical rows (Claude email parser, manual JSON edits) ended up
  // with the string "false" instead of bool false — JS treats that as
  // truthy and the checked-in toggle would render checked + appear to
  // revert on tap-off. The server now coerces on save too.
  const truthy = (v) => v === true || v === 1 || v === "1"
    || (typeof v === "string" && (v.toLowerCase() === "true" || v.toLowerCase() === "yes"));
  for (const p of passengerDetails) {
    p.checked_in = truthy(p.checked_in);
  }
  const currentPax = Object.keys(passengerEdits).length > 0
    ? { ...(passengerDetails[selectedPassenger] || {}), ...passengerEdits }
    : passengerDetails[selectedPassenger] || {};
  // Fall back to the segment's top-level seat when the per-passenger
  // seat is empty so the "Seat: X" row still shows up. Booking
  // confirmations often write the seat to segment.seat without
  // populating passenger_details[0].seat (Tim's BA LGW→YVR flight had
  // 4D top-level + "" per-pax — the panel rendered no Seat row at all).
  if (!currentPax.seat && seg.seat) currentPax.seat = seg.seat;

  const savePassengerDetail = async () => {
    setPassengerSaving(true);
    try {
      const payload = {
        name: passengerEdits.name ?? currentPax.name,
        seat: passengerEdits.seat ?? currentPax.seat ?? "",
        checkin: passengerEdits.checkin ?? currentPax.checkin ?? "",
        meal: passengerEdits.meal ?? currentPax.meal ?? "Standard",
        checked_in: passengerEdits.checked_in ?? currentPax.checked_in ?? false,
        fare_type: passengerEdits.fare_type ?? currentPax.fare_type ?? "",
        lounge_access: passengerEdits.lounge_access ?? currentPax.lounge_access ?? false,
        cabin_luggage: passengerEdits.cabin_luggage ?? currentPax.cabin_luggage ?? null,
        checked_luggage: passengerEdits.checked_luggage ?? currentPax.checked_luggage ?? null,
        notes: passengerEdits.notes ?? currentPax.notes ?? "",
      };
      await authFetch(`/api/segments/${seg.id}/passenger/${selectedPassenger}`, {
        method: "PUT", body: JSON.stringify(payload)
      });
      // Blur any focused input and reset horizontal scroll — iOS Safari sometimes
      // leaves the viewport shifted after a focused input is unmounted.
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
      window.scrollTo(window.scrollX > 0 ? 0 : 0, window.scrollY);
      document.scrollingElement && (document.scrollingElement.scrollLeft = 0);
      setPassengerEdits({});
      setPassengerEditing(false);
      // Refresh trip data so the view mode shows updated values
      if (onRefreshTrip) setTimeout(() => onRefreshTrip(), 200);
    } catch (err) { console.error("Save passenger error:", err); }
    finally { setPassengerSaving(false); }
  };

  const saveCheckedInToggle = async (checked) => {
    setPassengerSaving(true);
    try {
      const payload = {
        name: currentPax.name,
        seat: currentPax.seat ?? "",
        checkin: currentPax.checkin ?? "",
        meal: currentPax.meal ?? "Standard",
        checked_in: checked,
        fare_type: currentPax.fare_type ?? "",
        lounge_access: currentPax.lounge_access ?? false,
        cabin_luggage: currentPax.cabin_luggage ?? null,
        checked_luggage: currentPax.checked_luggage ?? null,
        notes: currentPax.notes ?? "",
      };
      await authFetch(`/api/segments/${seg.id}/passenger/${selectedPassenger}`, {
        method: "PUT", body: JSON.stringify(payload)
      });
      if (onRefreshTrip) setTimeout(() => onRefreshTrip(), 200);
    } catch (err) { console.error("Save checked_in error:", err); }
    finally { setPassengerSaving(false); }
  };

  const addPassenger = async () => {
    const name = prompt("Passenger name:");
    if (!name) return;
    try {
      await authFetch(`/api/segments/${seg.id}/passenger`, {
        method: "POST", body: JSON.stringify({ name })
      });
    } catch (err) { console.error("Add passenger error:", err); }
  };

  const removePassenger = async (idx) => {
    if (!window.confirm(`Remove ${passengerDetails[idx]?.name || "this passenger"}?`)) return;
    try {
      await authFetch(`/api/segments/${seg.id}/passenger/${idx}`, { method: "DELETE" });
      if (selectedPassenger >= passengerDetails.length - 1 && selectedPassenger > 0) setSelectedPassenger(selectedPassenger - 1);
    } catch (err) { console.error("Remove passenger error:", err); }
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontSize: 12, color: "var(--flight-color)", marginBottom: 4 }}>
          {seg.start_datetime ? formatDate(seg.start_datetime) : ""}
        </div>
        <div style={{ fontFamily: "Outfit", fontSize: 18, fontWeight: 600 }}>
          {depCity} to {arrCity}
        </div>
        {seg.confirmation_code && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
            Booking ref: <span className="mono" style={{ color: "var(--text-dim)" }}>{seg.confirmation_code}</span>
          </div>
        )}
      </div>

      {/* Departure */}
      <div style={{ padding: "16px 20px 0" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ fontSize: 20, marginTop: 2, color: "var(--flight-color)" }}>✈</div>
          <div>
            <div style={{ fontFamily: "DM Mono", fontSize: 18, fontWeight: 600 }}>{depTime}</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>{depCity}</div>
            <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{depAirport}{depCode ? ` (${depCode})` : ""}</div>
            {seg.departure_terminal && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Terminal {seg.departure_terminal}</div>}
          </div>
        </div>
      </div>

      {/* Flight info line */}
      <div style={{ padding: "12px 20px 12px 54px", display: "flex", flexDirection: "column", gap: 4, borderLeft: "2px solid var(--flight-color)", marginLeft: 30 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {seg.flight_number && <span style={{ marginRight: 6 }}>✈ {seg.flight_number}</span>}
          {seg.aircraft_type && <span style={{ color: "var(--text-dim)" }}>– {seg.aircraft_type}</span>}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{seg.provider ? `Operated by ${seg.provider}` : ""}</div>
        {durationStr && <div style={{ fontSize: 13, color: "var(--text-dim)" }}>Duration {durationStr}</div>}
        {seg.seat && <div style={{ fontSize: 13, color: "var(--text-dim)" }}>Seat: {seg.seat}</div>}
      </div>

      {/* Arrival */}
      <div style={{ padding: "0 20px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ fontSize: 18, marginTop: 2, color: "var(--text-dim)" }}>◉</div>
          <div>
            <div style={{ fontFamily: "DM Mono", fontSize: 18, fontWeight: 600 }}>
              {arrTime || "—"}
              {seg.start_datetime && seg.end_datetime && (() => {
                const sd = seg.start_datetime.split("T")[0], ed = seg.end_datetime.split("T")[0];
                if (sd === ed) return null;
                const diff = Math.round((new Date(ed + "T00:00:00") - new Date(sd + "T00:00:00")) / 86400000);
                return diff > 0 ? <span style={{ marginLeft: 8, fontSize: 13, color: "var(--amber)" }}>+{diff}d</span> : null;
              })()}
            </div>
            {seg.end_datetime && (() => {
              const sd = seg.start_datetime?.split("T")[0], ed = seg.end_datetime.split("T")[0];
              if (sd !== ed) return <div style={{ fontSize: 12, color: "var(--amber)", marginTop: 2 }}>{formatDate(seg.end_datetime)}</div>;
              return null;
            })()}
            <div style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>{arrCity}</div>
            <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{arrAirport}{arrCode ? ` (${arrCode})` : ""}</div>
            {seg.arrival_terminal && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Terminal {seg.arrival_terminal}</div>}
          </div>
        </div>
      </div>

      {/* Flight Details — only Confirmation Code at segment level */}
      {seg.confirmation_code && (
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 8 }}>Flight Details</div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Confirmation Code</div>
            <div style={{ fontFamily: "DM Mono", fontSize: 13, color: "var(--text)" }}>{seg.confirmation_code}</div>
          </div>
        </div>
      )}

      {/* Passengers */}
      {passengers && (
        <div style={{ padding: "8px 20px", fontSize: 12, color: "var(--text-dim)", borderTop: "1px solid var(--border)" }}>
          Passengers: {passengers.join(", ")}
        </div>
      )}

      {/* Passenger Details */}
      {passengerDetails.length > 0 && (
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--border)" }} onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)" }}>Passenger Details</div>
            <select
              value={selectedPassenger}
              onChange={e => { setSelectedPassenger(parseInt(e.target.value)); setPassengerEdits({}); setPassengerEditing(false); }}
              style={{ background: "var(--sidebar)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, padding: "4px 8px", fontSize: 12 }}>
              {passengerDetails.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
            </select>
          </div>

          {/* ── VIEW MODE ── */}
          {!passengerEditing && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 6 }}>{currentPax.name}</div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={currentPax.checked_in || false}
                    disabled={passengerSaving}
                    onChange={(e) => saveCheckedInToggle(e.target.checked)}
                    style={{ accentColor: "var(--green)" }} />
                  <span style={{ color: currentPax.checked_in ? "var(--green)" : "var(--text-muted)", fontSize: 13 }}>
                    {currentPax.checked_in ? "\u2713 Checked In" : "Not Checked In"}
                  </span>
                </label>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-dim)", marginBottom: 8 }}>
                {currentPax.fare_type && <div>Fare Type: <span style={{ color: "var(--text)" }}>{currentPax.fare_type}</span></div>}
                {currentPax.lounge_access && <div>Lounge Access: <span style={{ color: "var(--green)" }}>Yes ✓</span></div>}
                {(currentPax.seat || currentPax.checkin) && (
                  <div>
                    {currentPax.seat && <span>Seat: <span style={{ color: "var(--text)" }}>{currentPax.seat}</span></span>}
                    {currentPax.seat && currentPax.checkin && <span> | </span>}
                    {currentPax.checkin && <span>Check-in: <span style={{ color: "var(--text)" }}>{currentPax.checkin}</span></span>}
                  </div>
                )}
                {currentPax.meal && currentPax.meal !== "Standard" && <div>Meal: <span style={{ color: "var(--text)" }}>{currentPax.meal}</span></div>}
                {currentPax.meal === "Standard" && <div>Meal: Standard</div>}
                {(() => {
                  const cl = currentPax.cabin_luggage ? (typeof currentPax.cabin_luggage === "string" ? JSON.parse(currentPax.cabin_luggage) : currentPax.cabin_luggage) : null;
                  if (cl && (cl.items || cl.weight || cl.size)) {
                    const parts = [];
                    if (cl.items) parts.push(`${cl.items} item${cl.items > 1 ? "s" : ""}`);
                    if (cl.weight) parts.push(cl.weight);
                    if (cl.size) parts.push(`(${cl.size})`);
                    return <div>Cabin: {parts.join(", ")}</div>;
                  }
                  return null;
                })()}
                {(() => {
                  const cl = currentPax.checked_luggage ? (typeof currentPax.checked_luggage === "string" ? JSON.parse(currentPax.checked_luggage) : currentPax.checked_luggage) : null;
                  if (cl && (cl.items || cl.weight)) {
                    const parts = [];
                    if (cl.items) parts.push(`${cl.items} item${cl.items > 1 ? "s" : ""}`);
                    if (cl.weight) parts.push(cl.weight);
                    return <div>Checked: {parts.join(", ")}</div>;
                  }
                  return null;
                })()}
                {currentPax.notes && <div style={{ marginTop: 2, fontStyle: "italic", color: "var(--text-muted)" }}>{currentPax.notes}</div>}
              </div>

              {/* Lounge Details — shown in view mode when this passenger has lounge access */}
              {currentPax.lounge_access && (
                <div style={{ marginBottom: 8, padding: 10, background: "var(--sidebar)", borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)" }}>Lounge Details</div>
                    {loungeInfo && !loungeEditing && <span onClick={() => { setLoungeEditing(true); setLoungeEditData({ ...loungeInfo }); }} style={{ fontSize: 11, color: "var(--text-muted)", cursor: "pointer", textDecoration: "underline" }}>Edit</span>}
                    {loungeEditing && <span onClick={() => { setLoungeInfo(loungeEditData); setLoungeEditing(false); }} style={{ fontSize: 11, color: "var(--green)", cursor: "pointer", textDecoration: "underline" }}>Save</span>}
                  </div>
                  {loungeLoading ? <div style={{ color: "var(--text-dim)", fontSize: 12 }}>Looking up lounge...</div> : loungeEditing && loungeEditData ? (
                    <div style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
                      <input className="input" value={loungeEditData.lounge_name || ""} onChange={e => setLoungeEditData(d => ({ ...d, lounge_name: e.target.value }))} placeholder="Lounge name" style={{ fontSize: 12, padding: "4px 8px" }} />
                      <input className="input" value={loungeEditData.location || ""} onChange={e => setLoungeEditData(d => ({ ...d, location: e.target.value }))} placeholder="Location" style={{ fontSize: 12, padding: "4px 8px" }} />
                      <input className="input" value={loungeEditData.hours || ""} onChange={e => setLoungeEditData(d => ({ ...d, hours: e.target.value }))} placeholder="Hours" style={{ fontSize: 12, padding: "4px 8px" }} />
                      <input className="input" value={loungeEditData.amenities || ""} onChange={e => setLoungeEditData(d => ({ ...d, amenities: e.target.value }))} placeholder="Amenities" style={{ fontSize: 12, padding: "4px 8px" }} />
                      <input className="input" value={loungeEditData.tip || ""} onChange={e => setLoungeEditData(d => ({ ...d, tip: e.target.value }))} placeholder="Tip" style={{ fontSize: 12, padding: "4px 8px" }} />
                      <span onClick={() => setLoungeEditing(false)} style={{ fontSize: 11, color: "var(--text-muted)", cursor: "pointer", marginTop: 2 }}>Cancel</span>
                    </div>
                  ) : loungeInfo ? (
                    <div style={{ fontSize: 13 }}>
                      <div style={{ color: "#fff", fontWeight: 600, marginBottom: 4 }}>{loungeInfo.lounge_name}</div>
                      {loungeInfo.location && <div style={{ color: "var(--text-dim)", marginBottom: 2 }}>{"\ud83d\udccd"} {loungeInfo.location}</div>}
                      {loungeInfo.hours && <div style={{ color: "var(--text-dim)", marginBottom: 2 }}>{"\ud83d\udd50"} {loungeInfo.hours}</div>}
                      {loungeInfo.partnership && <div style={{ color: "var(--text-dim)", marginBottom: 2 }}>{loungeInfo.partnership}</div>}
                      {loungeInfo.amenities && <div style={{ color: "var(--text-dim)", marginBottom: 2 }}>{"\u2728"} {loungeInfo.amenities}</div>}
                      {loungeInfo.tip && <div style={{ color: "var(--green)", fontStyle: "italic", marginTop: 4 }}>{"\ud83d\udca1"} {loungeInfo.tip}</div>}
                    </div>
                  ) : null}
                </div>
              )}

              <div style={{ display: "flex", gap: 6, justifyContent: "space-between" }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setPassengerEditing(true); setPassengerEdits({}); }}>Edit Passenger</button>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={addPassenger} title="Add passenger">+ Add</button>
                  {passengerDetails.length > 1 && (
                    <button className="btn btn-danger btn-sm" onClick={() => removePassenger(selectedPassenger)} title="Remove selected passenger">Remove</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── EDIT MODE ── */}
          {passengerEditing && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 8 }}>{currentPax.name}</div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={currentPax.checked_in || false}
                    onChange={(e) => setPassengerEdits(prev => ({ ...prev, checked_in: e.target.checked }))}
                    style={{ accentColor: "var(--green)" }} />
                  <span style={{ color: currentPax.checked_in ? "var(--green)" : "var(--text-muted)" }}>
                    {currentPax.checked_in ? "Checked In" : "Not Checked In"}
                  </span>
                </label>
              </div>
              {/* Fare Type & Lounge Access */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Fare Type</div>
                  <select
                    value={currentPax.fare_type || ""}
                    onChange={e => setPassengerEdits(prev => ({ ...prev, fare_type: e.target.value }))}
                    style={{ width: "100%", background: "var(--sidebar)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}>
                    <option value="">Select...</option>
                    <option value="Lite">Lite</option>
                    <option value="Economy">Economy</option>
                    <option value="Premium Economy">Premium Economy</option>
                    <option value="Business">Business</option>
                    <option value="Premium Business">Premium Business</option>
                    <option value="First Class">First Class</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Lounge Access</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 4 }}>
                    <input type="checkbox" checked={!!currentPax.lounge_access}
                      onChange={(e) => { setPassengerEdits(prev => ({ ...prev, lounge_access: e.target.checked })); if (e.target.checked) triggerLoungeLookup(); }}
                      style={{ width: 18, height: 18, accentColor: "var(--green)" }} />
                    <span style={{ fontSize: 13, color: currentPax.lounge_access ? "var(--green)" : "var(--text-dim)" }}>
                      {currentPax.lounge_access ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </div>
              {/* Seat, Check-in, Meal */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Seat</div>
                  <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                    value={currentPax.seat || ""} placeholder="e.g. 14A"
                    onChange={e => setPassengerEdits(prev => ({ ...prev, seat: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Check-in #</div>
                  <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                    value={currentPax.checkin || ""} placeholder="e.g. ABC123"
                    onChange={e => setPassengerEdits(prev => ({ ...prev, checkin: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Meal</div>
                  <select
                    value={currentPax.meal || "Standard"}
                    onChange={e => setPassengerEdits(prev => ({ ...prev, meal: e.target.value }))}
                    style={{ width: "100%", background: "var(--sidebar)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}>
                    <option value="Standard">Standard</option>
                    <option value="No Meal">No Meal</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Halal">Halal</option>
                    <option value="Kosher">Kosher</option>
                    <option value="Gluten Free">Gluten Free</option>
                  </select>
                </div>
              </div>
              {/* Luggage */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 6 }}>Luggage</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Cabin Items</div>
                    <input className="input" type="number" min="0" style={{ fontSize: 13, padding: "6px 8px" }}
                      value={(() => { const cl = currentPax.cabin_luggage; return cl ? (typeof cl === "string" ? JSON.parse(cl) : cl).items || "" : ""; })()}
                      placeholder="0"
                      onChange={e => {
                        const existing = currentPax.cabin_luggage ? (typeof currentPax.cabin_luggage === "string" ? JSON.parse(currentPax.cabin_luggage) : currentPax.cabin_luggage) : {};
                        setPassengerEdits(prev => ({ ...prev, cabin_luggage: { ...existing, items: parseInt(e.target.value) || 0 } }));
                      }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Cabin Weight</div>
                    <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                      value={(() => { const cl = currentPax.cabin_luggage; return cl ? (typeof cl === "string" ? JSON.parse(cl) : cl).weight || "" : ""; })()}
                      placeholder="7kg"
                      onChange={e => {
                        const existing = currentPax.cabin_luggage ? (typeof currentPax.cabin_luggage === "string" ? JSON.parse(currentPax.cabin_luggage) : currentPax.cabin_luggage) : {};
                        setPassengerEdits(prev => ({ ...prev, cabin_luggage: { ...existing, weight: e.target.value } }));
                      }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Cabin Size</div>
                    <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                      value={(() => { const cl = currentPax.cabin_luggage; return cl ? (typeof cl === "string" ? JSON.parse(cl) : cl).size || "" : ""; })()}
                      placeholder="55x40x20cm"
                      onChange={e => {
                        const existing = currentPax.cabin_luggage ? (typeof currentPax.cabin_luggage === "string" ? JSON.parse(currentPax.cabin_luggage) : currentPax.cabin_luggage) : {};
                        setPassengerEdits(prev => ({ ...prev, cabin_luggage: { ...existing, size: e.target.value } }));
                      }} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Checked Items</div>
                    <input className="input" type="number" min="0" style={{ fontSize: 13, padding: "6px 8px" }}
                      value={(() => { const cl = currentPax.checked_luggage; return cl ? (typeof cl === "string" ? JSON.parse(cl) : cl).items || "" : ""; })()}
                      placeholder="0"
                      onChange={e => {
                        const existing = currentPax.checked_luggage ? (typeof currentPax.checked_luggage === "string" ? JSON.parse(currentPax.checked_luggage) : currentPax.checked_luggage) : {};
                        setPassengerEdits(prev => ({ ...prev, checked_luggage: { ...existing, items: parseInt(e.target.value) || 0 } }));
                      }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Checked Weight</div>
                    <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                      value={(() => { const cl = currentPax.checked_luggage; return cl ? (typeof cl === "string" ? JSON.parse(cl) : cl).weight || "" : ""; })()}
                      placeholder="23kg"
                      onChange={e => {
                        const existing = currentPax.checked_luggage ? (typeof currentPax.checked_luggage === "string" ? JSON.parse(currentPax.checked_luggage) : currentPax.checked_luggage) : {};
                        setPassengerEdits(prev => ({ ...prev, checked_luggage: { ...existing, weight: e.target.value } }));
                      }} />
                  </div>
                </div>
              </div>
              {/* Additional Info / Notes */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Additional Info</div>
                <input className="input" style={{ fontSize: 13, padding: "6px 8px" }}
                  value={currentPax.notes || ""} placeholder="e.g. Window preferred"
                  onChange={e => setPassengerEdits(prev => ({ ...prev, notes: e.target.value }))} />
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-primary btn-sm" disabled={passengerSaving}
                    onClick={savePassengerDetail}>{passengerSaving ? "Saving..." : "Save"}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setPassengerEdits({}); setPassengerEditing(false); }}>Cancel</button>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={addPassenger} title="Add passenger">+ Add</button>
                  {passengerDetails.length > 1 && (
                    <button className="btn btn-danger btn-sm" onClick={() => removePassenger(selectedPassenger)} title="Remove selected passenger">Remove</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Editable notes */}
      <div style={{ padding: "10px 20px", borderTop: "1px solid var(--border)" }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)", marginBottom: 4 }}>Additional Information</div>
        {editingNotes === seg.id ? (
          <div>
            <textarea className="input" style={{ minHeight: 50, fontSize: 13 }} value={editNotesText}
              onChange={(e) => setEditNotesText(e.target.value)} onClick={(e) => e.stopPropagation()} />
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); onUpdateNotes(seg.id, editNotesText); setEditingNotes(null); }}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingNotes(null); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditingNotes(seg.id); setEditNotesText(seg.notes || ""); }}>
            {seg.notes ? <Truncated text={seg.notes} lines={4} style={{ color: "var(--text-dim)", fontSize: 13 }} /> : <div style={{ color: "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>Click to add notes…</div>}
          </div>
        )}
      </div>

      {/* Flight status */}
      <div style={{ padding: "10px 20px", borderTop: "1px solid var(--border)" }}>
        {!flightStatus && !statusLoading && (
          <button className="btn btn-secondary" style={{ width: "100%", padding: "10px", fontSize: 13 }}
            onClick={(e) => { e.stopPropagation(); checkFlightStatus(); }}
            disabled={!seg.flight_number}>
            {seg.flight_number ? `✈ Check ${seg.flight_number} Status` : "No flight number — cannot check status"}
          </button>
        )}
        {statusLoading && (
          <div style={{ textAlign: "center", padding: 8, color: "var(--text-dim)", fontSize: 13 }}>Checking flight status...</div>
        )}
        {flightStatus && !flightStatus.error && flightStatus.status !== "unknown" && flightStatus.status !== "not_found" && (
          <div style={{ padding: 8, background: "var(--sidebar)", borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)" }}>Flight Status</div>
              <span className={`badge ${flightStatus.status === "landed" || flightStatus.status === "active" ? "badge-confirmed" : flightStatus.status === "cancelled" ? "badge-unconfirmed" : "badge-tentative"}`}>
                {flightStatus.status || "scheduled"}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
              <div>
                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Departure</div>
                {flightStatus.departure_delay ? (
                  <div style={{ color: "var(--amber)" }}>Delayed {flightStatus.departure_delay} min</div>
                ) : (
                  <div style={{ color: "var(--green)" }}>On time</div>
                )}
                {flightStatus.departure_gate && <div style={{ color: "#fff" }}>Gate {flightStatus.departure_gate}</div>}
                {flightStatus.departure_actual && <div style={{ color: "#ccc", fontSize: 12 }}>{flightStatus.status === "scheduled" ? "Scheduled" : "Actual"}: {flightStatus.departure_actual.replace(/.*[T ](\d{2}:\d{2}).*/, "$1")}</div>}
              </div>
              <div>
                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Arrival</div>
                {flightStatus.arrival_delay ? (
                  <div style={{ color: "var(--amber)" }}>Delayed {flightStatus.arrival_delay} min</div>
                ) : (
                  <div style={{ color: "var(--green)" }}>On time</div>
                )}
                {flightStatus.arrival_gate && <div style={{ color: "#fff" }}>Gate {flightStatus.arrival_gate}</div>}
                {flightStatus.arrival_actual && <div style={{ color: "#ccc", fontSize: 12 }}>{flightStatus.status === "scheduled" ? "Scheduled" : "Actual"}: {flightStatus.arrival_actual.replace(/.*[T ](\d{2}:\d{2}).*/, "$1")}</div>}
              </div>
            </div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); checkFlightStatus(); }}>Refresh</button>
            </div>
          </div>
        )}
        {flightStatus && (flightStatus.status === "unknown" || flightStatus.status === "not_found") && (
          <div style={{ padding: 8, background: "var(--sidebar)", borderRadius: 8, fontSize: 13 }}>
            <div style={{ color: "var(--amber)", marginBottom: 4 }}>
              {flightStatus.message || "Flight status not available yet — data typically appears 24-48 hours before departure."}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); checkFlightStatus(); }}>Try Again</button>
          </div>
        )}
        {flightStatus?.error && (
          <div style={{ padding: 8, background: "var(--sidebar)", borderRadius: 8, fontSize: 13, color: "var(--amber)" }}>
            {flightStatus.error}
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={(e) => { e.stopPropagation(); checkFlightStatus(); }}>Retry</button>
          </div>
        )}
      </div>

      {/* Personal notes */}
      {canAnnotate && (
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--purple)", marginBottom: 4 }}>My Notes</div>
          {editingPersonalNote === seg.id ? (
            <div>
              <textarea className="input" style={{ minHeight: 50, fontSize: 13 }} value={personalNoteText}
                onChange={(e) => setPersonalNoteText(e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="Add your personal notes — seat preferences, reminders, etc." />
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); onSavePersonalNote(seg.id); }}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingPersonalNote(null); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditingPersonalNote(seg.id); setPersonalNoteText(personalNote || ""); }}>
              {personalNote ? <div style={{ color: "#d4a0ff", fontSize: 13 }}>{personalNote}</div> : <div style={{ color: "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>Click to add your personal notes…</div>}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {(onEdit || onDelete) && (
        <div style={{ padding: "8px 20px 12px", display: "flex", justifyContent: "flex-end", gap: 6 }}>
          {onEdit && <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); onEdit(); }}>Edit</button>}
          {onDelete && <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); if(window.confirm("Delete this flight?")) onDelete(seg.id); }}>Delete</button>}
        </div>
      )}
    </div>
  );
}

function CalendarView({ tripData, timeline, onNavigateToDay }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);

  if (!tripData) return <div className="empty-state"><h2>Select a trip first</h2></div>;

  // Determine which months to show
  const startDate = tripData.start_date ? new Date(tripData.start_date) : new Date();
  const endDate = tripData.end_date ? new Date(tripData.end_date) : new Date(startDate.getTime() + 90 * 86400000);
  const months = [];
  const d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  while (d <= endDate) {
    months.push({ year: d.getFullYear(), month: d.getMonth() });
    d.setMonth(d.getMonth() + 1);
  }
  const currentMonth = viewMonth || months[0];

  // Build segment map by date
  const segsByDate = {};
  for (const day of timeline) {
    if (day.segments.length > 0) segsByDate[day.date] = day.segments;
  }

  const { year, month } = currentMonth;
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  let week = new Array(firstDay).fill(null);
  for (let dd = 1; dd <= daysInMonth; dd++) {
    week.push(dd);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) { while (week.length < 7) week.push(null); weeks.push(week); }

  const monthName = new Date(year, month).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const tripStart = tripData.start_date;
  const tripEnd = tripData.end_date;

  // Expanded day view
  if (selectedDate) {
    const segs = segsByDate[selectedDate] || [];
    const dd = new Date(selectedDate + "T00:00:00");
    const dayName = dd.toLocaleDateString("en-AU", { weekday: "long" });
    const dayLabel = dd.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ cursor: "pointer", color: "var(--green)", fontSize: 14 }} onClick={() => setSelectedDate(null)}>← Back to calendar</span>
        </div>
        <h2 style={{ marginBottom: 4 }}><span style={{ color: "var(--green)" }}>{dayName}</span> {dayLabel}</h2>
        <div style={{ marginTop: 16 }}>
          {segs.length === 0 ? (
            <div style={{ padding: 20, color: "var(--green)", fontSize: 14, fontStyle: "italic" }}>Free day</div>
          ) : (
            segs.map((seg, i) => (
              <div key={i} className={`timeline-segment ${seg.type}`}
                style={{ cursor: "pointer" }}
                onClick={() => { if (onNavigateToDay) onNavigateToDay(selectedDate); }}>
                <div className="seg-icon">{getSegIcon(seg)}</div>
                <div className="seg-body">
                  <div className="seg-title">{seg.title}</div>
                  <div className="seg-detail">
                    {seg.start_location && seg.end_location && seg.start_location !== seg.end_location
                      ? `${seg.start_location} → ${seg.end_location}`
                      : seg.start_location || seg.end_location || ""}
                    {seg.provider && <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>· {seg.provider}</span>}
                  </div>
                  {seg.notes && <div style={{ marginTop: 4 }}><Truncated text={seg.notes} lines={2} style={{ fontSize: 12, color: "#b8b4cc" }} /></div>}
                  {seg.passengers && (() => {
                    try {
                      const p = JSON.parse(seg.passengers);
                      let pd = [];
                      try { pd = JSON.parse(seg.passenger_details || "[]"); } catch {}
                      return p.length > 0 ? (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                          {p.map((name, idx) => (
                            <span key={idx}>
                              {idx > 0 && ", "}
                              <span>{name}</span>
                              {pd[idx] && pd[idx].checked_in && <span style={{ color: "var(--green)", fontSize: 10 }}> ✓</span>}
                            </span>
                          ))}
                        </div>
                      ) : null;
                    } catch { return null; }
                  })()}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="seg-time">
                    {formatTimeRange(seg.start_datetime, seg.end_datetime, seg.type)}
                  </div>
                  <span className={`badge badge-${seg.type}`} style={{ marginTop: 4 }}>{seg.type}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={() => { if (onNavigateToDay) onNavigateToDay(selectedDate); }}>
            View in timeline →
          </button>
        </div>
      </div>
    );
  }

  // Month grid view
  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm"
          onClick={() => { const prev = months[months.findIndex(m => m.year === year && m.month === month) - 1]; if (prev) setViewMonth(prev); }}
          disabled={months[0]?.year === year && months[0]?.month === month}>
          ← Prev
        </button>
        <h2 style={{ margin: 0 }}>{monthName}</h2>
        <button className="btn btn-secondary btn-sm"
          onClick={() => { const next = months[months.findIndex(m => m.year === year && m.month === month) + 1]; if (next) setViewMonth(next); }}
          disabled={months[months.length - 1]?.year === year && months[months.length - 1]?.month === month}>
          Next →
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 2, overflow: "hidden" }}>
        {["S","M","T","W","T","F","S"].map((d, di) => (
          <div key={di} style={{ padding: 4, textAlign: "center", fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>{d}</div>
        ))}
        {weeks.flat().map((dd, i) => {
          if (!dd) return <div key={i} style={{ padding: 4 }} />;
          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
          const segs = segsByDate[dateStr] || [];
          const isToday = dateStr === localDateKey();
          const inTrip = tripStart && tripEnd ? dateStr >= tripStart && dateStr <= tripEnd : segs.length > 0;

          return (
            <div key={i} onClick={() => (inTrip || segs.length > 0) && setSelectedDate(dateStr)} style={{
              padding: 4, minHeight: 60, borderRadius: 6, cursor: inTrip || segs.length > 0 ? "pointer" : "default",
              background: inTrip ? "var(--sidebar)" : "rgba(255,255,255,0.01)",
              border: isToday ? "2px solid var(--green)" : inTrip ? "1px solid var(--border)" : "1px solid transparent",
              opacity: inTrip ? 1 : 0.4,
              transition: "all 0.15s",
            }}
              onMouseEnter={(e) => { if (inTrip) e.currentTarget.style.background = "var(--card)"; }}
              onMouseLeave={(e) => { if (inTrip) e.currentTarget.style.background = "var(--sidebar)"; }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? "var(--green)" : "#fff", marginBottom: 2 }}>{dd}</div>
              {segs.slice(0, 3).map((s, j) => (
                <div key={j} style={{ fontSize: 10, padding: "1px 3px", borderRadius: 3, marginBottom: 1, background: `${SEG_COLORS[s.type] || "var(--text-muted)"}20`, color: SEG_COLORS[s.type] || "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {getSegIcon(s)} {s.title.length > 12 ? s.title.slice(0, 12) + "…" : s.title}
                </div>
              ))}
              {segs.length > 3 && <div style={{ fontSize: 9, color: "var(--text-muted)" }}>+{segs.length - 3} more</div>}
              {segs.length === 0 && inTrip && <div style={{ fontSize: 9, color: "var(--green)", fontStyle: "italic" }}>Free</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// INBOX VIEW
// ══════════════════════════════════════════════════════════════════════════
function InboxView({ emails: emailsProp, loading, selectedTrip, trips, onUploadContent, onUploadFiles, onRefresh, authFetch, onSelectTrip }) {
  const emails = Array.isArray(emailsProp) ? emailsProp : [];
  // Trips eligible as assignment targets: not archived, not finished, and
  // not so far in the future that the picker turns into a wall of buttons.
  // Mirrors the iOS InboxView.assignableTrips filter so users see the
  // same shortlist on both platforms.
  const assignableTrips = (() => {
    const t0 = new Date(); t0.setHours(0, 0, 0, 0);
    const horizon = new Date(t0); horizon.setDate(horizon.getDate() + 365);
    return (trips || []).filter(trip => {
      if (trip.archived_at) return false;
      if (trip.end_date) {
        const end = new Date(trip.end_date); end.setHours(0, 0, 0, 0);
        if (end < t0) return false;
      }
      if (trip.start_date) {
        const start = new Date(trip.start_date); start.setHours(0, 0, 0, 0);
        if (start > horizon) return false;
      }
      return true;
    });
  })();
  const [pasteContent, setPasteContent] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillProgress, setBackfillProgress] = useState(null); // { current, total }
  const fileRef = useRef();

  const runBackfill = async () => {
    setBackfilling(true); setBackfillResult(null);
    let reparsed = 0, failed = 0, segmentsUpdated = 0;
    const total = emails.length;
    setBackfillProgress({ current: 0, total });
    for (let i = 0; i < emails.length; i++) {
      const em = emails[i];
      setBackfillProgress({ current: i + 1, total });
      try {
        const r = await authFetch(`/api/emails/${em.id}/reparse`, { method: "POST" });
        const data = await r.json();
        if (r.ok) {
          reparsed++;
          segmentsUpdated += data.policy_updated || 0;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    setBackfillResult({ emails_scanned: total, emails_reparsed: reparsed, emails_failed: failed, segments_updated: segmentsUpdated });
    setBackfillProgress(null);
    setBackfilling(false);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = [...e.dataTransfer.files];
    if (files.length > 0) handleFiles(files);
  };

  const handleFiles = async (files) => {
    console.log("Uploading", files.length, "file(s):", files.map(f => f.name).join(", "));
    try {
      const result = await onUploadFiles(files);
      console.log("Upload result:", result);
      setParseResult(result);
    } catch (err) {
      console.error("Upload error:", err);
      setParseResult({ error: err.message });
    }
  };

  const handlePaste = async () => {
    if (!pasteContent.trim()) return;
    const result = await onUploadContent(pasteContent);
    setParseResult(result);
    setPasteContent("");
  };

  const assignToTrip = async (emailId, tripId) => {
    const r = await authFetch(`/api/emails/${emailId}/assign`, {
      method: "POST", body: JSON.stringify({ trip_id: tripId }),
    });
    // Pick the chronologically-first newly-created segment so the timeline
    // jumps the user to the booking they just assigned (matches the iOS
    // "assign-and-jump" UX in InboxView.assignAndJump).
    let target = null;
    try {
      const j = await r.json();
      const segs = Array.isArray(j?.segments) ? j.segments : [];
      const first = segs
        .filter(s => s?.id)
        .sort((a, b) => String(a.start_datetime || "").localeCompare(String(b.start_datetime || "")))[0];
      if (first?.id) target = { segId: first.id };
    } catch {}
    onRefresh();
    onSelectTrip(tripId, target);
  };

  const [pendingOpen, setPendingOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-row expand state for the Pending-assignment list. Clicking a row
  // toggles open; opening fetches /api/emails/:id so we can show the
  // parsed segments inline (subject alone isn't enough to decide which
  // trip to assign to).
  const [expandedPendingId, setExpandedPendingId] = useState(null);
  const [expandedPendingSegments, setExpandedPendingSegments] = useState([]);
  const [expandedPendingLoading, setExpandedPendingLoading] = useState(false);
  const togglePendingDetail = async (em) => {
    if (expandedPendingId === em.id) {
      setExpandedPendingId(null);
      setExpandedPendingSegments([]);
      return;
    }
    setExpandedPendingId(em.id);
    setExpandedPendingSegments([]);
    setExpandedPendingLoading(true);
    try {
      const r = await authFetch(`/api/emails/${em.id}`);
      if (r && r.ok) {
        const d = await r.json();
        setExpandedPendingSegments(Array.isArray(d.segments) ? d.segments : []);
      }
    } catch {}
    setExpandedPendingLoading(false);
  };
  const dismissEmail = async (emailId) => {
    try { await authFetch(`/api/emails/${emailId}`, { method: "DELETE" }); onRefresh(); } catch {}
  };
  const dismissAll = async (ids) => {
    if (!window.confirm(`Permanently delete ${ids.length} unassigned email${ids.length === 1 ? "" : "s"}? This can't be undone, but the segments in your trips are unaffected.`)) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await authFetch(`/api/emails/${id}`, { method: "DELETE" });
    } finally { setBulkBusy(false); onRefresh(); }
  };

  return (
    <div>
      <div className="section-header">
        <h2>Inbox</h2>
        <button className="btn btn-secondary btn-sm" onClick={onRefresh}>Refresh</button>
      </div>

      {/* Drop zone */}
      <div className={`drop-zone ${dragOver ? "dragover" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}>
        <h3>{loading ? "Processing..." : "Drop booking emails here"}</h3>
        <p style={{ color: "var(--text-dim)", fontSize: 13 }}>
          Upload .eml, .pdf, or image files (.jpg/.png/.webp), or paste email HTML below. Claude will extract all bookings automatically.
        </p>
        <input ref={fileRef} type="file" accept=".eml,.msg,.html,.htm,.txt,.pdf,application/pdf,.jpg,.jpeg,.png,.webp,.gif,image/*" multiple style={{ display: "none" }}
          onChange={(e) => { if (e.target.files.length > 0) handleFiles([...e.target.files]); }} />
      </div>

      {/* Paste area */}
      <div style={{ marginTop: 16 }}>
        <textarea className="input" placeholder="Or paste booking confirmation HTML/text here..."
          value={pasteContent} onChange={(e) => setPasteContent(e.target.value)} />
        <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={handlePaste} disabled={!pasteContent.trim() || loading}>
          {loading ? "Parsing..." : "Parse Content"}
        </button>
      </div>

      {/* Parse result */}
      {parseResult?.error && (
        <div className="card" style={{ marginTop: 16, borderColor: "var(--red)" }}>
          <div style={{ color: "var(--red)", fontSize: 14 }}>Error: {parseResult.error}</div>
        </div>
      )}
      {parseResult?.segments?.length === 0 && parseResult?.email && (
        <div className="card" style={{ marginTop: 16, borderColor: "var(--amber)" }}>
          <div style={{ color: "var(--amber)", fontSize: 14 }}>No bookings found in this content. Parse status: {parseResult.email.parse_status}</div>
          {parseResult.email.parse_error && <div style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 4 }}>{parseResult.email.parse_error}</div>}
        </div>
      )}
      {parseResult?.segments?.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 8 }}>
            Extracted {parseResult.segments.length} booking(s)
            {parseResult.fileCount > 1 && ` from ${parseResult.fileCount} files`}
          </h3>
          {parseResult.segments.map((s, i) => (
            <div key={i} style={{ padding: 8, borderBottom: "1px solid var(--border)", fontSize: 13 }}>
              <span className={`badge badge-${s.type}`}>{s.type}</span>
              <span style={{ marginLeft: 8 }}>{s.title}</span>
              {s.start_datetime && <span className="mono" style={{ marginLeft: 8, color: "var(--text-muted)" }}>{formatDate(s.start_datetime)}</span>}
            </div>
          ))}
          {assignableTrips.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <span style={{ fontSize: 13, color: "var(--text-dim)" }}>Assign to trip: </span>
              {assignableTrips.map(t => (
                <button key={t.id} className="btn btn-secondary btn-sm" style={{ marginLeft: 4 }}
                  onClick={() => assignToTrip(parseResult.email?.id, t.id)}>{t.title}</button>
              ))}
            </div>
          ) : trips.length > 0 ? (
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
              No active trips in the next 12 months — create a trip or unarchive one to assign these bookings.
            </div>
          ) : null}
        </div>
      )}

      {/* TEMPORARY — one-shot policy backfill (remove once everyone has run it) */}
      {emails.length > 0 && (
        <div style={{ marginTop: 24, padding: 12, background: "rgba(255,171,64,0.06)", border: "1px solid rgba(255,171,64,0.2)", borderRadius: 8 }}>
          <div style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: 0.5, color: "var(--amber)", marginBottom: 4 }}>One-shot maintenance</div>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--text-dim)", marginBottom: 8 }}>
            Re-runs Claude on every email already in your inbox so the new policy fields (cancellation deadline, prepayment terms, fees, PIN/gate codes, WiFi, NP Pass) get extracted, then patches them onto your existing segments. Manual edits are preserved. Takes a minute or two.
          </div>
          <button className="btn btn-secondary btn-sm" onClick={runBackfill} disabled={backfilling}>
            {backfilling
              ? backfillProgress
                ? `Reparsing ${backfillProgress.current}/${backfillProgress.total}…`
                : "Reparsing…"
              : "Reparse all and backfill"}
          </button>
          {backfillResult && !backfillResult.error && (
            <span style={{ marginLeft: 10, fontSize: "var(--text-sm)", color: "var(--green)" }}>
              Scanned {backfillResult.emails_scanned} · reparsed {backfillResult.emails_reparsed ?? 0} · failed {backfillResult.emails_failed ?? 0} · updated {backfillResult.segments_updated} segment{backfillResult.segments_updated === 1 ? "" : "s"}
            </span>
          )}
          {backfillResult?.error && (
            <span style={{ marginLeft: 10, fontSize: "var(--text-sm)", color: "var(--red)" }}>{backfillResult.error}</span>
          )}
        </div>
      )}

      {/* Pending assignment — parsed emails that were never attached to a
          trip. Collapsed by default; many will be old duplicates of bookings
          already imported via other paths, so a bulk Dismiss is offered. */}
      {(() => {
        const pending = emails.filter(em => em.parse_status === "parsed" && !em.trip_id);
        if (pending.length === 0 || trips.length === 0) return null;
        const pendingIds = pending.map(e => e.id);
        return (
          <div style={{ marginTop: 24, background: "rgba(255,171,64,0.06)", border: "1px solid rgba(255,171,64,0.2)", borderRadius: 8 }}>
            <div onClick={() => setPendingOpen(o => !o)}
              style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
              <span style={{ fontSize: 16, color: "var(--amber)", transition: "transform 0.15s", transform: pendingOpen ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: 0.5, color: "var(--amber)" }}>
                  Pending assignment ({pending.length})
                </div>
                <div style={{ fontSize: "var(--text-sm)", color: "var(--text-dim)", marginTop: 4 }}>
                  Parsed emails not attached to a trip. Many will be old duplicates of bookings you've already imported.
                </div>
              </div>
              <button className="btn btn-danger btn-sm"
                onClick={(e) => { e.stopPropagation(); dismissAll(pendingIds); }}
                disabled={bulkBusy}>
                {bulkBusy ? "Dismissing…" : `Dismiss all ${pending.length}`}
              </button>
            </div>
            {pendingOpen && (
              <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                {pending.map(em => {
                  const isOpen = expandedPendingId === em.id;
                  return (
                  <div key={em.id} style={{ padding: 10, background: "var(--sidebar)", borderRadius: 6 }}>
                    <div
                      onClick={() => togglePendingDetail(em)}
                      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer", userSelect: "none" }}
                    >
                      <span style={{ fontSize: 10, color: "var(--text-muted)", transition: "transform 0.15s", transform: isOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block", width: 10 }}>▶</span>
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{em.subject || "Untitled"}</div>
                      <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{new Date(em.created_at).toLocaleDateString("en-AU")}</span>
                      <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); dismissEmail(em.id); }} title="Delete this email">×</button>
                    </div>
                    {isOpen && (
                      <div style={{ margin: "0 0 8px", padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }}>
                        {expandedPendingLoading ? (
                          <span style={{ color: "var(--text-muted)" }}>Loading parsed segments…</span>
                        ) : expandedPendingSegments.length === 0 ? (
                          <span style={{ color: "var(--text-muted)" }}>No segments extracted from this email.</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {expandedPendingSegments.map((s, i) => (
                              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <span style={{ fontSize: 14 }}>{SEG_ICONS[s.type] || "📌"}</span>
                                <span className={`badge badge-${s.type}`} style={{ fontSize: 10 }}>{s.type}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || "Untitled"}</span>
                                {s.start_datetime && <span className="mono" style={{ color: "var(--text-muted)", fontSize: 11 }}>{formatDate(s.start_datetime)}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Assign to:</span>
                      {assignableTrips.length > 0 ? assignableTrips.map(t => (
                        <button key={t.id} className="btn btn-secondary btn-sm"
                          onClick={() => assignToTrip(em.id, t.id)}>{t.title}</button>
                      )) : (
                        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          No active trips — create one or unarchive an old trip.
                        </span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Email list */}
      {emails.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: 16 }}>Previous uploads</h3>
          {emails.map(em => (
            <div key={em.id} className="email-list-item">
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{em.subject || "Untitled"}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {em.from_address && `From: ${em.from_address} · `}
                  {new Date(em.created_at).toLocaleDateString("en-AU")}
                  {em.trip_id ? "" : <span style={{ color: "var(--amber)", marginLeft: 6 }}>· unassigned</span>}
                </div>
              </div>
              <span className={`parse-status ${em.parse_status}`}>{em.parse_status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// AI ASSISTANT VIEW
// ══════════════════════════════════════════════════════════════════════════
function AIView({ tripData, briefing, excursions, packing, aiLoading, onBriefing, onExcursions, onPacking, authFetch }) {
  // Day plan state lives inline in the AI tab — separate from the
  // timeline-inline DaySuggestionDrawer because this surface picks
  // an arbitrary date via DatePicker rather than walking day cards.
  const [dpDate, setDpDate] = React.useState(() => localDateKey());
  const [dpInterest, setDpInterest] = React.useState("Anything");
  const [dpLoading, setDpLoading] = React.useState(false);
  const [dpResult, setDpResult] = React.useState(null);
  const [dpAddedKeys, setDpAddedKeys] = React.useState(new Set());
  const DP_INTERESTS = ["Anything", "Hotel activities", "Restaurants", "Sports", "Day trips", "Cultural", "Nature", "Shopping", "Nightlife"];
  // Same getLocationForDate semantics as TimelineView so the AI tab
  // sends the right city to /api/ai/day-suggestion (Tim's bug: iOS
  // was sending the whole trip's destination and getting ideas
  // across every city, including ones already visited).
  const getDpLocation = (date) => {
    const segs = tripData?.segments || [];
    const hotel = segs.find(s => (s.type === "hotel" || s.type === "friends_house")
      && s.start_datetime && s.end_datetime
      && date >= s.start_datetime.slice(0, 10) && date < s.end_datetime.slice(0, 10));
    if (hotel) return hotel.start_location || hotel.title || "";
    let last = "";
    for (const s of segs) {
      const segDate = (s.end_datetime || s.start_datetime || "").slice(0, 10);
      if (segDate && segDate <= date) last = s.end_location || s.start_location || s.title || last;
    }
    return last || (tripData?.destination?.split(",")[0].trim() || "");
  };
  const dpDestination = getDpLocation(dpDate);
  // Day index + planned-segment count for the picked date so the header
  // can show "Day 12 · 3 planned" or "free day" before any fetch fires.
  const dpDayMeta = React.useMemo(() => {
    if (!tripData) return { day: null, planned: 0 };
    let day = null;
    if (tripData.start_date) {
      const s = new Date(tripData.start_date + "T00:00:00Z").getTime();
      const d = new Date(dpDate + "T00:00:00Z").getTime();
      const diff = Math.round((d - s) / 86400000);
      if (diff >= 0) day = diff + 1;
    }
    const planned = (tripData.segments || []).filter((seg) =>
      (seg.start_datetime || "").slice(0, 10) === dpDate).length;
    return { day, planned };
  }, [tripData, dpDate]);
  const fetchDayPlan = async () => {
    if (!tripData) return;
    setDpLoading(true); setDpResult(null);
    try {
      const r = await authFetch("/api/ai/day-suggestion", {
        method: "POST",
        body: JSON.stringify({
          trip_id: tripData.id,
          destination: dpDestination,
          date: dpDate,
          interest: dpInterest === "Anything" ? "" : dpInterest,
        }),
      });
      const data = await r.json();
      setDpResult(data);
      setDpAddedKeys(new Set());
    } catch {}
    setDpLoading(false);
  };
  const savePlaceFromAiTab = async (place) => {
    if (!tripData) return;
    const key = (place.name || "").toLowerCase();
    if (dpAddedKeys.has(key)) return;
    try {
      await authFetch(`/api/trips/${tripData.id}/segments`, {
        method: "POST",
        body: JSON.stringify({
          type: "excursion",
          title: place.name || "Excursion",
          start_datetime: `${dpDate}T09:00`,
          notes: place.brief || "",
          status: "suggested",
        }),
      });
      setDpAddedKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    } catch {}
  };
  const [aiTab, setAiTab] = useState("briefing");
  const [askQuestion, setAskQuestion] = useState("");
  // Session-scoped Ask log (oldest first). Each entry:
  // { id, question, answer, pending }. Capped at 5 turns so older
  // questions roll off the front when new ones land. Resets when
  // the trip changes via the tripData dep.
  const [askHistory, setAskHistory] = useState([]);
  const [askLoading, setAskLoading] = useState(false);
  React.useEffect(() => { setAskHistory([]); }, [tripData?.id]);

  if (!tripData) return <div className="empty-state"><h2>Select a trip first</h2><p>AI features need a trip to work with.</p></div>;

  return (
    <div>
      <h2 style={{ marginBottom: 4 }}>AI Assistant</h2>
      <div style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 16 }}>{tripData.title} — {tripData.destination || (() => { try { const c = JSON.parse(tripData.route_cities); return [...new Set(c.slice(1))].join(", "); } catch { return ""; } })()}</div>

      <div className="tab-bar">
        <button className={`tab-btn ${aiTab === "briefing" ? "active" : ""}`} onClick={() => setAiTab("briefing")}>Briefing</button>
        <button className={`tab-btn ${aiTab === "excursions" ? "active" : ""}`} onClick={() => setAiTab("excursions")}>Excursions</button>
        <button className={`tab-btn ${aiTab === "packing" ? "active" : ""}`} onClick={() => setAiTab("packing")}>Packing</button>
        <button className={`tab-btn ${aiTab === "dayplan" ? "active" : ""}`} onClick={() => setAiTab("dayplan")}>Day plan</button>
        <button className={`tab-btn ${aiTab === "ask" ? "active" : ""}`} onClick={() => setAiTab("ask")}>Ask Anything</button>
      </div>

      {aiTab === "briefing" && (
        <div>
          {!briefing ? (
            <div className="empty-state">
              <h3>Destination Briefing</h3>
              <p style={{ marginBottom: 12 }}>Weather, safety, culture, events, and practical tips for {tripData.destination}.</p>
              <button className="btn btn-primary" onClick={onBriefing} disabled={aiLoading === "briefing"}>
                {aiLoading === "briefing" ? "Generating..." : "Generate Briefing"}
              </button>
            </div>
          ) : (
            <div className="card">
              {briefing.weather && (
                <div className="briefing-section">
                  <h4>Weather</h4>
                  <p>{briefing.weather.summary} — {briefing.weather.high_c}°C high / {briefing.weather.low_c}°C low, {briefing.weather.conditions}</p>
                </div>
              )}
              {briefing.safety && (
                <div className="briefing-section">
                  <h4>Safety</h4>
                  <p>Risk level: <strong>{briefing.safety.level}</strong> — {briefing.safety.notes}</p>
                </div>
              )}
              {briefing.culture && (
                <div className="briefing-section">
                  <h4>Culture & Practical</h4>
                  <p>Language: {briefing.culture.language} · Currency: {briefing.culture.currency} · Tipping: {briefing.culture.tipping}</p>
                  {briefing.culture.tips && <ul>{briefing.culture.tips.map((t,i) => <li key={i}>{t}</li>)}</ul>}
                </div>
              )}
              {briefing.practical && (
                <div className="briefing-section">
                  <h4>Practical Info</h4>
                  <p>Visa: {briefing.practical.visa}</p>
                  <p>Power: {briefing.practical.power}</p>
                  <p>Transport: {briefing.practical.transport}</p>
                  <p>Health: {briefing.practical.health}</p>
                </div>
              )}
              {briefing.events?.length > 0 && (
                <div className="briefing-section">
                  <h4>Events</h4>
                  <ul>{briefing.events.map((e,i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onBriefing}>Refresh</button>
            </div>
          )}
        </div>
      )}

      {aiTab === "excursions" && (
        <div>
          {!excursions ? (
            <div className="empty-state">
              <h3>Excursion Recommendations</h3>
              <p style={{ marginBottom: 12 }}>AI-suggested activities based on your destination and existing plans.</p>
              <button className="btn btn-primary" onClick={onExcursions} disabled={aiLoading === "excursions"}>
                {aiLoading === "excursions" ? "Generating..." : "Get Recommendations"}
              </button>
            </div>
          ) : (
            <div>
              {excursions.map((ex, i) => (
                <div key={i} className="card" style={{ marginBottom: 12 }}>
                  <h4 style={{ marginBottom: 4 }}>{ex.title}</h4>
                  <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 6 }}>{ex.description}</p>
                  <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)" }}>
                    {ex.duration && <span>Duration: {ex.duration}</span>}
                    {ex.difficulty && <span>Difficulty: {ex.difficulty}</span>}
                    {ex.cost_estimate && <span>Cost: {ex.cost_estimate}</span>}
                  </div>
                  {ex.booking_tip && <div style={{ fontSize: 12, color: "var(--amber)", marginTop: 4 }}>Tip: {ex.booking_tip}</div>}
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={onExcursions}>Refresh</button>
            </div>
          )}
        </div>
      )}

      {aiTab === "packing" && (
        <div>
          {!packing ? (
            <div className="empty-state">
              <h3>Packing List</h3>
              <p style={{ marginBottom: 12 }}>AI-generated packing list based on destination, weather, and planned activities.</p>
              <button className="btn btn-primary" onClick={onPacking} disabled={aiLoading === "packing"}>
                {aiLoading === "packing" ? "Generating..." : "Generate Packing List"}
              </button>
            </div>
          ) : (
            <div className="card">
              {Object.entries(packing).filter(([k]) => k !== "tips").map(([category, items]) => (
                <div key={category} className="packing-category">
                  <h4>{category}</h4>
                  {Array.isArray(items) ? items.map((item, i) => (
                    <div key={i} className="packing-item">☐ {item}</div>
                  )) : <div className="packing-item">{items}</div>}
                </div>
              ))}
              {packing.tips && (
                <div className="briefing-section" style={{ marginTop: 12 }}>
                  <h4>Tips</h4>
                  <ul>{packing.tips.map((t,i) => <li key={i}>{t}</li>)}</ul>
                </div>
              )}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onPacking}>Refresh</button>
            </div>
          )}
        </div>
      )}

      {aiTab === "dayplan" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
              <div>
                <h3 style={{ margin: 0 }}>Day plan</h3>
                <div style={{ fontSize: 12, marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {dpDestination && <span style={{ color: "var(--green)" }}>{dpDestination}</span>}
                  {dpDayMeta.day != null && <span style={{ color: "var(--text-muted)" }}>· Day {dpDayMeta.day}</span>}
                  {dpDayMeta.planned > 0
                    ? <span style={{ color: "var(--amber)" }}>· {dpDayMeta.planned} planned</span>
                    : dpDayMeta.day != null && <span style={{ color: "var(--text-dim)", fontStyle: "italic" }}>· free day</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="date" className="input" value={dpDate}
                  onChange={(e) => setDpDate(e.target.value)}
                  style={{ width: 150 }} />
                <button className="btn btn-primary btn-sm" onClick={fetchDayPlan} disabled={dpLoading}>
                  {dpLoading ? "…" : "Get ideas"}
                </button>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 12 }}>
              Pick a date and an interest. Suggestions are scoped to wherever you are on that day — not the whole trip.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {DP_INTERESTS.map(i => (
                <button key={i}
                  onClick={() => setDpInterest(i)}
                  style={{
                    padding: "3px 10px", borderRadius: 12, fontSize: 11,
                    border: dpInterest === i ? "1px solid var(--green)" : "1px solid var(--border)",
                    background: dpInterest === i ? "var(--green)" : "transparent",
                    color: dpInterest === i ? "#111" : "var(--text)",
                    cursor: "pointer", fontWeight: dpInterest === i ? 600 : 400,
                  }}>{i}</button>
              ))}
            </div>
            {dpLoading && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Thinking…</div>}
            {dpResult?.suggestion && (
              <div style={{ fontSize: 13, color: "var(--text-dim)", fontStyle: "italic", marginBottom: 12 }}>
                {dpResult.suggestion}
              </div>
            )}
            {dpResult?.places?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {dpResult.places.map((place, i) => {
                  const key = (place.name || "").toLowerCase();
                  const added = dpAddedKeys.has(key);
                  return (
                    <div key={i} style={{ padding: 10, background: "var(--card)", borderRadius: 8, display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: "var(--green)", textDecoration: added ? "line-through" : "none" }}>{place.name}</div>
                        {place.brief && <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>{place.brief}</div>}
                        {place.duration && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{place.duration}</div>}
                        {added && <div style={{ fontSize: 11, color: "var(--green)", marginTop: 2 }}>Added to your trip</div>}
                      </div>
                      <button onClick={() => savePlaceFromAiTab(place)} disabled={added}
                        style={{
                          background: "none", border: "none", color: "var(--green)",
                          fontSize: 22, cursor: added ? "default" : "pointer",
                          padding: 0, lineHeight: 1,
                        }}>
                        {added ? "✓" : "+"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {dpResult && (dpResult.places || []).length === 0 && !dpLoading && (
              <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
                Nothing came back — try a different interest.
              </div>
            )}
          </div>
        </div>
      )}

      {aiTab === "ask" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Ask Anything</h3>
              {askHistory.length > 0 && (
                <button
                  onClick={() => setAskHistory([])}
                  style={{
                    background: "none", border: "none", color: "var(--text-dim)",
                    fontSize: 12, cursor: "pointer", padding: 0,
                  }}>
                  Clear
                </button>
              )}
            </div>
            {askHistory.length === 0 && (
              <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 12 }}>Ask any question about your trip destination and get an instant answer.</p>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input className="input" style={{ flex: 1 }} placeholder="Ask anything about your trip destination..."
                value={askQuestion} onChange={e => setAskQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && askQuestion.trim()) handleAsk(); }} />
              <button className="btn btn-primary" onClick={handleAsk} disabled={!askQuestion.trim() || askLoading}>
                {askLoading ? "Thinking..." : "Ask"}
              </button>
            </div>
          </div>
          {askHistory.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {askHistory.map((turn) => (
                <div className="card" key={turn.id}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "var(--text-muted)" }}>Q</span>
                    <span style={{ fontSize: 13, color: "var(--text-dim)", fontStyle: "italic", lineHeight: 1.5 }}>{turn.question}</span>
                  </div>
                  {turn.pending ? (
                    <div style={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic", paddingLeft: 14 }}>
                      Thinking…
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "var(--green)" }}>A</span>
                      <span style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{turn.answer}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  async function handleAsk() {
    const q = askQuestion.trim();
    if (!q) return;
    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Snapshot prior completed turns BEFORE we append the new one;
    // the server replays them so Claude keeps the thread.
    const priorHistory = askHistory
      .filter((t) => !t.pending)
      .flatMap((t) => {
        const out = [{ role: "user", content: t.question }];
        if (t.answer) out.push({ role: "assistant", content: t.answer });
        return out;
      });
    setAskHistory((prev) => {
      const next = [...prev, { id: turnId, question: q, answer: "", pending: true }];
      return next.length > 5 ? next.slice(-5) : next;
    });
    setAskQuestion("");
    setAskLoading(true);
    try {
      const r = await authFetch("/api/ai/ask", {
        method: "POST",
        body: JSON.stringify({
          question: q,
          destination: tripData.destination || (tripData.route_cities ? (() => { try { const c = JSON.parse(tripData.route_cities); return [...new Set(c.slice(1))].join(", "); } catch { return ""; } })() : ""),
          dates: `${tripData.start_date || "upcoming"} to ${tripData.end_date || "upcoming"}`,
          trip_context: tripData.segments?.map(s => `${s.type}: ${s.title}`).join(", ") || "",
          current_location: tripData.today_city || null,
          history: priorHistory,
        }),
      });
      const data = await r.json();
      const ans = data.answer || data.error || "No answer received.";
      setAskHistory((prev) => prev.map((t) => t.id === turnId ? { ...t, answer: ans, pending: false } : t));
    } catch (err) {
      setAskHistory((prev) => prev.map((t) => t.id === turnId ? { ...t, answer: "Failed to get answer. Please try again.", pending: false } : t));
    } finally { setAskLoading(false); }
  }
}

// ══════════════════════════════════════════════════════════════════════════
// PROFILE VIEW
// ══════════════════════════════════════════════════════════════════════════
const COUNTRY_NAMES = {
  AU:"Australia", CL:"Chile", US:"United States", GB:"United Kingdom", FR:"France", DE:"Germany",
  IT:"Italy", ES:"Spain", NL:"Netherlands", PT:"Portugal", AT:"Austria", BE:"Belgium", IE:"Ireland",
  GR:"Greece", TH:"Thailand", VN:"Vietnam", KH:"Cambodia", MY:"Malaysia", SG:"Singapore",
  ID:"Indonesia", PH:"Philippines", JP:"Japan", KR:"South Korea", CN:"China", HK:"Hong Kong",
  TW:"Taiwan", IN:"India", NZ:"New Zealand", CA:"Canada", MX:"Mexico", BR:"Brazil",
  AR:"Argentina", CO:"Colombia", PE:"Peru", AE:"UAE", ZA:"South Africa", TR:"Turkey",
  IL:"Israel", SE:"Sweden", NO:"Norway", DK:"Denmark", FI:"Finland", IS:"Iceland", CH:"Switzerland",
  CZ:"Czech Republic", PL:"Poland", HU:"Hungary", HR:"Croatia", RO:"Romania",
};

const countryFlag = (code) => String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));


// Self-service account management: change email, set/change password,
// delete account. Collapsible so it's out of the way day-to-day.
function AccountCard({ user, authFetch, onLogout }) {
  const [open, setOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState(user.email || "");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null); // { ok?, error? }
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const saveEmail = async () => {
    const trimmed = emailDraft.trim();
    if (!trimmed || trimmed === user.email) return;
    setEmailBusy(true); setEmailMsg(null);
    try {
      const r = await authFetch("/api/auth/email", { method: "PUT", body: JSON.stringify({ email: trimmed }) });
      const data = await r.json();
      if (!r.ok) { setEmailMsg({ error: data.error || "Failed" }); }
      else {
        // Server reissues tokens with the new email; store them so the
        // refresh flow keeps working with the new identity.
        localStorage.setItem("tripiq_tokens", JSON.stringify(data));
        setEmailMsg({ ok: "Email updated. Reloading…" });
        setTimeout(() => window.location.reload(), 600);
      }
    } catch (e) { setEmailMsg({ error: e.message || "Connection failed" }); }
    setEmailBusy(false);
  };

  const savePassword = async () => {
    if (!newPw) return;
    setPwBusy(true); setPwMsg(null);
    try {
      const body = { password: newPw };
      if (curPw) body.currentPassword = curPw;
      const r = await authFetch("/api/auth/password", { method: "PUT", body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) { setPwMsg({ error: data.error || "Failed" }); }
      else {
        localStorage.setItem("tripiq_tokens", JSON.stringify(data));
        setPwMsg({ ok: "Password updated." });
        setCurPw(""); setNewPw("");
      }
    } catch (e) { setPwMsg({ error: e.message || "Connection failed" }); }
    setPwBusy(false);
  };

  const deleteAccount = async () => {
    const typed = window.prompt(`This will permanently delete your account and every trip you OWN (segments, attachments, all of it). Trips you've only been invited to are unaffected.\n\nType DELETE to confirm:`);
    if (typed !== "DELETE") return;
    try {
      const r = await authFetch("/api/auth/account", { method: "DELETE", body: JSON.stringify({ confirm: "DELETE" }) });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(j.error || "Delete failed");
        return;
      }
      // Account gone — clear the session and bounce to login.
      localStorage.removeItem("tripiq_tokens");
      if (onLogout) onLogout();
      window.location.href = "/";
    } catch (e) { alert(e.message || "Connection failed"); }
  };

  return (
    <div className="card" style={{ padding: 0 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
        <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#e8e6f0" }}>Account</div>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>
            Change email, set or update your password, or delete your account.
          </div>
        </div>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)", padding: 16, display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", marginBottom: 8 }}>Email</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="input" type="email" value={emailDraft} onChange={e => setEmailDraft(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={saveEmail} disabled={emailBusy || !emailDraft.trim() || emailDraft.trim() === user.email}>
                {emailBusy ? "Saving…" : "Save"}
              </button>
            </div>
            {emailMsg?.error && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 6 }}>{emailMsg.error}</div>}
            {emailMsg?.ok && <div style={{ fontSize: 12, color: "var(--green)", marginTop: 6 }}>{emailMsg.ok}</div>}
          </div>

          <div>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", marginBottom: 8 }}>Password</div>
            <input className="input" type="password" placeholder="Current password (leave blank if you don't have one)" value={curPw} onChange={e => setCurPw(e.target.value)} style={{ marginBottom: 8 }} />
            <input className="input" type="password" placeholder="New password" value={newPw} onChange={e => setNewPw(e.target.value)} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
              15+ characters, or 8+ with a number and a lowercase letter.
            </div>
            <button className="btn btn-primary" onClick={savePassword} disabled={pwBusy || !newPw}>
              {pwBusy ? "Saving…" : "Update password"}
            </button>
            {pwMsg?.error && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 6 }}>{pwMsg.error}</div>}
            {pwMsg?.ok && <div style={{ fontSize: 12, color: "var(--green)", marginTop: 6 }}>{pwMsg.ok}</div>}
          </div>

          <div>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--red)", marginBottom: 8 }}>Danger zone</div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 8 }}>
              Deletes your account and every trip you own. Trips you've been invited to are unaffected. Can't be undone.
            </div>
            <button className="btn btn-danger" onClick={deleteAccount}>Delete my account</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Lightweight viewer for the /api/client-errors sink — wraps the
// ErrorBoundary + window.error + reportError() captures into a collapsible
// card so the user can see what fired without ssh'ing into the box.
function RecentErrorsCard({ authFetch }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch("/api/client-errors?limit=50");
      if (!r || !r.ok) throw new Error(`HTTP ${r?.status || "?"}`);
      const j = await r.json();
      setRows(j.errors || []);
    } catch (e) { setErr(e.message || "Failed to load"); }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { if (open && !rows && !loading) load(); }, [open, rows, loading, load]);

  const fmt = (ts) => { try { return new Date(ts.replace(" ", "T") + "Z").toLocaleString("en-AU"); } catch { return ts; } };
  const recentCount = rows ? rows.length : null;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
        <span style={{ fontSize: 18, color: "var(--text-muted)", transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#e8e6f0" }}>Recent errors</div>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>
            Anything the ErrorBoundary or background JS caught. {recentCount === null ? "Tap to load." : `${recentCount} in the last 30 days.`}
          </div>
        </div>
        {recentCount > 0 && (
          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12, background: "rgba(255,82,82,0.15)", color: "var(--red)" }}>
            {recentCount}
          </span>
        )}
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)", padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setRows(null); load(); }} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {err && <div style={{ fontSize: 12, color: "var(--red)" }}>{err}</div>}
          {!err && rows && rows.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>No errors logged. ✨</div>
          )}
          {!err && rows && rows.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflowY: "auto" }}>
              {rows.map(r => (
                <details key={r.id} style={{ background: "var(--sidebar)", borderRadius: 6, padding: "8px 10px" }}>
                  <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", gap: 8, alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--ff-mono)", flexShrink: 0 }}>{fmt(r.created_at)}</span>
                    <span style={{ fontSize: 10, color: "var(--blue)", flexShrink: 0 }}>{r.source || "—"}</span>
                    <span style={{ fontSize: 12, color: "#e8e6f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{r.message}</span>
                  </summary>
                  {(r.stack || r.component_stack || r.url || r.user_agent) && (
                    <div style={{ marginTop: 8, fontSize: 11, fontFamily: "var(--ff-mono)", color: "var(--text-dim)" }}>
                      {r.url && <div style={{ marginBottom: 6, wordBreak: "break-all" }}>URL: {r.url}</div>}
                      {r.stack && <pre style={{ margin: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10 }}>{r.stack}</pre>}
                      {r.component_stack && <pre style={{ margin: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, color: "var(--text-muted)" }}>{r.component_stack}</pre>}
                      {r.user_agent && <div style={{ marginTop: 6, color: "var(--text-muted)", fontSize: 10 }}>UA: {r.user_agent}</div>}
                    </div>
                  )}
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileView({ user, authFetch, onLogout, installPrompt, isInstalled, onPromptUsed }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [addingLoyalty, setAddingLoyalty] = useState(false);
  const [passport, setPassport] = useState(null);
  const [newLoyaltyAirline, setNewLoyaltyAirline] = useState("");
  const [newLoyaltyCustom, setNewLoyaltyCustom] = useState("");
  const [newLoyaltyNumber, setNewLoyaltyNumber] = useState("");

  const LOYALTY_AIRLINES = [
    "Qantas Frequent Flyer", "Virgin Australia Velocity", "Virgin Atlantic", "Iberia Club",
    "Etihad Guest", "Qatar Airways Privilege Club", "British Airways Executive Club",
    "TAP Air Portugal", "Ryan Air", "Emirates Skywards", "China Southern Airlines",
    "Air Asia", "Vietnam Airlines", "Scoot", "Cathay Pacific Asia Miles",
    "Thai Airways Royal Orchid Plus", "Singapore Airlines KrisFlyer",
    "Malaysian Airlines Enrich", "LATAM Airlines LATAM Pass", "Icelandair Saga Club",
    "Bamboo Airlines", "SAS EuroBonus", "EasyJet", "Other",
  ];

  const [addingPastCountry, setAddingPastCountry] = useState(false);
  const [addingPastCity, setAddingPastCity] = useState(false);
  const [newPastCity, setNewPastCity] = useState("");

  const [form, setForm] = useState({
    fullName: "", dateOfBirth: "", nationality: "", phone: "",
    emergencyName: "", emergencyPhone: "", emergencyRelation: "",
    seatPref: "", mealPref: "", homeCurrency: "AUD",
    allergies: "", medications: "", medicalNotes: "",
    insuranceProvider: "", insurancePolicy: "", insurancePhone: "",
    passports: [{ number: "", country: "", issued: "", expiry: "" }],
    travelDocs: [], // [{ type, country, reference, expiry, passport_index, notes }]
    loyaltyPrograms: [],
    pastCountries: [],
    pastCities: [],
  });
  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleSection = (name) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    authFetch("/api/profile").then(r => r.json()).then(d => {
      if (d.profile) {
        const p = d.profile;
        // Migrate old single-passport data to passports array
        let passports = p.passports;
        if (!passports || !Array.isArray(passports) || passports.length === 0) {
          if (p.passportNumber || p.passportCountry || p.passportIssued || p.passportExpiry) {
            passports = [{ number: p.passportNumber || "", country: p.passportCountry || "", issued: p.passportIssued || "", expiry: p.passportExpiry || "" }];
          } else {
            passports = [{ number: "", country: "", issued: "", expiry: "" }];
          }
        }
        // Migrate old loyalty string to array
        let loyaltyPrograms = p.loyaltyPrograms;
        if (typeof loyaltyPrograms === "string") {
          loyaltyPrograms = loyaltyPrograms ? loyaltyPrograms.split(",").map(s => {
            const trimmed = s.trim();
            return { airline: trimmed, number: "" };
          }) : [];
        }
        if (!Array.isArray(loyaltyPrograms)) loyaltyPrograms = [];

        setForm({
          fullName: p.fullName || "", dateOfBirth: p.dateOfBirth || "",
          nationality: p.nationality || "", phone: p.phone || "",
          emergencyName: p.emergencyName || "", emergencyPhone: p.emergencyPhone || "",
          emergencyRelation: p.emergencyRelation || "",
          seatPref: p.seatPref || "", mealPref: p.mealPref || "",
          homeCurrency: p.homeCurrency || "AUD",
          allergies: p.allergies || "", medications: p.medications || "",
          medicalNotes: p.medicalNotes || "",
          insuranceProvider: p.insuranceProvider || "", insurancePolicy: p.insurancePolicy || "",
          insurancePhone: p.insurancePhone || "",
          passports,
          travelDocs: Array.isArray(p.travelDocs) ? p.travelDocs : [],
          loyaltyPrograms,
          pastCountries: Array.isArray(p.pastCountries) ? p.pastCountries : [],
          pastCities: Array.isArray(p.pastCities) ? p.pastCities : [],
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [authFetch]);

  // Fetch travel passport data
  useEffect(() => {
    authFetch("/api/passport").then(r => r.json()).then(d => {
      setPassport(d);
    }).catch(() => {});
  }, [authFetch]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    // Flatten passports[0] to old fields for backward compat, plus include the array
    const pp = form.passports[0] || {};
    const data = {
      fullName: form.fullName, dateOfBirth: form.dateOfBirth, nationality: form.nationality, phone: form.phone,
      emergencyName: form.emergencyName, emergencyPhone: form.emergencyPhone, emergencyRelation: form.emergencyRelation,
      passportNumber: pp.number, passportCountry: pp.country, passportExpiry: pp.expiry, passportIssued: pp.issued,
      seatPref: form.seatPref, mealPref: form.mealPref, homeCurrency: form.homeCurrency,
      allergies: form.allergies, medications: form.medications, medicalNotes: form.medicalNotes,
      insuranceProvider: form.insuranceProvider, insurancePolicy: form.insurancePolicy, insurancePhone: form.insurancePhone,
      passports: form.passports,
      travelDocs: form.travelDocs,
      loyaltyPrograms: form.loyaltyPrograms,
      pastCountries: form.pastCountries,
      pastCities: form.pastCities,
    };
    try {
      await authFetch("/api/profile", { method: "PUT", body: JSON.stringify({ profile: data }) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {} finally { setSaving(false); }
  };

  // Field helpers — defined as plain functions returning JSX (NOT components)
  // This prevents React from unmounting/remounting inputs on re-render
  const field = (label, key, type = "text", placeholder = "") => (
    <div className="form-group" style={{ marginBottom: 10 }} key={key}>
      <label htmlFor={`profile-${key}`}>{label}</label>
      <input className="input" id={`profile-${key}`} type={type} value={form[key] || ""} onChange={e => setField(key, e.target.value)} placeholder={placeholder} autoComplete="off" />
    </div>
  );

  const passportField = (label, idx, prop, type = "text", placeholder = "") => (
    <div className="form-group" style={{ marginBottom: 10 }} key={`pp-${idx}-${prop}`}>
      <label htmlFor={`passport-${idx}-${prop}`}>{label}</label>
      <input className="input" id={`passport-${idx}-${prop}`} type={type} value={(form.passports[idx] || {})[prop] || ""}
        onChange={e => {
          const updated = [...form.passports];
          updated[idx] = { ...updated[idx], [prop]: e.target.value };
          setField("passports", updated);
        }} placeholder={placeholder} autoComplete="off" />
    </div>
  );

  const addPassport = () => setField("passports", [...form.passports, { number: "", country: "", issued: "", expiry: "" }]);
  const removePassport = (idx) => {
    const updated = form.passports.filter((_, i) => i !== idx);
    setField("passports", updated.length ? updated : [{ number: "", country: "", issued: "", expiry: "" }]);
  };

  const addLoyalty = () => {
    const airline = newLoyaltyAirline === "Other" ? newLoyaltyCustom : newLoyaltyAirline;
    if (!airline) return;
    setField("loyaltyPrograms", [...form.loyaltyPrograms, { airline, number: newLoyaltyNumber }]);
    setNewLoyaltyAirline(""); setNewLoyaltyCustom(""); setNewLoyaltyNumber(""); setAddingLoyalty(false);
  };
  const removeLoyalty = (idx) => setField("loyaltyPrograms", form.loyaltyPrograms.filter((_, i) => i !== idx));

  const SectionHeader = ({ name, color, title }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: expandedSections.has(name) ? 14 : 0 }}>
      <h3 style={{ fontSize: 16, color }}>{title}</h3>
      <button className="btn btn-secondary btn-sm" onClick={() => toggleSection(name)}>
        {expandedSections.has(name) ? "Hide Details" : "Show Details"}
      </button>
    </div>
  );

  if (loading) return <div className="empty-state"><p>Loading profile...</p></div>;

  // Detect iOS Safari — Apple doesn't fire beforeinstallprompt, so the
  // button path is unavailable. We surface manual Share→Add to Home Screen
  // instructions instead.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafariIOS = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

  const handleInstall = async () => {
    if (!installPrompt) return;
    try {
      installPrompt.prompt();
      await installPrompt.userChoice;
    } catch {}
    if (onPromptUsed) onPromptUsed();
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="section-header">
        <h2>Profile</h2>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, paddingBottom: 70 }}>
        {/* Account — change email / set or change password / delete account */}
        <AccountCard user={user} authFetch={authFetch} onLogout={onLogout} />

        {/* Recent errors — surfaces what the ErrorBoundary + global handlers captured */}
        <RecentErrorsCard authFetch={authFetch} />

        {/* Install app card — only render when something useful to show */}
        {(isInstalled || installPrompt || isIOS) && (
          <div className="card" style={{ padding: 16, display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{isInstalled ? "✅" : "📲"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#e8e6f0", marginBottom: 4 }}>
                {isInstalled ? "TripIQ is installed" : "Install TripIQ"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.5 }}>
                {isInstalled
                  ? "Launches like a native app and caches your latest trip data so you can read it offline."
                  : installPrompt
                  ? "Adds TripIQ to your home screen, removes the browser chrome, and enables full offline reading of cached trips."
                  : isSafariIOS
                  ? <>Tap the <strong>Share</strong> icon in Safari, then <strong>Add to Home Screen</strong>. The app launches full-screen and caches your trips for offline reading.</>
                  : isIOS
                  ? "Open this page in Safari, then tap Share → Add to Home Screen. Other iOS browsers can't install web apps."
                  : "Use your browser's menu (look for 'Install app' or 'Add to Home Screen')."}
              </div>
            </div>
            {installPrompt && !isInstalled && (
              <button className="btn btn-primary" onClick={handleInstall} style={{ flexShrink: 0 }}>
                Install
              </button>
            )}
          </div>
        )}

        {/* Travel Passport */}
        {(() => {
          // Merge trip-derived data with manually added past travel.
          // Trip cities come from the server pre-cleaned (title-cased,
          // hotel-name junk dropped). Apply the same normalisation to
          // user-typed pastCities at display time so casing dups and
          // accidentally-typed hotel names don't bleed in.
          const tripCountries = passport ? passport.countries || [] : [];
          const tripCities = passport ? passport.cities || [] : [];
          const cleanCity = (raw) => {
            if (!raw || typeof raw !== "string") return null;
            let s = raw.trim().replace(/\s*\([^)]+\)\s*$/, "");
            if (!s || /^[A-Z]{2}$/.test(s)) return null;
            if (/^(awasi|tierra|alt\s+hotel|hotel)\s/i.test(s)) return null;
            if (/\s+airport\s*$/i.test(s)) return null;
            s = s.replace(/^british\s+colombia$/i, "British Columbia");
            return s.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, b, c) => b + c.toUpperCase());
          };
          const dedupCities = (arr) => {
            const seen = new Set(); const out = [];
            for (const raw of arr) {
              const c = cleanCity(raw); if (!c) continue;
              if (seen.has(c.toLowerCase())) continue;
              seen.add(c.toLowerCase()); out.push(c);
            }
            return out.sort((a, b) => a.localeCompare(b));
          };
          const allCountries = [...new Set([...tripCountries, ...(form.pastCountries || [])])];
          const allCities = dedupCities([...tripCities, ...(form.pastCities || [])]);
          const countryCount = allCountries.filter(c => c !== "AU").length;
          const cityCount = allCities.length;
          const tripCount = passport ? passport.totalTrips || 0 : 0;
          const nightCount = passport ? passport.totalNights || 0 : 0;
          const worldPct = Math.round(countryCount / 195 * 1000) / 10;

          const addPastCountry = (code) => {
            if (!code || form.pastCountries.includes(code)) return;
            const updated = [...form.pastCountries, code];
            setField("pastCountries", updated);
            setAddingPastCountry(false);
            // Auto-save
            const pp = form.passports[0] || {};
            authFetch("/api/profile", { method: "PUT", body: JSON.stringify({ profile: {
              ...form, pastCountries: updated,
              passportNumber: pp.number, passportCountry: pp.country, passportExpiry: pp.expiry, passportIssued: pp.issued,
            }})}).catch(() => {});
          };
          const removePastCountry = (code) => {
            const updated = form.pastCountries.filter(c => c !== code);
            setField("pastCountries", updated);
            const pp = form.passports[0] || {};
            authFetch("/api/profile", { method: "PUT", body: JSON.stringify({ profile: {
              ...form, pastCountries: updated,
              passportNumber: pp.number, passportCountry: pp.country, passportExpiry: pp.expiry, passportIssued: pp.issued,
            }})}).catch(() => {});
          };
          const addPastCity = async (city) => {
            const trimmed = (city || "").trim();
            if (!trimmed || form.pastCities.includes(trimmed)) return;
            const updatedCities = [...form.pastCities, trimmed];
            setField("pastCities", updatedCities);
            setNewPastCity("");
            setAddingPastCity(false);
            // Auto-detect country from city and add if not already present
            let updatedCountries = form.pastCountries;
            try {
              const geo = await authFetch(`/api/geocode?city=${encodeURIComponent(trimmed)}`);
              const geoData = await geo.json();
              if (geoData.country_code && !allCountries.includes(geoData.country_code) && !form.pastCountries.includes(geoData.country_code)) {
                updatedCountries = [...form.pastCountries, geoData.country_code];
                setField("pastCountries", updatedCountries);
              }
            } catch {}
            const pp = form.passports[0] || {};
            authFetch("/api/profile", { method: "PUT", body: JSON.stringify({ profile: {
              ...form, pastCities: updatedCities, pastCountries: updatedCountries,
              passportNumber: pp.number, passportCountry: pp.country, passportExpiry: pp.expiry, passportIssued: pp.issued,
            }})}).catch(() => {});
          };
          const removePastCity = (city) => {
            const updated = form.pastCities.filter(c => c !== city);
            setField("pastCities", updated);
            const pp = form.passports[0] || {};
            authFetch("/api/profile", { method: "PUT", body: JSON.stringify({ profile: {
              ...form, pastCities: updated,
              passportNumber: pp.number, passportCountry: pp.country, passportExpiry: pp.expiry, passportIssued: pp.issued,
            }})}).catch(() => {});
          };

          return (
          <div className="card" style={{ borderColor: "var(--green)", borderWidth: 1 }}>
            <h3 style={{ fontSize: 18, color: "var(--green)", marginBottom: 20 }}>🛂 Travel Passport</h3>

            {/* Stats grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 24 }}>
              {[
                { icon: "🌍", value: countryCount, label: "Countries" },
                { icon: "🏙", value: cityCount, label: "Cities & regions" },
                { icon: "✈️", value: tripCount, label: "Trips" },
                { icon: "🌙", value: nightCount, label: "Nights Away" },
              ].map((s, i) => (
                <div key={i} style={{ background: "var(--sidebar)", borderRadius: 12, padding: "16px 12px", textAlign: "center", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "var(--green)", fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
                </div>
              ))}
              <div style={{ background: "var(--sidebar)", borderRadius: 12, padding: "16px 12px", textAlign: "center", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>🗺️</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "var(--green)", fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{worldPct}%</div>
                <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Explored</div>
                <div style={{ background: "var(--border)", borderRadius: 4, height: 5, marginTop: 8, overflow: "hidden" }}>
                  <div style={{ background: "var(--green)", height: "100%", width: `${Math.min(100, worldPct)}%`, borderRadius: 4, transition: "width 0.5s" }} />
                </div>
              </div>
            </div>

            {/* Countries section */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: 0.5 }}>Countries Visited</div>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 10px" }}
                  onClick={() => setAddingPastCountry(!addingPastCountry)}>
                  {addingPastCountry ? "Cancel" : "+ Add Country"}
                </button>
              </div>
              {addingPastCountry && (
                <div style={{ marginBottom: 10 }}>
                  <select className="input" style={{ fontSize: 13 }} value="" onChange={e => addPastCountry(e.target.value)}>
                    <option value="">Select a country...</option>
                    {Object.entries(COUNTRY_NAMES)
                      .filter(([code]) => !allCountries.includes(code))
                      .sort((a, b) => a[1].localeCompare(b[1]))
                      .map(([code, name]) => (
                        <option key={code} value={code}>{countryFlag(code)} {name}</option>
                      ))}
                  </select>
                </div>
              )}
              {allCountries.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {allCountries.map(code => {
                    const isPast = form.pastCountries.includes(code) && !tripCountries.includes(code);
                    return (
                      <div key={code} style={{
                        background: "var(--sidebar)", borderLeft: "3px solid var(--green)",
                        borderRadius: 8, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8,
                        fontSize: 13, color: "var(--text)", border: "1px solid var(--border)", borderLeftColor: "var(--green)", borderLeftWidth: 3,
                      }}>
                        <span style={{ fontSize: 28, lineHeight: 1 }}>{countryFlag(code)}</span>
                        <span>{COUNTRY_NAMES[code] || code}</span>
                        {isPast && (
                          <button onClick={() => removePastCountry(code)} style={{
                            background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer",
                            fontSize: 14, padding: "0 0 0 4px", lineHeight: 1,
                          }} title="Remove">x</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "var(--text-muted)", fontSize: 13, fontStyle: "italic" }}>
                  No countries yet. Add trips or add past travel below.
                </div>
              )}
            </div>

            {/* Cities section */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: 0.5 }}>Cities &amp; regions visited</div>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 10px" }}
                  onClick={() => setAddingPastCity(!addingPastCity)}>
                  {addingPastCity ? "Cancel" : "+ Add"}
                </button>
              </div>
              {addingPastCity && (
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input className="input" style={{ fontSize: 13, flex: 1 }} placeholder="City name..." value={newPastCity}
                    onChange={e => setNewPastCity(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addPastCity(newPastCity); }} />
                  <button className="btn btn-primary btn-sm" onClick={() => addPastCity(newPastCity)}>Add</button>
                </div>
              )}
              {allCities.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {allCities.map(city => {
                    const isPast = form.pastCities.includes(city) && !tripCities.includes(city);
                    return (
                      <span key={city} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        border: "1px solid rgba(0,230,118,0.3)", borderRadius: 20,
                        padding: "4px 12px", fontSize: 12, color: "var(--text)",
                        background: "rgba(0,230,118,0.06)",
                      }}>
                        {city}
                        {isPast && (
                          <button onClick={() => removePastCity(city)} style={{
                            background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer",
                            fontSize: 12, padding: 0, lineHeight: 1, marginLeft: 2,
                          }} title="Remove">x</button>
                        )}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "var(--text-muted)", fontSize: 13, fontStyle: "italic" }}>
                  No cities yet. Add trips or add past travel above.
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* Profile tags — Asian food / craft beer / supermarket / pharmacy /
            etc. Drives the "Near my hotel" panel on hotel cards in the
            timeline. Separate from the local-distinctives recommender. */}
        <ProfileTagsCard authFetch={authFetch} />

        {/* Push notifications — always-reachable toggle so it doesn't depend on
            the Smart Alerts panel being open with active alerts. */}
        <NotificationsCard authFetch={authFetch} />

        {/* Cross-trip attachments — every file the user has access to,
            grouped by trip. Useful when you can't remember which trip a
            voucher / photo / passport scan lives on. */}
        <AllAttachmentsPanel authFetch={authFetch} />

        {/* Cross-trip pinned places — 📌'd from Near-my-hotel. Surface
            on future hotels within 5 km of the anchor. */}
        <PinnedPlacesPanel authFetch={authFetch} />

        {/* Walking tours — ordered-stop lists exportable to Amap /
            Google / Apple Maps. Phase 4 feature; GCJ-02 shift is
            applied server-side at export time. */}
        <WalkingToursPanel authFetch={authFetch} />

        {/* Database backups — daily snapshot status + manual trigger */}
        <BackupCard authFetch={authFetch} />

        {/* Personal details */}
        <div className="card">
          <SectionHeader name="personal" color="var(--green)" title="Personal Details" />
          {expandedSections.has("personal") && (
            <>
              {field("Full Name (as on passport)", "fullName")}
              {field("Date of Birth", "dateOfBirth", "date")}
              {field("Nationality", "nationality", "text", "e.g. Australian")}
              {field("Phone", "phone", "text", "+61 4xx xxx xxx")}
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 13, color: "var(--amber)", marginBottom: 8 }}>Emergency Contact</h4>
                {field("Name", "emergencyName")}
                {field("Phone", "emergencyPhone")}
                {field("Relationship", "emergencyRelation", "text", "e.g. Partner, Parent")}
              </div>
            </>
          )}
        </div>

        {/* Passports */}
        <div className="card">
          <SectionHeader name="passport" color="var(--flight-color)" title="Passports" />
          {!expandedSections.has("passport") && (
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 8 }}>
              {form.passports.some(p => p.number) ? `${form.passports.filter(p => p.number).length} passport(s) on file` : "No passport details saved"}
            </div>
          )}
          {expandedSections.has("passport") && (
            <>
              <div style={{ fontSize: 12, color: "var(--amber)", marginBottom: 12, padding: "6px 10px", background: "rgba(255,171,64,0.08)", borderRadius: 6 }}>
                Passport details are encrypted at rest
              </div>
              {form.passports.map((pp, idx) => (
                <div key={idx} style={{ background: "var(--sidebar)", borderRadius: 10, padding: 14, marginBottom: 10, border: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Passport {idx + 1}{pp.country ? ` — ${pp.country}` : ""}</span>
                    {form.passports.length > 1 && (
                      <button className="btn btn-sm" style={{ color: "var(--red)", background: "none", border: "none", fontSize: 12, cursor: "pointer" }} onClick={() => removePassport(idx)}>Remove</button>
                    )}
                  </div>
                  {passportField("Passport Number", idx, "number")}
                  {passportField("Country of Issue", idx, "country", "text", "e.g. Australia")}
                  {passportField("Date of Issue", idx, "issued", "date")}
                  {passportField("Expiry Date", idx, "expiry", "date")}
                  {pp.expiry && (() => {
                    const daysLeft = daysUntil(pp.expiry);
                    if (daysLeft !== null && daysLeft < 180) {
                      return <div style={{ color: "var(--red)", fontSize: 12, marginTop: 4 }}>Warning: Passport expires in {daysLeft} days — some countries require 6 months validity</div>;
                    }
                    return null;
                  })()}
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addPassport} style={{ marginTop: 4 }}>+ Add Passport</button>
            </>
          )}
        </div>

        {/* Travel Documents (eTA / ETIAS / ESTA / eVisa) */}
        <div className="card">
          <SectionHeader name="travelDocs" color="var(--green)" title="Travel Documents" />
          {!expandedSections.has("travelDocs") && (
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 8 }}>
              {form.travelDocs.length > 0 ? `${form.travelDocs.length} document(s) on file` : "No eTA / ETIAS / eVisa records saved"}
            </div>
          )}
          {expandedSections.has("travelDocs") && (
            <>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                Stored alongside passports (encrypted). Used by the Visa panel to mark "on file" and by Smart Alerts to warn before expiry.
              </div>
              {form.travelDocs.map((doc, idx) => (
                <div key={idx} style={{ background: "var(--sidebar)", borderRadius: 10, padding: 14, marginBottom: 10, border: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                      {(doc.type || "Document").toUpperCase()} {doc.country ? `· ${doc.country}` : ""}
                    </span>
                    <button className="btn btn-sm" style={{ color: "var(--red)", background: "none", border: "none", fontSize: 12, cursor: "pointer" }} onClick={() => {
                      const next = form.travelDocs.filter((_, i) => i !== idx);
                      setField("travelDocs", next);
                    }}>Remove</button>
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Type</label>
                    <select className="input" value={doc.type || ""} onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], type: e.target.value }; setField("travelDocs", next);
                    }}>
                      <option value="">— Select —</option>
                      <option value="eta">eTA (Canada / Australia / NZ)</option>
                      <option value="etias">ETIAS (Schengen)</option>
                      <option value="esta">ESTA (United States)</option>
                      <option value="evisa">eVisa</option>
                      <option value="visa">Visa (paper)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Destination Country (ISO 2-letter)</label>
                    <input className="input" value={doc.country || ""} placeholder="e.g. CA, US, HU" onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], country: e.target.value.toUpperCase() }; setField("travelDocs", next);
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Linked Passport</label>
                    <select className="input" value={doc.passport_index ?? ""} onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], passport_index: e.target.value === "" ? null : parseInt(e.target.value) }; setField("travelDocs", next);
                    }}>
                      <option value="">Any passport</option>
                      {form.passports.map((p, i) => (
                        <option key={i} value={i}>Passport {i + 1}{p.country ? ` (${p.country})` : ""}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Reference / Authorisation Number</label>
                    <input className="input" value={doc.reference || ""} onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], reference: e.target.value }; setField("travelDocs", next);
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Expiry Date</label>
                    <input className="input" type="date" value={doc.expiry || ""} onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], expiry: e.target.value }; setField("travelDocs", next);
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label>Notes</label>
                    <input className="input" value={doc.notes || ""} placeholder="e.g. issued via canada.ca on 12 Mar 2026" onChange={e => {
                      const next = [...form.travelDocs]; next[idx] = { ...next[idx], notes: e.target.value }; setField("travelDocs", next);
                    }} />
                  </div>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={() => setField("travelDocs", [...form.travelDocs, { type: "", country: "", passport_index: null, reference: "", expiry: "", notes: "" }])} style={{ marginTop: 4 }}>+ Add Travel Document</button>
            </>
          )}
        </div>

        {/* Travel preferences */}
        <div className="card">
          <SectionHeader name="travel" color="var(--purple)" title="Travel Preferences" />
          {expandedSections.has("travel") && (
            <>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>Seat Preference</label>
                <select className="input" id="profile-seatPref" value={form.seatPref} onChange={e => setField("seatPref", e.target.value)}>
                  <option value="">No preference</option>
                  <option value="window">Window</option>
                  <option value="aisle">Aisle</option>
                  <option value="middle">Middle</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>Meal Preference</label>
                <select className="input" id="profile-mealPref" value={form.mealPref} onChange={e => setField("mealPref", e.target.value)}>
                  <option value="">Standard</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                  <option value="halal">Halal</option>
                  <option value="kosher">Kosher</option>
                  <option value="gluten-free">Gluten Free</option>
                  <option value="dairy-free">Dairy Free</option>
                  <option value="diabetic">Diabetic</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>Home Currency</label>
                <select className="input" id="profile-homeCurrency" value={form.homeCurrency} onChange={e => setField("homeCurrency", e.target.value)}>
                  <option value="AUD">AUD — Australian Dollar</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="GBP">GBP — British Pound</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="NZD">NZD — New Zealand Dollar</option>
                  <option value="CAD">CAD — Canadian Dollar</option>
                  <option value="SGD">SGD — Singapore Dollar</option>
                </select>
              </div>

              {/* Loyalty Programs */}
              <div style={{ marginTop: 12 }}>
                <label style={{ display: "block", fontSize: 12, color: "var(--text-dim)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Loyalty Programmes</label>
                {form.loyaltyPrograms.length > 0 && form.loyaltyPrograms.map((lp, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--sidebar)", borderRadius: 8, marginBottom: 6, border: "1px solid var(--border)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{lp.airline}</div>
                      {lp.number && <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "'DM Mono', monospace" }}>{lp.number}</div>}
                    </div>
                    <button className="btn btn-sm" style={{ color: "var(--red)", background: "none", border: "none", fontSize: 12, cursor: "pointer", flexShrink: 0 }} onClick={() => removeLoyalty(idx)}>Remove</button>
                  </div>
                ))}
                {form.loyaltyPrograms.length === 0 && !addingLoyalty && (
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>No loyalty programmes saved</div>
                )}
                {addingLoyalty ? (
                  <div style={{ background: "var(--sidebar)", borderRadius: 8, padding: 12, border: "1px solid var(--border)", marginTop: 6 }}>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label htmlFor="loyalty-airline">Airline</label>
                      <select className="input" id="loyalty-airline" value={newLoyaltyAirline} onChange={e => setNewLoyaltyAirline(e.target.value)}>
                        <option value="">Select airline...</option>
                        {LOYALTY_AIRLINES.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    {newLoyaltyAirline === "Other" && (
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label htmlFor="loyalty-custom">Airline Name</label>
                        <input className="input" id="loyalty-custom" value={newLoyaltyCustom} onChange={e => setNewLoyaltyCustom(e.target.value)} placeholder="Enter airline name" autoComplete="off" />
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label htmlFor="loyalty-number">Membership Number</label>
                      <input className="input" id="loyalty-number" value={newLoyaltyNumber} onChange={e => setNewLoyaltyNumber(e.target.value)} placeholder="e.g. 12345678" autoComplete="off" />
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-primary btn-sm" onClick={addLoyalty} disabled={!newLoyaltyAirline || (newLoyaltyAirline === "Other" && !newLoyaltyCustom)}>Add</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setAddingLoyalty(false); setNewLoyaltyAirline(""); setNewLoyaltyCustom(""); setNewLoyaltyNumber(""); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddingLoyalty(true)} style={{ marginTop: 4 }}>+ Add Programme</button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Medical & insurance */}
        <div className="card">
          <SectionHeader name="medical" color="var(--red)" title="Medical & Insurance" />
          {expandedSections.has("medical") && (
            <>
              {field("Allergies", "allergies", "text", "e.g. Penicillin, peanuts")}
              {field("Medications", "medications", "text", "Current medications")}
              {field("Medical Notes", "medicalNotes", "text", "Blood type, conditions, etc.")}
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 13, color: "var(--amber)", marginBottom: 8 }}>Travel Insurance</h4>
                {field("Provider", "insuranceProvider", "text", "e.g. Allianz, Cover-More")}
                {field("Policy Number", "insurancePolicy")}
                {field("Emergency Phone", "insurancePhone", "text", "24hr claims line")}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Account section */}
      <div className="card" style={{ marginTop: 0, marginBottom: 80 }}>
        <h3 style={{ fontSize: 16, marginBottom: 14, color: "var(--text-dim)" }}>Account</h3>
        <div style={{ fontSize: 14, color: "#fff", marginBottom: 4 }}>{user.name || "No name set"}</div>
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>{user.email}</div>
        <button className="btn btn-danger" style={{ width: "100%", padding: "12px", fontSize: 14 }} onClick={onLogout}>Sign Out</button>
      </div>

      {/* Floating save button */}
      <div style={{ position: "sticky", bottom: 0, left: 0, right: 0, padding: "12px 0", background: "linear-gradient(transparent, var(--bg) 30%)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        {saved && <span style={{ color: "var(--green)", fontSize: 13 }}>Saved</span>}
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: "12px 32px", fontSize: 15, fontWeight: 600, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,230,118,0.3)" }}>
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TRIP SELECTOR MODAL (post-upload)
// ══════════════════════════════════════════════════════════════════════════
function TripSelectorModal({ trips, segments, onSelectExisting, onCreateNew, onClose }) {
  const [mode, setMode] = useState(null); // null, "existing", "new"
  const [selectedTripId, setSelectedTripId] = useState("");

  // Auto-generate trip name and dates from segments
  const dates = segments.map(s => s.start_datetime?.split("T")[0]).filter(Boolean).sort();
  const endDates = segments.map(s => (s.end_datetime || s.start_datetime)?.split("T")[0]).filter(Boolean).sort();
  const startDate = dates[0] || "";
  const endDate = endDates[endDates.length - 1] || "";
  const cities = [...new Set(segments.filter(s => s.type === "flight").flatMap(s => [
    (s.start_location || "").replace(/\s*\(.*\)/, "").trim(),
    (s.end_location || "").replace(/\s*\(.*\)/, "").trim(),
  ]).filter(Boolean))];
  const autoTitle = cities.length > 1 ? `${cities[0]} to ${cities[cities.length - 1]}` : cities[0] || "New Trip";

  const [newTitle, setNewTitle] = useState(autoTitle);
  const [newStartDate, setNewStartDate] = useState(startDate);
  const [newEndDate, setNewEndDate] = useState(endDate);

  // Summarise what was found
  const typeCounts = {};
  segments.forEach(s => { typeCounts[s.type] = (typeCounts[s.type] || 0) + 1; });
  const summary = Object.entries(typeCounts).map(([t, c]) => `${c} ${t}${c > 1 ? "s" : ""}`).join(", ");

  if (!mode) {
    return (
      <Modal onClose={onClose}>
          <h2>Where should these bookings go?</h2>
          <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 16 }}>
            Found {segments.length} booking(s){summary ? `: ${summary}` : ""}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="btn btn-primary" style={{ width: "100%", padding: "12px 16px", fontSize: 14 }}
              onClick={() => setMode(trips.length > 0 ? "existing" : "new")}>
              {trips.length > 0 ? "Add to existing trip" : "Create new trip"}
            </button>
            {trips.length > 0 && (
              <button className="btn btn-secondary" style={{ width: "100%", padding: "12px 16px", fontSize: 14 }}
                onClick={() => setMode("new")}>
                Create new trip
              </button>
            )}
            <button className="btn btn-secondary" style={{ width: "100%", padding: "10px 16px", fontSize: 13, color: "var(--text-muted)" }}
              onClick={onClose}>
              Cancel
            </button>
          </div>
      </Modal>
    );
  }

  if (mode === "existing") {
    return (
      <Modal onClose={onClose}>
          <h2>Select a trip</h2>
          <div className="form-group" style={{ marginTop: 8 }}>
            <label>Trip</label>
            <select className="input" value={selectedTripId} onChange={e => setSelectedTripId(e.target.value)}>
              <option value="">-- Choose a trip --</option>
              {trips.map(t => (
                <option key={t.id} value={t.id}>{t.title}{t.start_date ? ` (${t.start_date})` : ""}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" disabled={!selectedTripId}
              onClick={() => onSelectExisting(selectedTripId)}>
              Add to this trip
            </button>
            <button className="btn btn-secondary" onClick={() => setMode(null)}>Back</button>
          </div>
      </Modal>
    );
  }

  if (mode === "new") {
    return (
      <Modal onClose={onClose}>
          <h2>Create new trip</h2>
          <div className="form-group">
            <label>Trip name</label>
            <input className="input" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Trip name" />
          </div>
          <div className="form-group">
            <label>Start date</label>
            <input className="input" type="date" value={newStartDate} onChange={e => setNewStartDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>End date (optional)</label>
            <input className="input" type="date" value={newEndDate} onChange={e => setNewEndDate(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" disabled={!newTitle.trim()}
              onClick={() => onCreateNew({ title: newTitle.trim(), start_date: newStartDate || null, end_date: newEndDate || null })}>
              Create &amp; add bookings
            </button>
            <button className="btn btn-secondary" onClick={() => setMode(null)}>Back</button>
          </div>
      </Modal>
    );
  }

  return null;
}

// ══════════════════════════════════════════════════════════════════════════
// MODALS
// ══════════════════════════════════════════════════════════════════════════
function CreateTripModal({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  return (
    <Modal onClose={onClose}>
        <h2>New Trip</h2>
        <div className="form-group"><label>Trip Name</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Summer in Europe" /></div>
        <div className="form-group"><label>Destination</label><input className="input" value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g. Paris, France" /></div>
        <div className="form-group"><label>Start Date *</label><input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required /></div>
        <div className="form-group"><label>End Date</label><input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>Leave blank — auto-extends from bookings</div></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onCreate({ title, destination: destination || null, start_date: startDate, end_date: endDate || null })} disabled={!title || !startDate}>Create</button>
        </div>
    </Modal>
  );
}

function EditTripModal({ trip, onClose, onSave, authFetch, onRegenerated }) {
  const [title, setTitle] = useState(trip.title || "");
  const [destination, setDestination] = useState(trip.destination || "");
  const [startDate, setStartDate] = useState(trip.start_date || "");
  const [endDate, setEndDate] = useState(trip.end_date || "");
  const [notes, setNotes] = useState(trip.notes || "");
  const [regenerating, setRegenerating] = useState(false);
  const [regenStatus, setRegenStatus] = useState("");

  const regenerateMeta = async () => {
    if (!authFetch) return;
    setRegenerating(true); setRegenStatus("");
    try {
      const r = await authFetch(`/api/trips/${trip.id}/regenerate-meta`, { method: "POST" });
      if (!r.ok) throw new Error("Regen failed");
      setRegenStatus("Regenerated — refresh the page to see the new summary.");
      if (onRegenerated) onRegenerated();
    } catch (e) {
      setRegenStatus("Regen failed: " + e.message);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Modal onClose={onClose}>
        <h2>Edit Trip</h2>
        <div className="form-group"><label>Trip Name</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Summer in Europe" /></div>
        <div className="form-group"><label>Destination</label><input className="input" value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g. Paris, France" /><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>Auto-populated from bookings. Edit to customise.</div></div>
        <div className="form-group"><label>Start Date *</label><input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required /></div>
        <div className="form-group"><label>End Date</label><input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>Leave blank — auto-extends from bookings</div></div>
        <div className="form-group"><label>Notes</label><textarea className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Trip notes..." /></div>
        {authFetch && (
          <div className="form-group">
            <button className="btn btn-secondary btn-sm" onClick={regenerateMeta} disabled={regenerating}>
              {regenerating ? "Regenerating…" : "Regenerate AI summary"}
            </button>
            {regenStatus && <div style={{ marginTop: 6, fontSize: "var(--text-sm)", color: regenStatus.startsWith("Regen failed") ? "var(--red)" : "var(--green)" }}>{regenStatus}</div>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave({ title, destination, start_date: startDate, end_date: endDate, notes })} disabled={!title || !startDate}>Save</button>
        </div>
    </Modal>
  );
}

function AddSegmentModal({ tripId, defaultDate, onClose, authFetch, onDone }) {
  const [type, setType] = useState("flight");
  const [title, setTitle] = useState("");
  const [provider, setProvider] = useState("");
  // Seed start/end with the day the user was looking at in the timeline,
  // at noon. The picker still lets them refine — but the field no longer
  // silently defaults to today when they're scrolled six weeks ahead.
  const initialDt = defaultDate ? `${defaultDate}T12:00` : "";
  const [startDatetime, setStartDatetime] = useState(initialDt);
  const [endDatetime, setEndDatetime] = useState("");
  const [startLocation, setStartLocation] = useState("");
  const [endLocation, setEndLocation] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [roomType, setRoomType] = useState("");
  const [seatNumber, setSeatNumber] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [transportMode, setTransportMode] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [venueTimezone, setVenueTimezone] = useState("");
  // Bike-hire only — kept locally, folded into notes on save so we don't
  // need a DB migration. Bike model lives in vehicle_type (same as car_rental).
  const [riderName, setRiderName] = useState("");
  const [riderHeight, setRiderHeight] = useState("");
  const [bikeExtras, setBikeExtras] = useState("");
  // Ferry only — passengers + add-on detail fold into notes. Vessel name
  // reuses vehicle_type, cabin reuses room_type.
  const [ferryPassengers, setFerryPassengers] = useState("");
  const [ferryAddons, setFerryAddons] = useState("");

  const handleAdd = async () => {
    // car_rental and bike_hire both treat pickup/dropoff as the canonical
    // locations; mirror to start/end_location so legacy code paths (timeline,
    // alerts) work without special-casing.
    const isCarRental = type === "car_rental";
    const isBikeHire = type === "bike_hire";
    const usesPickup = isCarRental || isBikeHire;
    const startLoc = usesPickup ? (pickupLocation || startLocation) : startLocation;
    const endLoc   = usesPickup ? (dropoffLocation || pickupLocation || endLocation) : endLocation;

    // For bike_hire and ferry fold the structured extras into notes so they
    // survive round-trips without a DB schema change.
    let finalNotes = notes;
    if (isBikeHire) {
      const bits = [];
      if (riderName) bits.push(`Rider: ${riderName}`);
      if (riderHeight) bits.push(`Height: ${riderHeight}`);
      if (bikeExtras) bits.push(`Extras: ${bikeExtras}`);
      if (bits.length) finalNotes = [bits.join(" · "), notes].filter(Boolean).join("\n\n");
    }
    if (type === "ferry") {
      const bits = [];
      if (ferryPassengers) bits.push(`Passengers: ${ferryPassengers}`);
      if (ferryAddons) bits.push(`Add-ons: ${ferryAddons}`);
      if (bits.length) finalNotes = [bits.join(" · "), notes].filter(Boolean).join("\n\n");
    }

    const r = await authFetch(`/api/trips/${tripId}/segments`, {
      method: "POST",
      body: JSON.stringify({
        type, title, provider,
        start_datetime: startDatetime, end_datetime: endDatetime,
        start_location: startLoc, end_location: endLoc,
        flight_number: flightNumber, notes: finalNotes,
        confirmation_code: confirmationCode,
        room_type: roomType,
        seat: seatNumber,
        vehicle_type: vehicleType,
        transport_mode: transportMode,
        pickup_location: usesPickup ? pickupLocation : undefined,
        dropoff_location: usesPickup ? (dropoffLocation || pickupLocation) : undefined,
        check_in_time: type === "hotel" ? checkInTime : undefined,
        check_out_time: type === "hotel" ? checkOutTime : undefined,
        venue_timezone: type === "event" ? (venueTimezone || undefined) : undefined,
      }),
    });
    let newSegId = null;
    try { const j = await r.json(); newSegId = j?.id || j?.segment?.id || null; } catch {}
    onDone(newSegId);
  };

  return (
    <Modal onClose={onClose}>
        <h2>Add Segment</h2>
        <div className="form-group">
          <label>Type</label>
          <div className="type-chip-grid" role="radiogroup" aria-label="Segment type">
            {SEGMENT_TYPE_OPTIONS.map(opt => {
              const selected = type === opt.value;
              const color = SEG_COLORS[opt.value] || "var(--text)";
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`type-chip${selected ? " selected" : ""}`}
                  style={selected ? { "--chip-color": color, "--chip-bg": `color-mix(in srgb, ${color} 18%, transparent)` } : undefined}
                  onClick={() => setType(opt.value)}
                >
                  <span className="type-chip-emoji">{SEG_ICONS[opt.value] || "📌"}</span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="form-group"><label>Title</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder={type === "flight" ? "e.g. CX134 Melbourne to Hong Kong" : type === "hotel" ? "e.g. Mandarin Oriental, Hong Kong" : type === "train" ? "e.g. Eurostar 9123 London → Paris" : type === "transport" ? "e.g. Hire car Gatwick to Brighton" : type === "bike_hire" ? "e.g. Bike hire — Eat Sleep Cycle, Girona" : type === "ferry" ? "e.g. Ferry Nuuk → Ilulissat" : type === "excursion" ? "e.g. Victoria Peak tour" : type === "event" ? "e.g. Formula 1 Grand Prix" : "e.g. Segment title"} /></div>
        <div className="form-group"><label>Provider</label><input className="input" value={provider} onChange={e => setProvider(e.target.value)} placeholder={type === "flight" ? "e.g. Cathay Pacific" : type === "hotel" ? "e.g. Mandarin Oriental" : type === "train" ? "e.g. Eurostar" : type === "transport" ? "e.g. Hertz" : type === "bike_hire" ? "e.g. Eat Sleep Cycle" : type === "ferry" ? "e.g. Arctic Umiaq Line" : type === "excursion" ? "e.g. Klook" : type === "event" ? "e.g. Ticketmaster" : "e.g. Provider name"} /></div>
        {type === "flight" && (<>
          <div className="form-group"><label>Flight Number</label><input className="input" value={flightNumber} onChange={e => setFlightNumber(e.target.value)} placeholder="e.g. CX134" /></div>
          <div className="form-group"><label>Seat Number</label><input className="input" value={seatNumber} onChange={e => setSeatNumber(e.target.value)} placeholder="e.g. 14A" /></div>
        </>)}
        {type === "hotel" && (<>
          <div className="form-group"><label>Confirmation Code</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. ABC123" /></div>
          <div className="form-group"><label>Room Type</label><input className="input" value={roomType} onChange={e => setRoomType(e.target.value)} placeholder="e.g. Deluxe Double" /></div>
          <div className="form-group"><label>Check-in Time</label><input className="input" value={checkInTime} onChange={e => setCheckInTime(e.target.value)} placeholder="e.g. 15:00" /></div>
          <div className="form-group"><label>Check-out Time</label><input className="input" value={checkOutTime} onChange={e => setCheckOutTime(e.target.value)} placeholder="e.g. 11:00" /></div>
        </>)}
        {type === "transport" && (<>
          <div className="form-group"><label>Transport Mode</label><select className="input" value={transportMode} onChange={e => setTransportMode(e.target.value)}><option value="">— Select —</option><option value="car">Car</option><option value="taxi">Taxi</option><option value="train">Train</option><option value="bus">Bus</option><option value="tube">Tube</option><option value="ferry">Ferry</option><option value="yacht">Yacht</option><option value="helicopter">Helicopter</option></select></div>
          <div className="form-group"><label>Vehicle Type</label><input className="input" value={vehicleType} onChange={e => setVehicleType(e.target.value)} placeholder="e.g. VW Golf" /></div>
          <div className="form-group"><label>Confirmation Code</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. ABC123" /></div>
        </>)}
        {type === "car_rental" && (<>
          <div className="form-group"><label>Vehicle / Car Group</label><input className="input" value={vehicleType} onChange={e => setVehicleType(e.target.value)} placeholder="e.g. Toyota RAV4 or Group F Mid-size SUV" /></div>
          <div className="form-group"><label>Confirmation Code</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. SIXT-ABC123" /></div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "-4px 0 8px" }}>
            One entry covers the whole rental. The timeline auto-renders pickup on the start day and return on the end day.
          </div>
        </>)}
        {type === "bike_hire" && (<>
          <div className="form-group"><label>Bike Model</label><input className="input" value={vehicleType} onChange={e => setVehicleType(e.target.value)} placeholder="e.g. Hybrid Road E-Bike (Shimano Ultegra di2)" /></div>
          <div className="form-group"><label>Confirmation Code</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. ESC-12345" /></div>
          <div className="form-group"><label>Rider Name</label><input className="input" value={riderName} onChange={e => setRiderName(e.target.value)} placeholder="e.g. Timothy" /></div>
          <div className="form-group"><label>Rider Height / Frame Size</label><input className="input" value={riderHeight} onChange={e => setRiderHeight(e.target.value)} placeholder="e.g. 6 foot or 56cm" /></div>
          <div className="form-group"><label>Extras</label><input className="input" value={bikeExtras} onChange={e => setBikeExtras(e.target.value)} placeholder="e.g. Helmet, Flat Pedals, GPS" /></div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "-4px 0 8px" }}>
            One entry covers the whole hire. Timeline shows pickup on the start day and return on the end day. Leave dropoff blank if same as pickup.
          </div>
        </>)}
        {type === "ferry" && (<>
          <div className="form-group"><label>Vessel Name</label><input className="input" value={vehicleType} onChange={e => setVehicleType(e.target.value)} placeholder="e.g. Sarfaq Ittuk" /></div>
          <div className="form-group"><label>Cabin / Accommodation</label><input className="input" value={roomType} onChange={e => setRoomType(e.target.value)} placeholder="e.g. Junior Suite Twin Bed, deck 4" /></div>
          <div className="form-group"><label>Booking Number</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. 9OEBOB" /></div>
          <div className="form-group"><label>Passengers</label><input className="input" value={ferryPassengers} onChange={e => setFerryPassengers(e.target.value)} placeholder="e.g. Timothy Smith, Diana Smith" /></div>
          <div className="form-group"><label>Add-ons / Meals</label><input className="input" value={ferryAddons} onChange={e => setFerryAddons(e.target.value)} placeholder="e.g. Wi-Fi 12 hrs · 3 meals 18 Jul · 2 meals 19 Jul" /></div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "-4px 0 8px" }}>
            One entry covers the whole crossing. Timeline shows departure on the start day and arrival on the end day for overnight sailings.
          </div>
        </>)}
        {type === "train" && (<>
          <div className="form-group"><label>Service / Train Number</label><input className="input" value={flightNumber} onChange={e => setFlightNumber(e.target.value)} placeholder="e.g. 9123 or TGV 6603" /></div>
          <div className="form-group"><label>Seat / Coach</label><input className="input" value={seatNumber} onChange={e => setSeatNumber(e.target.value)} placeholder="e.g. Coach 12, Seat 47A" /></div>
          <div className="form-group"><label>Class</label><input className="input" value={roomType} onChange={e => setRoomType(e.target.value)} placeholder="Standard / Standard Premier / Business" /></div>
          <div className="form-group"><label>Booking Reference</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder="e.g. PNR-12345" /></div>
        </>)}
        {(type === "excursion" || type === "event") && (
          <div className="form-group"><label>Confirmation Code</label><input className="input" value={confirmationCode} onChange={e => setConfirmationCode(e.target.value)} placeholder={type === "event" ? "e.g. Ticket ref TK-12345" : "e.g. ABC123"} /></div>
        )}
        {type === "event" && (
          <div className="form-group">
            <label>Venue Timezone (IANA)</label>
            <input className="input" value={venueTimezone} onChange={e => setVenueTimezone(e.target.value)} placeholder="e.g. America/Vancouver, Europe/Budapest" list="iana-tz-list" />
            <datalist id="iana-tz-list">
              <option value="America/Vancouver" /><option value="America/Edmonton" /><option value="America/Toronto" /><option value="America/Nuuk" />
              <option value="Europe/London" /><option value="Europe/Budapest" /><option value="Europe/Madrid" /><option value="Europe/Copenhagen" /><option value="Europe/Paris" />
              <option value="Australia/Sydney" /><option value="Australia/Melbourne" /><option value="Australia/Brisbane" /><option value="Asia/Singapore" /><option value="Asia/Hong_Kong" /><option value="Asia/Tokyo" />
            </datalist>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Leave blank to auto-derive from venue location.</div>
          </div>
        )}
        <div className="form-group"><label>{type === "hotel" ? "Check-in Date/Time" : (type === "car_rental" || type === "bike_hire") ? "Pickup Date/Time" : (type === "ferry" || type === "train") ? "Departure Date/Time" : "Start Date/Time"}</label><input className="input" type="datetime-local" value={startDatetime} onChange={e => setStartDatetime(e.target.value)} /></div>
        <div className="form-group"><label>{type === "hotel" ? "Check-out Date/Time" : (type === "car_rental" || type === "bike_hire") ? "Return Date/Time" : (type === "ferry" || type === "train") ? "Arrival Date/Time" : "End Date/Time"}</label><input className="input" type="datetime-local" value={endDatetime} onChange={e => setEndDatetime(e.target.value)} /></div>
        {(type === "car_rental" || type === "bike_hire") ? (
          <>
            <div className="form-group"><label>Pickup Location</label><input className="input" value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} placeholder={type === "bike_hire" ? "e.g. Eat Sleep Cycle, Plaça de Catalunya, 15, Girona" : "e.g. SIXT, 1085 Burrard St, Vancouver"} /></div>
            <div className="form-group"><label>Dropoff Location {type === "bike_hire" && <span style={{ fontSize: 10, color: "var(--text-muted)" }}>(leave blank if same)</span>}</label><input className="input" value={dropoffLocation} onChange={e => setDropoffLocation(e.target.value)} placeholder={type === "bike_hire" ? "e.g. same hub" : "e.g. SIXT, Calgary Int'l Airport"} /></div>
          </>
        ) : (
          <>
            <div className="form-group"><label>{type === "flight" ? "Departure Airport" : type === "hotel" ? "Address" : type === "transport" ? "Pickup Location" : type === "event" ? "Venue / Location" : type === "ferry" ? "Departure Port" : type === "train" ? "Departure Station" : "Start Location"}</label><input className="input" value={startLocation} onChange={e => setStartLocation(e.target.value)} placeholder={type === "hotel" ? "e.g. 123 High Street, London" : type === "flight" ? "e.g. MEL" : type === "transport" ? "e.g. Gatwick Airport" : type === "event" ? "e.g. Albert Hall, London" : type === "ferry" ? "e.g. Nuuk" : type === "train" ? "e.g. London St Pancras" : ""} /></div>
            {type !== "hotel" && (
              <div className="form-group"><label>{type === "flight" ? "Arrival Airport" : type === "transport" ? "Dropoff Location" : type === "ferry" ? "Arrival Port" : type === "train" ? "Arrival Station" : "End Location"}</label><input className="input" value={endLocation} onChange={e => setEndLocation(e.target.value)} placeholder={type === "flight" ? "e.g. HKG" : type === "transport" ? "e.g. Brighton" : type === "ferry" ? "e.g. Ilulissat" : type === "train" ? "e.g. Paris Gare du Nord" : ""} /></div>
            )}
          </>
        )}
        <div className="form-group"><label>Notes</label><textarea className="input" value={notes} onChange={e => setNotes(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={!title}>Add</button>
        </div>
    </Modal>
  );
}

function InviteModal({ trip, onClose, authFetch }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [result, setResult] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [owner, setOwner] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [mRes, iRes] = await Promise.all([
        authFetch(`/api/trips/${trip.id}/members`),
        authFetch(`/api/trips/${trip.id}/invites`),
      ]);
      const mData = await mRes.json();
      const iData = await iRes.json();
      setOwner(mData.owner || null);
      setMembers(mData.members || []);
      setInvites(iData.invites || []);
    } catch {}
  }, [authFetch, trip.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleInvite = async () => {
    try {
      const r = await authFetch(`/api/trips/${trip.id}/invite`, {
        method: "POST", body: JSON.stringify({ email, role }),
      });
      const data = await r.json();
      if (data.ok) { setResult(`Invited ${email}`); setEmail(""); loadAll(); }
      else setResult(data.error);
    } catch (e) { setResult(e.message); }
  };

  const generateLink = async () => {
    try {
      const r = await authFetch(`/api/trips/${trip.id}/invite`, {
        method: "POST", body: JSON.stringify({ role }),
      });
      const data = await r.json();
      setInviteLink(window.location.origin + data.url);
      loadAll();
    } catch {}
  };

  const changeRole = async (uid, newRole) => {
    await authFetch(`/api/trips/${trip.id}/members/${uid}`, {
      method: "PUT", body: JSON.stringify({ role: newRole }),
    });
    loadAll();
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name || "this member"} from the trip? They'll no longer see it in their account.`)) return;
    await authFetch(`/api/trips/${trip.id}/members/${uid}`, { method: "DELETE" });
    loadAll();
  };

  const revokeInvite = async (code) => {
    if (!window.confirm("Revoke this invite link? Anyone holding it will no longer be able to use it.")) return;
    await authFetch(`/api/trips/${trip.id}/invites/${code}`, { method: "DELETE" });
    if (inviteLink.endsWith(`/invite/${code}`)) setInviteLink("");
    loadAll();
  };

  return (
    <Modal onClose={onClose}>
        <h2>Share Trip</h2>
        <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>{trip.title}</p>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: 0.5 }}>Members</label>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {owner && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--sidebar)", padding: "8px 10px", borderRadius: 8 }}>
                <div style={{ fontSize: 13, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{owner.name || owner.email}</div>
                  <div style={{ color: "var(--text-dim)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{owner.email}</div>
                </div>
                <span style={{ fontSize: 11, color: "var(--purple)", textTransform: "uppercase", letterSpacing: 0.5 }}>Owner</span>
              </div>
            )}
            {members.map(m => (
              <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--sidebar)", padding: "8px 10px", borderRadius: 8 }}>
                <div style={{ flex: 1, fontSize: 13, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.name || m.email}
                    {!m.accepted && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--amber)", textTransform: "uppercase" }}>pending</span>}
                  </div>
                  <div style={{ color: "var(--text-dim)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</div>
                </div>
                <select className="input" style={{ width: "auto", padding: "4px 8px", fontSize: 12 }} value={m.role} onChange={e => changeRole(m.user_id, e.target.value)}>
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                </select>
                <button className="btn btn-danger btn-sm" onClick={() => removeMember(m.user_id, m.name || m.email)}>Remove</button>
              </div>
            ))}
            {members.length === 0 && <div style={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic" }}>No one else has access yet.</div>}
          </div>
        </div>

        {invites.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: 0.5 }}>Outstanding Invite Links</label>
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {invites.map(inv => (
                <div key={inv.invite_code} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--sidebar)", padding: "8px 10px", borderRadius: 8 }}>
                  <div style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
                    <div className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>/invite/{inv.invite_code}</div>
                    <div style={{ color: "var(--text-dim)", fontSize: 11 }}>{inv.role} · used {inv.use_count}{inv.max_uses ? `/${inv.max_uses}` : ""}</div>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => navigator.clipboard.writeText(window.location.origin + "/invite/" + inv.invite_code)}>Copy</button>
                  <button className="btn btn-danger btn-sm" onClick={() => revokeInvite(inv.invite_code)}>Revoke</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Role for new invites</label>
          <select className="input" value={role} onChange={e => setRole(e.target.value)}>
            <option value="viewer">Viewer (read-only)</option>
            <option value="editor">Editor (can add/edit)</option>
          </select>
        </div>
        <div className="form-group"><label>Invite by Email</label><input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="friend@example.com" /></div>
        <button className="btn btn-primary" onClick={handleInvite} disabled={!email} style={{ marginBottom: 12 }}>Send Invite</button>
        {result && <div style={{ fontSize: 13, color: "var(--green)", marginBottom: 12 }}>{result}</div>}
        <div className="login-divider">or</div>
        <button className="btn btn-secondary" onClick={generateLink} style={{ width: "100%", marginBottom: 8 }}>Generate Invite Link</button>
        {inviteLink && (
          <div style={{ fontSize: 12, wordBreak: "break-all", background: "var(--sidebar)", padding: 10, borderRadius: 8 }}>
            <span className="mono">{inviteLink}</span>
            <button className="btn btn-sm btn-secondary" style={{ marginTop: 6 }} onClick={() => navigator.clipboard.writeText(inviteLink)}>Copy</button>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
    </Modal>
  );
}
